import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Upload, X, FileText, File, FileSpreadsheet, FileType, Download, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';
import ImageUpload from './ImageUpload';
import SubestadosHierarchyEditor from './SubestadosHierarchyEditor';
import '../styles/admin-console.css';
import '../styles/image-upload.css';

// Iconos SVG profesionales
const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const CrownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11.562 3.266a1 1 0 0 1 1.876 0L15.39 7.87a1 1 0 0 0 1.7.117L17.7 6.3a1 1 0 0 1 1.4 0L21 8.6a1 1 0 0 1 0 1.4L19.987 10.9a1 1 0 0 0-.117 1.7L21.734 13.438a1 1 0 0 1 0 1.876L19.13 15.39a1 1 0 0 0-.117 1.7L20.7 18.3a1 1 0 0 1 0 1.4L18.4 21a1 1 0 0 1-1.4 0L15.1 19.987a1 1 0 0 0-1.7-.117L12.562 21.734a1 1 0 0 1-1.876 0L8.87 19.13a1 1 0 0 0-1.7.117L6.3 20.7a1 1 0 0 1-1.4 0L3 18.4a1 1 0 0 1 0-1.4L4.013 15.1a1 1 0 0 0 .117-1.7L2.266 12.562a1 1 0 0 1 0-1.876L4.87 8.87a1 1 0 0 0 .117-1.7L3.3 6.3a1 1 0 0 1 0-1.4L5.6 3a1 1 0 0 1 1.4 0L8.9 4.013a1 1 0 0 0 1.7.117L11.562 2.266Z" />
    <path d="M12 8v8" />
    <path d="M8 12h8" />
  </svg>
);

const BuildingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
    <path d="M6 12H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
    <path d="M18 9h2a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-2" />
    <path d="M10 6h4" />
    <path d="M10 10h4" />
    <path d="M10 14h4" />
    <path d="M10 18h4" />
  </svg>
);

const ZapIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

const LogOutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16,17 21,12 16,7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const BarChartIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="20" x2="12" y2="10" />
    <line x1="18" y1="20" x2="18" y2="4" />
    <line x1="6" y1="20" x2="6" y2="16" />
  </svg>
);

// Catálogo/config por defecto para el panel de vistas (fallback en frontend)
const DASHBOARD_CATALOG_FALLBACK = {
  analytics_ai: {
    fields: [
      'total_calls',
      'total_calls_completed',
      'total_calls_failed',
      'avg_duration',
      'chart_by_hour',
      'response_rate',
      'calls_by_hours_completed',
      'agent_id',
      'agent_name',
      'start_time',
      'status',
      'call_duration_secs',
      // Campos para desglose de subestados jerárquicos
      'titular',
      'subestado',
      'estado_llamada',
      'resultado',
      'tipo_llamada',
      'categoria',
      // Campos opcionales para segunda fila de tarjetas KPI principales
      'custom_kpi_1',
      'custom_kpi_2',
      'custom_kpi_3',
      'custom_kpi_4',
      'custom_kpi_5',
      // Campos específicos para la tabla "Resultados de Agentes"
      'agent_table_total_calls',
      'agent_table_completed',
      'agent_table_failed',
      'agent_table_avg_duration',
      'agent_table_response_rate',
      // Campos opcionales adicionales para tabla de agentes
      'agent_table_custom_1',
      'agent_table_custom_2',
      'agent_table_custom_3',
      'agent_table_custom_4',
      'agent_table_custom_5',
    ]
  },
  analytics: {
    fields: [
      // Visibles (SaaS)
      'total_calls',
      'attended_calls',
      'abandoned_calls',
      'avg_duration',
      'call_distribution',
      'contrataciones',
      'occupancy',
      'tma',
      'tme',
      'evolution_chart',
      'campaign_results',
      'looker_tabs',
      // Datos fuente (mapeo)
      'call_start',
      'call_type',
      'abandonadas',
      'talk_time',
      'hold_time',
      'wrap_up_time',
      'contact_id',
      'campaign',
      'hiring',
    ],
  },
  conversations: {
    fields: [
      'transcript',
      'sentiment',
      'duration',
      'created_at',
      'agent_name',
    ],
  },
  campaigns: { fields: [] },
  reports: { fields: [] },
  osint: { fields: [] },
  quality_plan: { fields: [] },
};

const DASHBOARD_DEFAULT_CONFIG = {
  sections: {
    analytics_ai: {
      enabled: true,
      dataSource: 'main',
      fields: {
        total_calls: true,
        total_calls_completed: true,
        total_calls_failed: true,
        avg_duration: true,
        chart_by_hour: true,
        response_rate: true,
        calls_by_hours_completed: true,
        agent_id: true,
        agent_name: true,
        start_time: true,
        status: true,
        call_duration_secs: true,
        // Campos para desglose de subestados jerárquicos
        titular: true,
        subestado: true,
        estado_llamada: true,
        resultado: true,
        tipo_llamada: true,
        categoria: true,
        // Campos opcionales para segunda fila de tarjetas KPI principales
        custom_kpi_1: false,
        custom_kpi_2: false,
        custom_kpi_3: false,
        custom_kpi_4: false,
        custom_kpi_5: false,
        // Campos específicos para la tabla "Resultados de Agentes"
        agent_table_total_calls: true,
        agent_table_completed: true,
        agent_table_failed: true,
        agent_table_avg_duration: true,
        agent_table_response_rate: true,
        // Campos opcionales adicionales para tabla de agentes
        agent_table_custom_1: false,
        agent_table_custom_2: false,
        agent_table_custom_3: false,
        agent_table_custom_4: false,
        agent_table_custom_5: false,
      }
      ,
      fieldMappings: {
        total_calls: null,
        total_calls_completed: null,
        total_calls_failed: null,
        avg_duration: null,
        chart_by_hour: null,
        response_rate: null,
        calls_by_hours_completed: null,
        agent_id: { table: 'datos_agente_ia', column: 'agent_id' },
        agent_name: { table: 'datos_agente_ia', column: 'agent_name' },
        start_time: { table: 'datos_agente_ia', column: 'start_time' },
        status: { table: 'datos_agente_ia', column: 'status' },
        call_duration_secs: { table: 'datos_agente_ia', column: 'call_duration_secs' },
        // Campos para desglose de subestados jerárquicos (mapeo inicial, puede cambiarse)
        titular: { table: 'datos_agente_ia', column: 'titular' },
        subestado: { table: 'datos_agente_ia', column: 'subestado' },
        estado_llamada: { table: 'datos_agente_ia', column: 'estado_llamada' },
        resultado: { table: 'datos_agente_ia', column: 'resultado' },
        tipo_llamada: { table: 'datos_agente_ia', column: 'tipo_llamada' },
        categoria: { table: 'datos_agente_ia', column: 'categoria' },
        // Campos opcionales para segunda fila de tarjetas KPI principales
        custom_kpi_1: null,
        custom_kpi_2: null,
        custom_kpi_3: null,
        custom_kpi_4: null,
        custom_kpi_5: null,
        // Campos específicos para la tabla "Resultados de Agentes"
        agent_table_total_calls: null,
        agent_table_completed: null,
        agent_table_failed: null,
        agent_table_avg_duration: null,
        agent_table_response_rate: null,
        // Campos opcionales adicionales para tabla de agentes
        agent_table_custom_1: null,
        agent_table_custom_2: null,
        agent_table_custom_3: null,
        agent_table_custom_4: null,
        agent_table_custom_5: null,
      }
      ,
      fieldCalculations: {
        total_calls: null,
        total_calls_completed: null,
        total_calls_failed: null,
        avg_duration: null,
        chart_by_hour: null,
        response_rate: null,
        calls_by_hours_completed: null,
        agent_id: null,
        agent_name: null,
        start_time: null,
        status: null,
        call_duration_secs: null,
        // Campos para desglose de subestados jerárquicos
        titular: null,
        subestado: null,
        estado_llamada: null,
        resultado: null,
        tipo_llamada: null,
        categoria: null,
        // Campos opcionales para segunda fila de tarjetas KPI principales
        custom_kpi_1: null,
        custom_kpi_2: null,
        custom_kpi_3: null,
        custom_kpi_4: null,
        custom_kpi_5: null,
        // Campos específicos para la tabla "Resultados de Agentes"
        agent_table_total_calls: null,
        agent_table_completed: null,
        agent_table_failed: null,
        agent_table_avg_duration: null,
        agent_table_response_rate: null,
        // Campos opcionales adicionales para tabla de agentes
        agent_table_custom_1: null,
        agent_table_custom_2: null,
        agent_table_custom_3: null,
        agent_table_custom_4: null,
        agent_table_custom_5: null,
      }
      ,
      fieldCalculationSpecs: {},
      fieldLabels: {},
      fieldFormats: {},
      fieldAllowAudio: {}, // Campos que permiten escuchar/descargar audio y ver transcripciones
    },
    analytics: {
      enabled: true,
      dataSource: 'main',
      fields: {
        // Visibles
        total_calls: true,
        attended_calls: true,
        abandoned_calls: true,
        avg_duration: true,
        call_distribution: true,
        contrataciones: true,
        occupancy: true,
        tma: true,
        tme: true,
        evolution_chart: true,
        campaign_results: true,
        looker_tabs: true,
        // Datos fuente
        call_start: true,
        call_type: true,
        abandonadas: true,
        talk_time: true,
        hold_time: true,
        wrap_up_time: true,
        contact_id: true,
        campaign: true,
        hiring: true,
      },
      fieldMappings: {
        // Visibles (sin mapeo)
        total_calls: null,
        attended_calls: null,
        abandoned_calls: null,
        avg_duration: null,
        call_distribution: null,
        contrataciones: null,
        occupancy: null,
        tma: null,
        tme: null,
        evolution_chart: null,
        campaign_results: null,
        looker_tabs: null,
        // Datos fuente (GoContact sugerido)
        call_start: { table: 'datos_go_contact', column: 'call_start' },
        call_type: { table: 'datos_go_contact', column: 'call_type' },
        abandonadas: { table: 'datos_go_contact', column: 'abandonadas' },
        talk_time: { table: 'datos_go_contact', column: 'talk_time' },
        hold_time: { table: 'datos_go_contact', column: 'hold_time' },
        wrap_up_time: { table: 'datos_go_contact', column: 'wrap_up_time' },
        contact_id: { table: 'datos_go_contact', column: 'contact_id' },
        campaign: { table: 'datos_go_contact', column: 'campana' },
        hiring: { table: 'datos_go_contact', column: 'segmento' },
      },
      fieldCalculations: {
        // Visibles
        total_calls: null,
        attended_calls: null,
        abandoned_calls: null,
        avg_duration: null,
        call_distribution: null,
        contrataciones: null,
        occupancy: null,
        tma: null,
        tme: null,
        evolution_chart: null,
        campaign_results: null,
        looker_tabs: null,
        // Datos fuente
        call_start: null,
        call_type: null,
        abandonadas: null,
        talk_time: null,
        hold_time: null,
        wrap_up_time: null,
        contact_id: null,
        campaign: null,
        hiring: null,
      },
      fieldCalculationSpecs: {},
      fieldLabels: {},
      fieldFormats: {},
    },
    informes: {
      enabled: true,
      dataSource: 'main',
      fields: {},
      fieldMappings: {},
      fieldCalculations: {},
      fieldCalculationSpecs: {},
      fieldLabels: {},
      fieldFormats: {},
    },
    conversations: {
      enabled: true,
      dataSource: 'main',
      fields: {
        transcript: true,
        sentiment: true,
        duration: true,
        created_at: true,
        agent_name: true,
      },
      fieldMappings: {
        transcript: null,
        sentiment: null,
        duration: null,
        created_at: null,
        agent_name: null,
      },
      fieldCalculations: {
        transcript: null,
        sentiment: null,
        duration: null,
        created_at: null,
        agent_name: null,
      },
      fieldCalculationSpecs: {},
    },
    campaigns: {
      enabled: true,
      dataSource: 'main',
      fields: {},
      fieldMappings: {},
      fieldCalculations: {},
      fieldCalculationSpecs: {},
    },
    reports: {
      enabled: true,
      dataSource: 'main',
      fields: {},
      fieldMappings: {},
      fieldCalculations: {},
      fieldCalculationSpecs: {},
    },
    osint: {
      enabled: true,
      dataSource: 'main',
      fields: {},
      fieldMappings: {},
      fieldCalculations: {},
      fieldCalculationSpecs: {},
      fieldLabels: {},
      fieldFormats: {},
    },
    quality_plan: {
      enabled: true,
      dataSource: 'main',
      fields: {},
      fieldMappings: {},
      fieldCalculations: {},
      fieldCalculationSpecs: {},
      fieldLabels: {},
      fieldFormats: {},
    },
  }
};

// Metadatos UX para hacer el editor entendible (SaaS)
const DASHCFG_SECTION_META = {
  analytics_ai: {
    description: 'Métricas y rendimiento del Agente IA (se calcula desde una tabla/dataset).',
    // Campos que se muestran al cliente (métricas calculadas) - PRINCIPALES
    clientFields: [
      'total_calls',
      'total_calls_completed',
      'total_calls_failed',
      'avg_duration',
      'chart_by_hour',
      'response_rate',
      'calls_by_hours_completed',
      // Campos opcionales para segunda fila de tarjetas KPI principales
      'custom_kpi_1',
      'custom_kpi_2',
      'custom_kpi_3',
      'custom_kpi_4',
      'custom_kpi_5',
      // Campos específicos para la tabla "Resultados de Agentes" (independientes)
      'agent_table_total_calls',
      'agent_table_completed',
      'agent_table_failed',
      'agent_table_avg_duration',
      'agent_table_response_rate',
      // Campos opcionales adicionales para tabla de agentes
      'agent_table_custom_1',
      'agent_table_custom_2',
      'agent_table_custom_3',
      'agent_table_custom_4',
      'agent_table_custom_5',
    ],
    // Campos específicos para la tabla "Resultados de Agentes" (independientes)
    agentTableFields: [
      'agent_table_total_calls',
      'agent_table_completed',
      'agent_table_failed',
      'agent_table_avg_duration',
      'agent_table_response_rate',
      // Campos opcionales adicionales para tabla de agentes
      'agent_table_custom_1',
      'agent_table_custom_2',
      'agent_table_custom_3',
      'agent_table_custom_4',
      'agent_table_custom_5',
    ],
    // Campos fuente (no se muestran al cliente; se usan para cálculos/tabla)
    // start_time es CRÍTICO para filtros de fecha - debe estar primero en la lista
    dataFields: [
      'start_time',
      'agent_id',
      'agent_name',
      'status',
      'call_duration_secs',
      // Campos para desglose de subestados jerárquicos
      'titular',
      'subestado',
      'estado_llamada',
      'resultado',
      'tipo_llamada',
      'categoria',
    ],
    requiredMappings: ['start_time', 'agent_id', 'agent_name', 'status', 'call_duration_secs'],
    preferredTableField: 'agent_id',
  },
  analytics: {
    description: 'Analíticas generales (llamadas, distribución, evolución y campañas).',
    // Lo que se muestra al cliente (métricas/bloques)
    clientFields: [
      'total_calls',
      'attended_calls',
      'abandoned_calls',
      'avg_duration',
      'call_distribution',
      'contrataciones',
      'occupancy',
      'tma',
      'tme',
      'evolution_chart',
      'campaign_results',
      'looker_tabs',
    ],
    // Datos base para cálculos (mapeo a tabla/columnas)
    dataFields: [
      'call_start',
      'call_type',
      'abandonadas',
      'talk_time',
      'hold_time',
      'wrap_up_time',
      'contact_id',
      'campaign',
      'hiring',
    ],
    requiredMappings: [
      'call_start',
      'call_type',
      'abandonadas',
      'talk_time',
      'hold_time',
      'wrap_up_time',
      'contact_id',
      'campaign',
      'hiring',
    ],
    preferredTableField: 'call_start',
  },
  conversations: {
    description: 'Conversaciones (transcripciones, sentimiento y metadatos).',
    clientFields: ['transcript', 'sentiment', 'duration', 'created_at', 'agent_name'],
    dataFields: ['transcript', 'sentiment', 'duration', 'created_at', 'agent_name'],
    requiredMappings: [],
    preferredTableField: 'created_at',
  },
  campaigns: {
    description: 'Campañas (habilitar/deshabilitar el módulo).',
    clientFields: [],
    dataFields: [],
    requiredMappings: [],
  },
  reports: {
    description: 'Reportes (habilitar/deshabilitar el módulo).',
    clientFields: [],
    dataFields: [],
    requiredMappings: [],
  },
  osint: {
    description: 'OSINT (habilitar/deshabilitar el módulo).',
    clientFields: [],
    dataFields: [],
    requiredMappings: [],
  },
  quality_plan: {
    description: 'Plan de Calidad (Kanban y Timeline).',
    clientFields: [],
    dataFields: [],
    requiredMappings: [],
  },
};

const DASHCFG_FIELD_LABELS = {
  analytics_ai: {
    total_calls: 'Total de llamadas',
    total_calls_completed: 'Completadas',
    total_calls_failed: 'Fallidas',
    avg_duration: 'Duración media',
    chart_by_hour: 'Gráfica por hora',
    response_rate: 'Tasa de respuesta',
    calls_by_hours_completed: 'Completadas por hora',
    agent_id: 'ID del agente (dato fuente)',
    agent_name: 'Nombre del agente (dato fuente)',
    start_time: 'Inicio (fecha/hora) (dato fuente)',
    status: 'Estado (dato fuente)',
    call_duration_secs: 'Duración en segundos (dato fuente)',
    // Campos para desglose de subestados
    titular: 'Titular (dato fuente para subestados)',
    subestado: 'Subestado (dato fuente para subestados)',
    estado_llamada: 'Estado de llamada (dato fuente para subestados)',
    resultado: 'Resultado (dato fuente para subestados)',
    tipo_llamada: 'Tipo de llamada (dato fuente para subestados)',
    categoria: 'Categoría (dato fuente para subestados)',
    // Campos opcionales para segunda fila de tarjetas KPI principales
    custom_kpi_1: 'Campo Personalizado 1 (Tarjetas Principales)',
    custom_kpi_2: 'Campo Personalizado 2 (Tarjetas Principales)',
    custom_kpi_3: 'Campo Personalizado 3 (Tarjetas Principales)',
    custom_kpi_4: 'Campo Personalizado 4 (Tarjetas Principales)',
    custom_kpi_5: 'Campo Personalizado 5 (Tarjetas Principales)',
    // Campos específicos para la tabla "Resultados de Agentes"
    agent_table_total_calls: 'Total Llamadas (Tabla Agentes)',
    agent_table_completed: 'Completadas (Tabla Agentes)',
    agent_table_failed: 'Fallidas (Tabla Agentes)',
    agent_table_avg_duration: 'Duración Media (Tabla Agentes)',
    agent_table_response_rate: 'Tasa de Respuesta (Tabla Agentes)',
    // Campos opcionales adicionales para tabla de agentes
    agent_table_custom_1: 'Campo Personalizado 1 (Tabla Agentes)',
    agent_table_custom_2: 'Campo Personalizado 2 (Tabla Agentes)',
    agent_table_custom_3: 'Campo Personalizado 3 (Tabla Agentes)',
    agent_table_custom_4: 'Campo Personalizado 4 (Tabla Agentes)',
    agent_table_custom_5: 'Campo Personalizado 5 (Tabla Agentes)',
  },
  analytics: {
    total_calls: 'Total de llamadas',
    attended_calls: 'Atendidas',
    abandoned_calls: 'Abandonadas',
    avg_duration: 'Duración media',
    call_distribution: 'Distribución de resultados',
    contrataciones: 'Contrataciones',
    occupancy: 'Ocupación',
    tma: 'TMA',
    tme: 'TME',
    evolution_chart: 'Evolución de llamadas',
    campaign_results: 'Resultados por campaña',
    looker_tabs: 'Dashboards (Looker)',
    call_start: 'Fecha/hora inicio (dato fuente)',
    call_type: 'Tipo de llamada (dato fuente)',
    abandonadas: 'Indicador abandonada (dato fuente)',
    talk_time: 'Tiempo de conversación (dato fuente)',
    hold_time: 'Tiempo en espera (dato fuente)',
    wrap_up_time: 'Tiempo post-llamada (dato fuente)',
    contact_id: 'ID contacto (dato fuente)',
    campaign: 'Campaña (dato fuente)',
    hiring: 'Segmento/contratación (dato fuente)',
  },
  conversations: {
    transcript: 'Transcripción',
    sentiment: 'Sentimiento',
    duration: 'Duración',
    created_at: 'Creado el',
    agent_name: 'Agente',
  },
};

const DASHCFG_FIELD_HELP = {
  analytics_ai: {
    total_calls: 'Conteo total en el periodo.',
    total_calls_completed: 'Llamadas con estado “completada”.',
    total_calls_failed: 'Llamadas con estado “fallida”.',
    avg_duration: 'Promedio de duración de llamadas.',
    chart_by_hour: 'Gráfica de distribución por hora.',
    response_rate: 'Porcentaje de respuesta (según lógica actual).',
    calls_by_hours_completed: 'Completadas agrupadas por hora.',
    agent_id: 'Columna que identifica al agente.',
    agent_name: 'Columna con el nombre del agente (para la tabla).',
    start_time: '⚠️ CRÍTICO: Columna con fecha/hora de inicio. Este campo es necesario para que funcionen los filtros de fecha en el panel de analytics. Debe ser de tipo DATETIME o TIMESTAMP.',
    status: 'Columna con el estado de la llamada.',
    call_duration_secs: 'Columna con duración en segundos.',
    // Ayuda para campos de subestados
    titular: 'Columna que indica si la llamada es con titular (ej: "SI", "NO"). Usado en el desglose jerárquico de subestados.',
    subestado: 'Columna con el subestado específico de la llamada (ej: "Titular cuelga llamada", "Titular molesto"). Usado en el desglose jerárquico de subestados.',
    estado_llamada: 'Columna alternativa para el estado de la llamada. Usado en el desglose jerárquico de subestados.',
    resultado: 'Columna con el resultado de la llamada. Usado en el desglose jerárquico de subestados.',
    tipo_llamada: 'Columna con el tipo de llamada. Usado en el desglose jerárquico de subestados.',
    categoria: 'Columna con la categoría de la llamada. Usado en el desglose jerárquico de subestados.',
    // Campos opcionales para segunda fila de tarjetas KPI principales
    custom_kpi_1: 'Campo personalizado opcional que aparece en una segunda fila de tarjetas KPI principales. Se muestra solo si está habilitado y tiene una fórmula configurada.',
    custom_kpi_2: 'Campo personalizado opcional que aparece en una segunda fila de tarjetas KPI principales. Se muestra solo si está habilitado y tiene una fórmula configurada.',
    custom_kpi_3: 'Campo personalizado opcional que aparece en una segunda fila de tarjetas KPI principales. Se muestra solo si está habilitado y tiene una fórmula configurada.',
    custom_kpi_4: 'Campo personalizado opcional que aparece en una segunda fila de tarjetas KPI principales. Se muestra solo si está habilitado y tiene una fórmula configurada.',
    custom_kpi_5: 'Campo personalizado opcional que aparece en una segunda fila de tarjetas KPI principales. Se muestra solo si está habilitado y tiene una fórmula configurada.',
    // Campos específicos para la tabla "Resultados de Agentes"
    agent_table_total_calls: 'Total de llamadas mostrado en la tabla "Resultados de Agentes". Configuración independiente de los campos principales.',
    agent_table_completed: 'Llamadas completadas mostradas en la tabla "Resultados de Agentes". Configuración independiente de los campos principales.',
    agent_table_failed: 'Llamadas fallidas mostradas en la tabla "Resultados de Agentes". Configuración independiente de los campos principales.',
    agent_table_avg_duration: 'Duración media mostrada en la tabla "Resultados de Agentes". Configuración independiente de los campos principales.',
    agent_table_response_rate: 'Tasa de respuesta mostrada en la tabla "Resultados de Agentes". Configuración independiente de los campos principales.',
    // Campos opcionales adicionales para tabla de agentes
    agent_table_custom_1: 'Campo personalizado opcional que aparece como columna adicional en la tabla "Resultados de Agentes". Se muestra solo si está habilitado y tiene una fórmula configurada.',
    agent_table_custom_2: 'Campo personalizado opcional que aparece como columna adicional en la tabla "Resultados de Agentes". Se muestra solo si está habilitado y tiene una fórmula configurada.',
    agent_table_custom_3: 'Campo personalizado opcional que aparece como columna adicional en la tabla "Resultados de Agentes". Se muestra solo si está habilitado y tiene una fórmula configurada.',
    agent_table_custom_4: 'Campo personalizado opcional que aparece como columna adicional en la tabla "Resultados de Agentes". Se muestra solo si está habilitado y tiene una fórmula configurada.',
    agent_table_custom_5: 'Campo personalizado opcional que aparece como columna adicional en la tabla "Resultados de Agentes". Se muestra solo si está habilitado y tiene una fórmula configurada.',
  },
  analytics: {
    total_calls: 'Conteo total en el periodo (según filtro inbound/outbound).',
    attended_calls: 'Llamadas atendidas (según lógica de abandonadas + talk_time).',
    abandoned_calls: 'Llamadas abandonadas (según lógica del dataset).',
    avg_duration: 'Promedio de duración (talk + hold + wrap_up).',
    call_distribution: 'Bloque con KPIs comerciales (ocupación, TMA, TME, etc.).',
    contrataciones: 'Conteo de contrataciones (según columna/valores configurados).',
    occupancy: 'Ocupación (desde estadísticas GoContact/tabla).',
    tma: 'Tiempo Medio de Atención.',
    tme: 'Tiempo Medio de Espera.',
    evolution_chart: 'Gráfica por hora (calls_by_hours).',
    campaign_results: 'Tabla resumen por campaña.',
    looker_tabs: 'Tabs de dashboards embebidos (si están configurados).',
    call_start: 'Columna fecha/hora de inicio (ej: call_start).',
    call_type: 'Columna con INBOUND/OUTBOUND (ej: call_type).',
    abandonadas: 'Columna con “si/no” o equivalente (ej: abandonadas).',
    talk_time: 'Segundos de conversación (ej: talk_time).',
    hold_time: 'Segundos en espera (ej: hold_time).',
    wrap_up_time: 'Segundos wrap-up (ej: wrap_up_time).',
    contact_id: 'Identificador único de contacto (para DISTINCT).',
    campaign: 'Nombre/ID de campaña (ej: campana).',
    hiring: 'Segmento/resultado para “contrataciones” (ej: segmento).',
  },
  conversations: {
    transcript: 'Texto completo de la conversación.',
    sentiment: 'Resultado de sentimiento.',
    duration: 'Duración total.',
    created_at: 'Fecha/hora de creación.',
    agent_name: 'Agente asociado.',
  },
};

const dashcfgHumanizeKey = (key) => String(key || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (m) => m.toUpperCase());

// Componente para gestión de archivos de informes en Admin Console
const InformesFileManager = ({ selectedClientId }) => {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');

  useEffect(() => {
    if (selectedClientId) {
      fetchFiles();
    }
  }, [selectedClientId]);

  const fetchFiles = async () => {
    if (!selectedClientId) return;
    setLoading(true);
    try {
      let url = `${API_URL}/api/informes/list`;
      if (user?.role === 'admin' && selectedClientId) {
        url += `?client_id=${encodeURIComponent(selectedClientId)}`;
      }
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Archivos recibidos para cliente', selectedClientId, ':', data.files); // Debug
        setFiles(data.files || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error al obtener archivos:', response.status, errorData); // Debug
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      alert('Por favor selecciona un archivo');
      return;
    }
    if (!selectedClientId) {
      alert('Por favor selecciona un cliente primero');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (uploadDescription) {
        formData.append('description', uploadDescription);
      }
      if (user?.role === 'admin' && selectedClientId) {
        formData.append('client_id', selectedClientId);
      }

      const response = await fetch(`${API_URL}/api/informes/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Archivo subido exitosamente:', result); // Debug
        // Esperar un momento para asegurar que el archivo se guardó en BD
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchFiles();
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadDescription('');
        alert('Archivo subido correctamente');
      } else {
        const error = await response.json();
        console.error('Error al subir archivo:', error); // Debug
        alert(error.detail || 'Error al subir el archivo');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este archivo?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/informes/${fileId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchFiles();
      } else {
        alert('Error al eliminar el archivo');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error al eliminar el archivo');
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'docx':
      case 'doc':
        return <File className="w-5 h-5 text-blue-500" />;
      case 'xlsx':
      case 'xls':
        return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
      case 'txt':
        return <FileType className="w-5 h-5 text-gray-500" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  if (!selectedClientId) {
    return (
      <div style={{
        padding: '24px',
        background: 'rgba(245, 158, 11, 0.1)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius: '12px',
        textAlign: 'center',
        color: 'var(--text)'
      }}>
        <p style={{ margin: 0, fontSize: '14px' }}>
          ⚠️ Por favor, selecciona un cliente en el paso 1 para gestionar sus archivos de informes.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      padding: '16px',
      background: 'rgba(13, 20, 40, 0.35)',
      border: '1px solid rgba(96, 120, 170, 0.28)',
      borderRadius: '14px'
    }}>
      {/* Botón de subir */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => setShowUploadModal(true)}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #1356f1 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 12px rgba(19, 86, 241, 0.3)'
          }}
        >
          <Upload className="w-5 h-5" />
          Subir Archivo
        </button>
      </div>

      {/* Lista de archivos */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
          Cargando archivos...
        </div>
      ) : files.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'var(--muted)',
          background: 'rgba(245, 158, 11, 0.1)',
          borderRadius: '10px',
          border: '1px dashed rgba(245, 158, 11, 0.3)'
        }}>
          <FileText style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.5 }} />
          <p style={{ margin: 0, fontWeight: 600 }}>No hay archivos subidos</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '13px' }}>Sube tu primer archivo usando el botón de arriba</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '400px', overflowY: 'auto' }}>
          {files.map((file) => (
            <div
              key={file.id}
              style={{
                padding: '12px',
                border: '1px solid rgba(96, 120, 170, 0.25)',
                borderRadius: '10px',
                background: 'rgba(13, 20, 40, 0.45)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              {getFileIcon(file.file_type)}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text)', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {file.filename}
                </div>
                {file.description && (
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.description}
                  </div>
                )}
                <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                  {formatFileSize(file.file_size)} • {new Date(file.created_at).toLocaleDateString('es-ES')}
                </div>
              </div>
              <button
                onClick={() => handleDelete(file.id)}
                style={{
                  padding: '6px',
                  border: 'none',
                  background: 'rgba(239, 68, 68, 0.2)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal de subida */}
      {showUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'rgba(11, 16, 32, 0.95)',
            borderRadius: '16px',
            padding: '32px',
            width: '90%',
            maxWidth: '500px',
            border: '1px solid rgba(96, 120, 170, 0.3)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: 'var(--text)' }}>
                Subir Archivo
              </h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                  setUploadDescription('');
                }}
                style={{
                  padding: '4px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'var(--muted)'
                }}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpload}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                  Archivo (PDF, Word, Excel, TXT) - Máximo 50MB
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid rgba(96, 120, 170, 0.35)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'rgba(13, 20, 40, 0.5)',
                    color: 'var(--text)'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                  Descripción (opcional)
                </label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid rgba(96, 120, 170, 0.35)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical',
                    background: 'rgba(13, 20, 40, 0.5)',
                    color: 'var(--text)'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadFile(null);
                    setUploadDescription('');
                  }}
                  style={{
                    padding: '12px 24px',
                    background: 'rgba(13, 20, 40, 0.5)',
                    color: 'var(--text)',
                    border: '1px solid rgba(96, 120, 170, 0.35)',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={uploading || !uploadFile}
                  style={{
                    padding: '12px 24px',
                    background: uploading ? '#9ca3af' : 'linear-gradient(135deg, #1356f1 0%, #2563eb 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {uploading ? 'Subiendo...' : 'Subir Archivo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminConsole = () => {
  const { user, logout, client } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dashboardConfig, setDashboardConfig] = useState(DASHBOARD_DEFAULT_CONFIG);
  const [dashboardCatalog, setDashboardCatalog] = useState(DASHBOARD_CATALOG_FALLBACK);
  const [dashboardDefaults, setDashboardDefaults] = useState(DASHBOARD_DEFAULT_CONFIG);
  const [schemaTables, setSchemaTables] = useState({});
  const [schemaDataSource, setSchemaDataSource] = useState('main');
  const [schemaLoading, setSchemaLoading] = useState(false);
  const schemaCacheRef = useRef({});
  const [availableDataSources, setAvailableDataSources] = useState([
    { key: 'main', label: 'Principal (ambtu_platform)' }
  ]);
  const [selectedConfigClientId, setSelectedConfigClientId] = useState('');
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionFields, setNewSectionFields] = useState('');
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState('');
  const [configDirty, setConfigDirty] = useState(false);
  const [cfgSelectedSection, setCfgSelectedSection] = useState('');
  const [cfgSearch, setCfgSearch] = useState('');
  const [cfgLookerStep, setCfgLookerStep] = useState(0); // 0 cliente, 1 segmento, 2 datos, 3 campos
  const [cfgLookerSectionSearch, setCfgLookerSectionSearch] = useState('');
  const [cfgLookerFieldSearch, setCfgLookerFieldSearch] = useState('');
  const [cfgLookerSelectedFieldKey, setCfgLookerSelectedFieldKey] = useState('');
  const [cfgLookerCompact, setCfgLookerCompact] = useState(false);
  const [cfgShowAdvanced, setCfgShowAdvanced] = useState(true);
  const [cfgCopyFromClientId, setCfgCopyFromClientId] = useState('');
  const [cfgTemplate, setCfgTemplate] = useState('default');
  const [cfgFormulaPeriod, setCfgFormulaPeriod] = useState('today'); // today | last_7d | last_30d (solo para referencia, no aplica filtros)
  const [cfgFormulaCategory, setCfgFormulaCategory] = useState('outbound'); // analytics only (solo para referencia, no aplica filtros)
  const [cfgFormulaPreview, setCfgFormulaPreview] = useState({}); // key -> {loading,value,error,debug}
  const [cfgCalcSelection, setCfgCalcSelection] = useState({}); // key -> number[] (indices seleccionados)
  const [cfgCalcQuickAdd, setCfgCalcQuickAdd] = useState({}); // key -> {table, func, op, wrap, columns[]}
  const [cfgCalcExpandedKey, setCfgCalcExpandedKey] = useState(''); // key -> sección:campo (solo 1 abierto para editar)
  const [cfgCalcDrawerAdvanced, setCfgCalcDrawerAdvanced] = useState(false);
  const [cfgCalcWizardStep, setCfgCalcWizardStep] = useState(0); // 0 valores, 1 filtros, 2 probar
  const [cfgSubestadosJsonText, setCfgSubestadosJsonText] = useState(''); // Estado local para edición libre del JSON
  const [cfgSubestadosJsonError, setCfgSubestadosJsonError] = useState(''); // Error de parsing del JSON
  const [cfgSubestadosVisualMode, setCfgSubestadosVisualMode] = useState(true); // true = visual, false = JSON avanzado
  const [cfgSubestadosEditingIndex, setCfgSubestadosEditingIndex] = useState(null); // índice del nivel editando, null = nuevo
  const [cfgSubestadosFormData, setCfgSubestadosFormData] = useState({
    level: 1,
    key: '',
    label: '',
    parentKey: '',
    column: '',
    value: '',
    operator: 'equals'
  });

  const sectionLabels = {
    analytics_ai: 'Analíticas Agente IA',
    analytics: 'Analíticas',
    informes: 'Informes',
    conversations: 'Conversaciones',
    campaigns: 'Campañas',
    reports: 'Reportes',
    osint: 'OSINT',
  };

  // Helpers para el editor SaaS (evita UI críptica tipo “field_key”)
  const dashcfgEffectiveCatalog = dashboardCatalog || DASHBOARD_CATALOG_FALLBACK;
  const dashcfgEffectiveConfig = dashboardConfig || dashboardDefaults || DASHBOARD_DEFAULT_CONFIG;

  const dashcfgGetSectionLabel = (sectionKey) => sectionLabels[sectionKey] || dashcfgHumanizeKey(sectionKey);

  const dashcfgGetSectionCatalogFields = (sectionKey) =>
    (dashcfgEffectiveCatalog?.[sectionKey]?.fields || []);

  const dashcfgGetSectionMeta = (sectionKey) => {
    const catalogFields = dashcfgGetSectionCatalogFields(sectionKey);
    const meta = DASHCFG_SECTION_META[sectionKey];
    return meta || {
      description: 'Sección personalizada.',
      clientFields: catalogFields,
      dataFields: catalogFields,
      requiredMappings: [],
      preferredTableField: catalogFields[0],
    };
  };

  const dashcfgGetSectionCfg = (sectionKey) => {
    const baseCfg = {
      enabled: true,
      dataSource: 'main',
      fields: {},
      fieldMappings: {},
      fieldCalculations: {},
      fieldCalculationSpecs: {},
      ...(dashcfgEffectiveConfig?.sections?.[sectionKey] || {}),
    };
    // Asegurar que todos los campos del catálogo estén inicializados en fields
    // Esto es crítico para campos sin cálculo que aún no han sido configurados
    const catalogFields = dashcfgGetSectionCatalogFields(sectionKey);
    if (!baseCfg.fields) {
      baseCfg.fields = {};
    }
    catalogFields.forEach(fieldKey => {
      if (!(fieldKey in baseCfg.fields)) {
        // Campo no inicializado: usar default según tipo
        const isOptional = fieldKey.startsWith('custom_kpi_') || fieldKey.startsWith('agent_table_custom_');
        baseCfg.fields[fieldKey] = !isOptional; // false para opcionales, true para otros
      }
    });
    return baseCfg;
  };

  const dashcfgGetFieldLabel = (sectionKey, fieldKey) => {
    // Primero verificar si hay un label personalizado en la configuración del cliente
    const sectionCfg = dashcfgGetSectionCfg(sectionKey);
    const customLabel = sectionCfg?.fieldLabels?.[fieldKey];
    if (customLabel && typeof customLabel === 'string' && customLabel.trim()) {
      return customLabel.trim();
    }
    // Si no hay label personalizado, usar el label por defecto
    return DASHCFG_FIELD_LABELS?.[sectionKey]?.[fieldKey] || dashcfgHumanizeKey(fieldKey);
  };

  const dashcfgGetFieldHelp = (sectionKey, fieldKey) =>
    DASHCFG_FIELD_HELP?.[sectionKey]?.[fieldKey] || '';

  const dashcfgIsClientField = (sectionKey, fieldKey) =>
    (dashcfgGetSectionMeta(sectionKey).clientFields || []).includes(fieldKey);

  const dashcfgIsDataField = (sectionKey, fieldKey) =>
    (dashcfgGetSectionMeta(sectionKey).dataFields || []).includes(fieldKey);

  const dashcfgIsRequiredMapping = (sectionKey, fieldKey) =>
    (dashcfgGetSectionMeta(sectionKey).requiredMappings || []).includes(fieldKey);

  const dashcfgMappingComplete = (m) => Boolean(m && m.table && m.column);

  const dashcfgGetMissingRequiredMappings = (sectionKey, sectionCfg) => {
    const required = dashcfgGetSectionMeta(sectionKey).requiredMappings || [];
    const fm = sectionCfg?.fieldMappings || {};
    return required.filter((f) => !dashcfgMappingComplete(fm?.[f]));
  };

  const dashcfgGetSectionBaseTable = (sectionKey, sectionCfg) => {
    const meta = dashcfgGetSectionMeta(sectionKey);
    const preferredField = meta.preferredTableField || (dashcfgGetSectionCatalogFields(sectionKey)[0]);
    return (sectionCfg?.fieldMappings?.[preferredField]?.table) || '';
  };

  const dashcfgSetSectionBaseTable = (sectionKey, nextTable) => {
    const cols = schemaTables?.[nextTable] || [];
    const meta = dashcfgGetSectionMeta(sectionKey);
    const fields = dashcfgGetSectionCatalogFields(sectionKey);
    const required = meta.requiredMappings || [];
    updateSectionConfig(sectionKey, (sec) => {
      const nextMappings = { ...(sec.fieldMappings || {}) };
      fields.forEach((fieldKey) => {
        const existing = nextMappings[fieldKey];
        const existingCol = existing?.column || null;
        const keepCol = existingCol && cols.includes(existingCol) ? existingCol : null;
        // Solo tocar mapeos que ya existían o que sean requeridos
        if (existing || required.includes(fieldKey)) {
          nextMappings[fieldKey] = { table: nextTable || null, column: keepCol };
        }
      });
      return { ...sec, fieldMappings: nextMappings };
    });
  };

  const dashcfgSectionKeys = Object.keys(dashcfgEffectiveCatalog || {});
  const dashcfgSelectedSectionKey = (cfgSelectedSection && dashcfgSectionKeys.includes(cfgSelectedSection))
    ? cfgSelectedSection
    : (dashcfgSectionKeys[0] || '');

  const dashcfgSelectedSectionCfg = dashcfgGetSectionCfg(dashcfgSelectedSectionKey);
  const dashcfgSelectedMeta = dashcfgGetSectionMeta(dashcfgSelectedSectionKey);
  const dashcfgSelectedFieldsAll = dashcfgGetSectionCatalogFields(dashcfgSelectedSectionKey);
  const dashcfgSelectedMissingRequired = dashcfgGetMissingRequiredMappings(dashcfgSelectedSectionKey, dashcfgSelectedSectionCfg);
  const dashcfgSelectedBaseTable = dashcfgGetSectionBaseTable(dashcfgSelectedSectionKey, dashcfgSelectedSectionCfg);

  // Modo Looker: mantener un campo seleccionado por sección para editar en el panel derecho.
  useEffect(() => {
    const meta = dashcfgGetSectionMeta(dashcfgSelectedSectionKey);
    const all = dashcfgGetSectionCatalogFields(dashcfgSelectedSectionKey);
    const fields = (meta?.clientFields || all || []);
    if (!fields.length) return;
    setCfgLookerSelectedFieldKey((prev) => (prev && fields.includes(prev) ? prev : fields[0]));
  }, [dashcfgSelectedSectionKey]);

  // En el Explore, al cambiar de campo, vuelve a “Valores” y modo Simple (reduce confusión).
  useEffect(() => {
    if (!cfgLookerSelectedFieldKey) return;
    setCfgCalcDrawerAdvanced(false);
    setCfgCalcWizardStep(0);
  }, [dashcfgSelectedSectionKey, cfgLookerSelectedFieldKey]);

  // Sincronizar el estado local del JSON cuando cambia la configuración del servidor
  // Usar useRef para evitar loops infinitos y rastrear si el usuario está editando
  const subestadosJsonSyncingRef = useRef(false);
  const subestadosJsonLastSyncedRef = useRef('');

  useEffect(() => {
    if (dashcfgSelectedSectionKey === 'analytics_ai') {
      const hierarchy = dashcfgSelectedSectionCfg.subestadosConfig?.hierarchy || [];
      const jsonText = JSON.stringify(hierarchy, null, 2);
      // Solo actualizar si el texto es diferente del último sincronizado y no estamos en medio de una edición
      if (!subestadosJsonSyncingRef.current && jsonText !== subestadosJsonLastSyncedRef.current) {
        subestadosJsonSyncingRef.current = true;
        setCfgSubestadosJsonText(jsonText);
        setCfgSubestadosJsonError('');
        subestadosJsonLastSyncedRef.current = jsonText;
        // Resetear el flag después de un pequeño delay
        setTimeout(() => {
          subestadosJsonSyncingRef.current = false;
        }, 100);
      }
    } else {
      setCfgSubestadosJsonText('');
      setCfgSubestadosJsonError('');
      subestadosJsonLastSyncedRef.current = '';
    }
  }, [dashcfgSelectedSectionKey, dashcfgSelectedSectionCfg.subestadosConfig?.hierarchy]);

  // Funciones para el editor visual de subestados
  const getSubestadosHierarchy = () => {
    return dashcfgSelectedSectionCfg.subestadosConfig?.hierarchy || [];
  };

  const getMappedColumns = () => {
    const mappings = dashcfgSelectedSectionCfg.fieldMappings || {};
    const columns = [];
    Object.keys(mappings).forEach(fieldKey => {
      const mapping = mappings[fieldKey];
      if (mapping && mapping.column) {
        columns.push({
          fieldKey,
          column: mapping.column,
          label: dashcfgGetFieldLabel(dashcfgSelectedSectionKey, fieldKey)
        });
      }
    });
    return columns;
  };

  const getAvailableParentKeys = (currentLevel, excludeIndex = null) => {
    const hierarchy = getSubestadosHierarchy();
    return hierarchy
      .filter((item, idx) => {
        if (excludeIndex !== null && idx === excludeIndex) return false;
        return item.level < currentLevel;
      })
      .map(item => ({ key: item.key, label: item.label, level: item.level }));
  };

  const generateKeyFromLabel = (label) => {
    return label
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  };

  const validateSubestadoForm = () => {
    const { level, key, label, parentKey, column, value, operator } = cfgSubestadosFormData;
    if (!label.trim()) return 'La etiqueta es requerida';
    if (!key.trim()) return 'El identificador (key) es requerido';
    if (!column.trim()) return 'Debes seleccionar una columna';
    if (!value.trim()) return 'El valor es requerido';
    if (level > 1 && !parentKey.trim()) return 'Debes seleccionar un nivel padre para niveles 2 y 3';

    // Validar que el key no esté duplicado
    const hierarchy = getSubestadosHierarchy();
    const existingIndex = hierarchy.findIndex((item, idx) =>
      item.key === key && idx !== cfgSubestadosEditingIndex
    );
    if (existingIndex !== -1) return 'Este identificador ya existe. Usa uno diferente.';

    return null;
  };

  const handleAddSubestado = () => {
    const error = validateSubestadoForm();
    if (error) {
      setCfgSubestadosJsonError(error);
      return;
    }

    const hierarchy = getSubestadosHierarchy();
    const newItem = {
      level: cfgSubestadosFormData.level,
      key: cfgSubestadosFormData.key.trim(),
      label: cfgSubestadosFormData.label.trim(),
      column: cfgSubestadosFormData.column,
      value: cfgSubestadosFormData.value.trim(),
      operator: cfgSubestadosFormData.operator
    };

    if (cfgSubestadosFormData.level > 1) {
      newItem.parentKey = cfgSubestadosFormData.parentKey;
    }

    let updatedHierarchy;
    if (cfgSubestadosEditingIndex !== null) {
      // Editar existente
      updatedHierarchy = [...hierarchy];
      updatedHierarchy[cfgSubestadosEditingIndex] = newItem;
    } else {
      // Agregar nuevo
      updatedHierarchy = [...hierarchy, newItem];
    }

    updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
      ...sec,
      subestadosConfig: {
        ...(sec.subestadosConfig || {}),
        hierarchy: updatedHierarchy
      }
    }));

    // Resetear formulario
    setCfgSubestadosFormData({
      level: 1,
      key: '',
      label: '',
      parentKey: '',
      column: '',
      value: '',
      operator: 'equals'
    });
    setCfgSubestadosEditingIndex(null);
    setCfgSubestadosJsonError('');
  };

  const handleEditSubestado = (index) => {
    const hierarchy = getSubestadosHierarchy();
    const item = hierarchy[index];
    if (!item) return;

    setCfgSubestadosFormData({
      level: item.level || 1,
      key: item.key || '',
      label: item.label || '',
      parentKey: item.parentKey || '',
      column: item.column || '',
      value: item.value || '',
      operator: item.operator || 'equals'
    });
    setCfgSubestadosEditingIndex(index);
    setCfgSubestadosJsonError('');
  };

  const handleDeleteSubestado = (index) => {
    if (!window.confirm('¿Estás seguro de eliminar este nivel? Esto también eliminará todos sus hijos.')) {
      return;
    }

    const hierarchy = getSubestadosHierarchy();
    const itemToDelete = hierarchy[index];
    if (!itemToDelete) return;

    // Eliminar el item y todos sus hijos
    const updatedHierarchy = hierarchy.filter((item, idx) => {
      if (idx === index) return false;
      // Eliminar hijos directos
      if (item.parentKey === itemToDelete.key) return false;
      // Eliminar hijos indirectos (nivel 3 hijos de nivel 2 que era hijo del eliminado)
      if (item.level === 3 && hierarchy.find(p => p.key === item.parentKey && p.parentKey === itemToDelete.key)) {
        return false;
      }
      return true;
    });

    updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
      ...sec,
      subestadosConfig: {
        ...(sec.subestadosConfig || {}),
        hierarchy: updatedHierarchy
      }
    }));

    if (cfgSubestadosEditingIndex === index) {
      setCfgSubestadosEditingIndex(null);
      setCfgSubestadosFormData({
        level: 1,
        key: '',
        label: '',
        parentKey: '',
        column: '',
        value: '',
        operator: 'equals'
      });
    }
  };

  const handleCancelEdit = () => {
    setCfgSubestadosEditingIndex(null);
    setCfgSubestadosFormData({
      level: 1,
      key: '',
      label: '',
      parentKey: '',
      column: '',
      value: '',
      operator: 'equals'
    });
    setCfgSubestadosJsonError('');
  };

  const buildHierarchyTree = () => {
    const hierarchy = getSubestadosHierarchy();
    const tree = [];
    const level1 = hierarchy.filter(item => item.level === 1);

    level1.forEach(item1 => {
      const children2 = hierarchy.filter(item => item.level === 2 && item.parentKey === item1.key);
      const node1 = {
        ...item1,
        children: children2.map(item2 => {
          const children3 = hierarchy.filter(item => item.level === 3 && item.parentKey === item2.key);
          return {
            ...item2,
            children: children3
          };
        })
      };
      tree.push(node1);
    });

    return tree;
  };

  const dashcfgPreviewKey = (sectionKey, fieldKey) => `${sectionKey}:${fieldKey}`;

  const dashcfgPreviewFormula = async (sectionKey, fieldKey, formula, spec = null) => {
    const key = dashcfgPreviewKey(sectionKey, fieldKey);
    setCfgFormulaPreview((prev) => ({ ...prev, [key]: { loading: true, value: null, error: null, debug: null } }));
    try {
      let url = `${API_URL}/api/admin/dashboard-config/formula-preview`;
      if (user?.role === 'admin') {
        if (!selectedConfigClientId) throw new Error('Selecciona un cliente para previsualizar');
        url += `?client_id=${encodeURIComponent(selectedConfigClientId)}`;
      }
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_key: sectionKey,
          field_key: fieldKey,
          formula: (typeof formula === 'string' ? formula : ''),
          spec: (Array.isArray(spec) && spec.length > 0 ? spec : null),
          category: cfgFormulaCategory,
          period: cfgFormulaPeriod,
        }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = json?.detail ? String(json.detail) : 'No se pudo previsualizar la fórmula';
        throw new Error(detail);
      }
      setCfgFormulaPreview((prev) => ({ ...prev, [key]: { loading: false, value: json?.value, error: null, debug: json?.debug || null } }));
    } catch (e) {
      setCfgFormulaPreview((prev) => ({ ...prev, [key]: { loading: false, value: null, error: e.message || 'Error', debug: null } }));
    }
  };

  const DASHCFG_CALC_OPS = ['+', '-', '*', '/'];
  const DASHCFG_CALC_FUNCS = ['SUM', 'COUNT', 'COUNT_DISTINCT', 'AVG', 'MIN', 'MAX'];

  // Drag & drop (HTML5) para el editor visual
  const DASHCFG_DND_MIME = 'application/x-ambtu-dashcfg';
  const dashcfgDndSet = (e, payload) => {
    try {
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData(DASHCFG_DND_MIME, JSON.stringify(payload || {}));
      // fallback
      e.dataTransfer.setData('text/plain', JSON.stringify(payload || {}));
    } catch (_) {
      // noop
    }
  };
  const dashcfgDndGet = (e) => {
    try {
      const raw = e.dataTransfer.getData(DASHCFG_DND_MIME) || e.dataTransfer.getData('text/plain') || '';
      if (!raw) return null;
      const obj = JSON.parse(raw);
      return (obj && typeof obj === 'object') ? obj : null;
    } catch (_) {
      return null;
    }
  };

  const dashcfgCalcKey = (sectionKey, fieldKey) => `${sectionKey}:${fieldKey}`;

  const dashcfgGetSelectedTermIdxs = (sectionKey, fieldKey) => {
    const k = dashcfgCalcKey(sectionKey, fieldKey);
    const arr = cfgCalcSelection?.[k];
    return Array.isArray(arr) ? Array.from(new Set(arr)).filter((n) => Number.isInteger(n)).sort((a, b) => a - b) : [];
  };

  const dashcfgToggleSelectedTerm = (sectionKey, fieldKey, idx) => {
    const k = dashcfgCalcKey(sectionKey, fieldKey);
    setCfgCalcSelection((prev) => {
      const current = Array.isArray(prev?.[k]) ? prev[k] : [];
      const set = new Set(current);
      if (set.has(idx)) set.delete(idx);
      else set.add(idx);
      return { ...(prev || {}), [k]: Array.from(set).sort((a, b) => a - b) };
    });
  };

  const dashcfgClearSelection = (sectionKey, fieldKey) => {
    const k = dashcfgCalcKey(sectionKey, fieldKey);
    setCfgCalcSelection((prev) => ({ ...(prev || {}), [k]: [] }));
  };

  const dashcfgCalcNormalizeSpec = (spec) => {
    if (!Array.isArray(spec)) return [];
    const clampParen = (v) => {
      const n = parseInt(v ?? 0, 10);
      if (Number.isNaN(n) || n < 0) return 0;
      return Math.min(n, 3);
    };
    const normalizeFilters = (filters) => {
      if (!Array.isArray(filters)) return [];
      return filters
        .filter((f) => f && typeof f === 'object')
        .slice(0, 5)
        .map((f) => {
          const col = (typeof f.column === 'string' ? f.column : '').trim();
          const op = (typeof f.operator === 'string' ? f.operator : (typeof f.op === 'string' ? f.op : '')).trim().toLowerCase();
          const values = Array.isArray(f.values) ? f.values : (typeof f.value === 'string' ? [f.value] : []);
          const normVals = values
            .map((v) => (v == null ? '' : String(v)).trim())
            .filter(Boolean)
            .slice(0, 50);
          const safeOp = ['in', 'not_in', 'equals', 'not_equals', 'contains'].includes(op) ? op : 'in';
          return { column: col, operator: safeOp, values: normVals };
        })
        .filter((f) => Boolean(f.column));
    };
    return spec.filter((t) => t && typeof t === 'object').slice(0, 50).map((t, idx) => ({
      op: (idx === 0 ? null : (DASHCFG_CALC_OPS.includes(t.op) ? t.op : '+')),
      func: (typeof t.func === 'string' && DASHCFG_CALC_FUNCS.includes(t.func.toUpperCase()))
        ? t.func.toUpperCase()
        : 'COUNT',
      sourceType: (t.sourceType === 'field' || t.sourceType === 'number' || t.sourceType === 'table') ? t.sourceType : 'table',
      table: typeof t.table === 'string' ? t.table : '',
      column: typeof t.column === 'string' ? t.column : '',
      fieldKey: typeof t.fieldKey === 'string' ? t.fieldKey : '',
      number: (typeof t.number === 'number' || typeof t.number === 'string') ? t.number : '',
      openParen: clampParen(t.openParen),
      closeParen: clampParen(t.closeParen),
      filters: normalizeFilters(t.filters),
    }));
  };

  const dashcfgCalcBuildFormulaFromSpec = (spec) => {
    const terms = dashcfgCalcNormalizeSpec(spec);

    const buildTerm = (t) => {
      if (t.sourceType === 'number') {
        const n = (t.number ?? '').toString().trim();
        return n || '';
      }
      const func = (t.func || 'COUNT').toString().trim().toUpperCase();
      const safeFunc = DASHCFG_CALC_FUNCS.includes(func) ? func : 'COUNT';

      if (t.sourceType === 'field') {
        const fk = (t.fieldKey || '').toString().trim();
        if (!fk) return '';
        return `${safeFunc}({{${fk}}})`;
      }
      const table = (t.table || '').toString().trim();
      const column = (t.column || '').toString().trim();
      if (!table || !column) return '';
      return `${safeFunc}(${table}.${column})`;
    };

    const built = [];
    terms.forEach((t, idx) => {
      const expr = buildTerm(t);
      if (!expr) return;
      const open = '('.repeat(Math.max(0, t.openParen || 0));
      const close = ')'.repeat(Math.max(0, t.closeParen || 0));
      const wrapped = `${open}${expr}${close}`;
      if (built.length === 0) {
        built.push(wrapped);
      } else {
        const op = DASHCFG_CALC_OPS.includes(t.op) ? t.op : '+';
        built.push(`${op} ${wrapped}`);
      }
    });

    return built.join(' ');
  };

  const dashcfgCalcBuildHumanFromSpec = (spec) => {
    const terms = dashcfgCalcNormalizeSpec(spec);
    const opTxt = (o) => {
      if (!o) return '';
      if (DASHCFG_CALC_OPS.includes(o)) return o;
      return '+';
    };
    const opFilterTxt = (op) => {
      const o = String(op || '').toLowerCase();
      if (o === 'not_in') return 'NOT IN';
      if (o === 'in') return 'IN';
      if (o === 'equals') return '=';
      if (o === 'not_equals') return '!=';
      if (o === 'contains') return 'contiene';
      return 'IN';
    };
    const quote = (v) => {
      const s = String(v ?? '').trim();
      if (!s) return "''";
      return `'${s.replace(/'/g, "''")}'`;
    };
    const filterToTxt = (f) => {
      if (!f || typeof f !== 'object') return '';
      const col = String(f.column || '').trim();
      if (!col) return '';
      const o = String(f.operator || '').toLowerCase();
      const vals = Array.isArray(f.values) ? f.values : [];
      const shown = vals.slice(0, 4).map(quote);
      const tail = vals.length > 4 ? ` +${vals.length - 4}…` : '';
      if (o === 'contains') {
        return `${col} contiene ${quote(vals[0] || '')}`;
      }
      if (o === 'equals' || o === 'not_equals') {
        return `${col} ${opFilterTxt(o)} ${quote(vals[0] || '')}`;
      }
      return `${col} ${opFilterTxt(o)} (${shown.join(', ')}${tail})`;
    };
    const buildTerm = (t) => {
      if (t.sourceType === 'number') {
        const n = (t.number ?? '').toString().trim();
        return n || '';
      }
      const func = (t.func || 'COUNT').toString().trim().toUpperCase();
      const safeFunc = DASHCFG_CALC_FUNCS.includes(func) ? func : 'COUNT';
      let src = '';
      if (t.sourceType === 'field') {
        const fk = (t.fieldKey || '').toString().trim();
        if (!fk) return '';
        src = `{{${fk}}}`;
      } else {
        const table = (t.table || '').toString().trim();
        const column = (t.column || '').toString().trim();
        if (!table || !column) return '';
        src = `${table}.${column}`;
      }
      const fs = Array.isArray(t.filters) ? t.filters : [];
      const ftxt = fs.map(filterToTxt).filter(Boolean).join(' AND ');
      return `${safeFunc}(${src}${ftxt ? ` WHERE ${ftxt}` : ''})`;
    };
    const built = [];
    terms.forEach((t, idx) => {
      const expr = buildTerm(t);
      if (!expr) return;
      const open = '('.repeat(Math.max(0, t.openParen || 0));
      const close = ')'.repeat(Math.max(0, t.closeParen || 0));
      const wrapped = `${open}${expr}${close}`;
      if (built.length === 0) {
        built.push(wrapped);
      } else {
        built.push(`${opTxt(t.op)} ${wrapped}`);
      }
    });
    return built.join(' ');
  };

  const dashcfgSetCalcSpec = (sectionKey, fieldKey, nextSpec, { syncFormula } = { syncFormula: true }) => {
    const norm = dashcfgCalcNormalizeSpec(nextSpec);
    const nextFormula = dashcfgCalcBuildFormulaFromSpec(norm);
    updateSectionConfig(sectionKey, (sec) => {
      const nextSpecs = { ...(sec.fieldCalculationSpecs || {}) };
      nextSpecs[fieldKey] = norm;
      const nextCalcs = { ...(sec.fieldCalculations || {}) };
      if (syncFormula) {
        nextCalcs[fieldKey] = nextFormula || null;
      }
      return { ...sec, fieldCalculationSpecs: nextSpecs, fieldCalculations: nextCalcs };
    });
  };

  const dashcfgGroupSelectedTerms = (sectionKey, fieldKey, spec, mode = 'group') => {
    const selected = dashcfgGetSelectedTermIdxs(sectionKey, fieldKey);
    if (!Array.isArray(spec) || spec.length === 0) return;
    if (selected.length === 0) return;
    const first = Math.max(0, Math.min(selected[0], spec.length - 1));
    const last = Math.max(0, Math.min(selected[selected.length - 1], spec.length - 1));
    const next = spec.map((t, idx) => {
      const cur = t || {};
      if (idx === first) {
        const n = parseInt(cur.openParen ?? 0, 10) || 0;
        const v = mode === 'ungroup' ? Math.max(n - 1, 0) : Math.min(n + 1, 3);
        return { ...cur, openParen: v };
      }
      if (idx === last) {
        const n = parseInt(cur.closeParen ?? 0, 10) || 0;
        const v = mode === 'ungroup' ? Math.max(n - 1, 0) : Math.min(n + 1, 3);
        return { ...cur, closeParen: v };
      }
      return cur;
    });
    dashcfgSetCalcSpec(sectionKey, fieldKey, next, { syncFormula: true });
  };

  const dashcfgGetQuickAddState = (sectionKey, fieldKey, baseTable) => {
    const k = dashcfgCalcKey(sectionKey, fieldKey);
    const s = cfgCalcQuickAdd?.[k] || {};
    const table = typeof s.table === 'string' ? s.table : (baseTable || '');
    const func = typeof s.func === 'string' ? s.func : 'COUNT';
    const op = typeof s.op === 'string' ? s.op : '+';
    const wrap = Boolean(s.wrap);
    const columns = Array.isArray(s.columns) ? s.columns : [];
    const search = typeof s.search === 'string' ? s.search : '';
    const number = (typeof s.number === 'number' || typeof s.number === 'string') ? s.number : '';
    return { table, func, op, wrap, columns, search, number };
  };

  const dashcfgSetQuickAddState = (sectionKey, fieldKey, patch) => {
    const k = dashcfgCalcKey(sectionKey, fieldKey);
    setCfgCalcQuickAdd((prev) => ({ ...(prev || {}), [k]: { ...(prev?.[k] || {}), ...(patch || {}) } }));
  };

  const [cfgColumnValues, setCfgColumnValues] = useState({}); // key -> {loading, values, error}

  const dashcfgValuesKey = ({ sectionKey, dataSource, table, column, period, category, clientId }) =>
    [clientId || '', sectionKey || '', dataSource || '', table || '', column || '', period || '', category || ''].join('|');

  const dashcfgLoadColumnValues = async ({ sectionKey, table, column, dataSource }) => {
    const cid = (user?.role === 'admin' ? selectedConfigClientId : '');
    const key = dashcfgValuesKey({
      clientId: cid,
      sectionKey,
      dataSource: dataSource || 'main',
      table,
      column,
      period: cfgFormulaPeriod,
      category: cfgFormulaCategory,
    });
    if (cfgColumnValues?.[key]?.loading) return;
    if (Array.isArray(cfgColumnValues?.[key]?.values) && cfgColumnValues[key].values.length > 0) return;

    setCfgColumnValues((prev) => ({ ...(prev || {}), [key]: { loading: true, values: [], error: null } }));
    try {
      let url = `${API_URL}/api/admin/dashboard-config/column-values?section_key=${encodeURIComponent(sectionKey)}&table=${encodeURIComponent(table)}&column=${encodeURIComponent(column)}&period=${encodeURIComponent(cfgFormulaPeriod)}&category=${encodeURIComponent(cfgFormulaCategory)}&limit=120`;
      if (user?.role === 'admin') {
        if (!selectedConfigClientId) throw new Error('Selecciona un cliente');
        url += `&client_id=${encodeURIComponent(selectedConfigClientId)}`;
      }
      const resp = await fetch(url, { credentials: 'include' });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = json?.detail ? String(json.detail) : 'No se pudieron cargar valores';
        throw new Error(detail);
      }
      const vals = Array.isArray(json?.values) ? json.values : [];
      setCfgColumnValues((prev) => ({ ...(prev || {}), [key]: { loading: false, values: vals, error: null } }));
    } catch (e) {
      setCfgColumnValues((prev) => ({ ...(prev || {}), [key]: { loading: false, values: [], error: e.message || 'Error' } }));
    }
  };

  // Agregar clase admin-page al body cuando se monta el componente
  useEffect(() => {
    document.body.classList.add('admin-page');

    // Limpiar la clase cuando se desmonta el componente
    return () => {
      document.body.classList.remove('admin-page');
    };
  }, []);

  // States for forms
  const [newUser, setNewUser] = useState({
    username: '', email: '', full_name: '', password: '', role: 'user', client_id: ''
  });
  const [newClient, setNewClient] = useState({
    name: '', slug: '', logo_url: '', primary_color: '#3b82f6', secondary_color: '#8b5cf6', looker_dashboard_url: '',
    enabled_sections: { agent_analytics: true, analytics: true, campaigns: true, conversations: true, reports: true, osint: true },
    data_table_name: '',
    inbound_queues: '',
    outbound_campaigns: '',
    campaign_column: '',
    hiring_column: '',
    hiring_values: '',
    id_report_builder_inbound: '',
    id_report_builder_outbound: '',
    logica_abandonadas: 'default'
  });
  const [newClientAgents, setNewClientAgents] = useState([]);
  const [newPerTabUrls, setNewPerTabUrls] = useState({
    'general-metrics': '',
    'temporal-analysis': '',
    'ddi-campaigns': '',
    'conversion-kpis': ''
  });
  const [newGoContactTenantId, setNewGoContactTenantId] = useState('');
  // Campañas se gestionan desde CampaignManagement.js

  // States for editing
  const [editingUser, setEditingUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editingClientLookerUrl, setEditingClientLookerUrl] = useState('');
  const [editingPerTabUrls, setEditingPerTabUrls] = useState({
    'general-metrics': '',
    'temporal-analysis': '',
    'ddi-campaigns': '',
    'conversion-kpis': ''
  });
  const [editingGoContactTenantId, setEditingGoContactTenantId] = useState('');
  const [editingClientAgents, setEditingClientAgents] = useState([]);
  const [agentsToDelete, setAgentsToDelete] = useState([]);
  const [showClientModal, setShowClientModal] = useState(false);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  // Seleccionar cliente por defecto para configurar (solo admin)
  useEffect(() => {
    if (user?.role === 'admin' && !selectedConfigClientId && clients.length > 0) {
      setSelectedConfigClientId(clients[0].id);
    }
  }, [user?.role, clients, selectedConfigClientId]);

  // Cargar config cuando se entra en la pestaña y/o cambia el cliente objetivo
  useEffect(() => {
    if (activeTab === 'dashboardConfig') {
      loadDashboardConfig();
    }
  }, [activeTab, selectedConfigClientId]);

  // Mantener una sección seleccionada válida en el editor de vistas
  useEffect(() => {
    if (activeTab !== 'dashboardConfig') return;
    const keys = Object.keys(dashboardCatalog || DASHBOARD_CATALOG_FALLBACK);
    if (!keys.length) return;
    if (!cfgSelectedSection || !keys.includes(cfgSelectedSection)) {
      setCfgSelectedSection(keys[0]);
    }
  }, [activeTab, dashboardCatalog, cfgSelectedSection]);

  // Cargar schema según el dataSource de la sección seleccionada
  useEffect(() => {
    if (activeTab !== 'dashboardConfig') return;
    const ds = (dashcfgSelectedSectionCfg?.dataSource || 'main');
    if (ds !== schemaDataSource) {
      loadDashboardSchema(ds);
    }
  }, [activeTab, dashcfgSelectedSectionKey, dashcfgSelectedSectionCfg?.dataSource, schemaDataSource]);

  // Cerrar editor de cálculo al cambiar de sección (evita confusión)
  useEffect(() => {
    if (activeTab !== 'dashboardConfig') return;
    setCfgCalcExpandedKey('');
  }, [activeTab, dashcfgSelectedSectionKey]);

  useEffect(() => {
    // Al abrir un campo, arrancar en modo simple (tipo tabla dinámica)
    if (!cfgCalcExpandedKey) return;
    setCfgCalcDrawerAdvanced(false);
    setCfgCalcWizardStep(0);
  }, [cfgCalcExpandedKey]);

  // En modo Looker: si el usuario no es admin, saltar el paso de cliente.
  useEffect(() => {
    if (user?.role !== 'admin' && cfgLookerStep === 0) {
      setCfgLookerStep(1);
    }
  }, [user?.role, cfgLookerStep]);

  // Mejor UX: mientras el modal está abierto, bloquea el scroll del body
  useEffect(() => {
    if (!cfgCalcExpandedKey) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev || '';
    };
  }, [cfgCalcExpandedKey]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [usersResponse, clientsResponse] = await Promise.all([
        fetch(`${API_URL}/api/users`, { credentials: 'include' }),
        fetch(`${API_URL}/api/clients`, { credentials: 'include' })
      ]);

      if (usersResponse.ok && clientsResponse.ok) {
        const usersData = await usersResponse.json();
        const clientsData = await clientsResponse.json();


        // Los endpoints devuelven directamente arrays
        setUsers(usersData);
        setClients(clientsData);
      } else {
        setError('Error al cargar los datos');
      }
    } catch (err) {
      setError('Error al cargar los datos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const loadDashboardSchema = async (dataSourceKey = 'main') => {
    const ds = (dataSourceKey || 'main').toString();
    // cache in-memory para evitar reconsultas
    if (schemaCacheRef.current?.[ds]) {
      setSchemaTables(schemaCacheRef.current[ds]);
      setSchemaDataSource(ds);
      return;
    }
    setSchemaLoading(true);
    setConfigError(''); // Limpiar errores previos
    try {
      const resp = await fetch(`${API_URL}/api/admin/dashboard-config/schema?data_source=${encodeURIComponent(ds)}`, { credentials: 'include' });
      if (resp.ok) {
        const schema = await resp.json();
        const tables = schema.tables || {};
        schemaCacheRef.current[ds] = tables;
        setSchemaTables(tables);
        setSchemaDataSource(ds);
        // Si no hay tablas, mostrar un mensaje informativo
        if (Object.keys(tables).length === 0) {
          setConfigError(`No se encontraron tablas en el origen de datos "${ds}". Verifica que la base de datos tenga tablas disponibles.`);
        }
      } else {
        let errorMessage = `No se pudo cargar el esquema del origen "${ds}"`;
        try {
          const err = await resp.json();
          if (resp.status === 403) {
            errorMessage = 'No tienes permisos para acceder al esquema. Debes ser administrador.';
          } else if (resp.status === 401) {
            errorMessage = 'No estás autenticado. Por favor, inicia sesión nuevamente.';
          } else {
            const detail = err?.detail ? String(err.detail) : '';
            errorMessage = detail ? `${errorMessage}: ${detail}` : errorMessage;
          }
        } catch (e) {
          // Si no se puede parsear el error, usar el mensaje por defecto
        }
        setConfigError(errorMessage);
        setSchemaTables({});
        setSchemaDataSource(ds);
      }
    } catch (e) {
      setConfigError(`Error de red al cargar el esquema del origen "${ds}": ${e.message}`);
      setSchemaTables({});
      setSchemaDataSource(ds);
    } finally {
      setSchemaLoading(false);
    }
  };

  const loadDashboardConfig = async () => {
    setConfigLoading(true);
    setConfigError('');
    try {
      let configUrl = `${API_URL}/api/admin/dashboard-config`;
      if (user?.role === 'admin') {
        if (!selectedConfigClientId) {
          throw new Error('Selecciona un cliente para configurar');
        }
        configUrl += `?client_id=${encodeURIComponent(selectedConfigClientId)}`;
      }
      const configResp = await fetch(configUrl, { credentials: 'include' });
      if (!configResp.ok) throw new Error('No se pudo cargar la configuración de vistas');
      const data = await configResp.json();
      let config = data.config || null;
      // Asegurar que todos los campos del catálogo estén inicializados en fields
      // Esto es crítico para campos sin cálculo que aún no han sido configurados
      if (config && config.sections) {
        const catalog = data.catalog || DASHBOARD_CATALOG_FALLBACK;
        for (const sectionKey in config.sections) {
          const section = config.sections[sectionKey];
          if (section && catalog[sectionKey] && catalog[sectionKey].fields) {
            if (!section.fields) {
              section.fields = {};
            }
            const catalogFields = catalog[sectionKey].fields;
            catalogFields.forEach(fieldKey => {
              if (!(fieldKey in section.fields)) {
                // Campo no inicializado: usar default según tipo
                const isOptional = fieldKey.startsWith('custom_kpi_') || fieldKey.startsWith('agent_table_custom_');
                section.fields[fieldKey] = !isOptional; // false para opcionales, true para otros
              }
            });
          }
          // Asegurar que fieldAllowAudio esté inicializado para analytics_ai
          if (sectionKey === 'analytics_ai' && section) {
            if (!section.fieldAllowAudio) {
              section.fieldAllowAudio = {};
            }
          }
        }
      }
      setDashboardConfig(config);
      setDashboardCatalog(data.catalog || DASHBOARD_CATALOG_FALLBACK);
      setDashboardDefaults(data.defaults || DASHBOARD_DEFAULT_CONFIG);
      setConfigDirty(false);
      if (Array.isArray(data.data_sources) && data.data_sources.length > 0) {
        setAvailableDataSources(data.data_sources);
      } else {
        setAvailableDataSources([{ key: 'main', label: 'Principal (ambtu_platform)' }]);
      }
      // Por defecto, cargar schema de main; otros DS se cargan al seleccionar la sección
      await loadDashboardSchema('main');
    } catch (err) {
      setConfigError(err.message);
      if (!dashboardDefaults) {
        setDashboardDefaults(DASHBOARD_DEFAULT_CONFIG);
      }
      if (!Object.keys(dashboardCatalog || {}).length) {
        setDashboardCatalog(DASHBOARD_CATALOG_FALLBACK);
      }
    } finally {
      setConfigLoading(false);
    }
  };

  const updateSectionConfig = (sectionKey, updater) => {
    setConfigDirty(true);
    setDashboardConfig((prev) => {
      const base = prev || dashboardDefaults || DASHBOARD_DEFAULT_CONFIG;
      const sections = { ...(base.sections || {}) };
      const current = {
        enabled: true,
        dataSource: 'main',
        fields: {},
        fieldMappings: {},
        fieldCalculations: {},
        fieldCalculationSpecs: {},
        fieldLabels: {},
        fieldFormats: {},
        fieldAllowAudio: {}, // Configuración de audio/transcripciones
        ...(sections[sectionKey] || {}),
      };
      const nextSection = updater({
        ...current,
        fields: { ...(current.fields || {}) },
        fieldMappings: { ...(current.fieldMappings || {}) },
        fieldCalculations: { ...(current.fieldCalculations || {}) },
        fieldCalculationSpecs: { ...(current.fieldCalculationSpecs || {}) },
        fieldLabels: { ...(current.fieldLabels || {}) },
        fieldFormats: { ...(current.fieldFormats || {}) },
        fieldAllowAudio: { ...(current.fieldAllowAudio || {}) },
      });
      return {
        sections: {
          ...sections,
          [sectionKey]: nextSection,
        },
      };
    });
  };

  const saveDashboardConfig = async () => {
    setConfigSaving(true);
    setConfigError('');
    try {
      // CRÍTICO: Usar una función de callback para obtener el estado más reciente
      // porque setDashboardConfig es asíncrono y puede que el estado no esté actualizado
      const currentConfig = dashboardConfig || dashboardDefaults || DASHBOARD_DEFAULT_CONFIG;
      console.log('Estado actual dashboardConfig antes de guardar:', currentConfig.sections?.analytics_ai?.fields);
      console.log('Estado actual fieldAllowAudio antes de guardar:', currentConfig.sections?.analytics_ai?.fieldAllowAudio);

      // Hacer una copia profunda para no mutar el estado original
      const payload = {
        sections: JSON.parse(JSON.stringify(currentConfig.sections || {})),
      };

      // Asegurar que fieldAllowAudio esté presente en analytics_ai antes de normalizar
      if (payload.sections?.analytics_ai) {
        if (!payload.sections.analytics_ai.fieldAllowAudio) {
          payload.sections.analytics_ai.fieldAllowAudio = currentConfig.sections?.analytics_ai?.fieldAllowAudio || {};
        }
      }

      console.log('Payload sections analytics_ai.fields ANTES de normalizar:', payload.sections?.analytics_ai?.fields);
      console.log('Payload sections analytics_ai.fieldAllowAudio ANTES de normalizar:', payload.sections?.analytics_ai?.fieldAllowAudio);

      // Asegurar que todos los campos estén explícitamente incluidos (incluso los que están en false)
      // Esto es importante para que el backend respete los valores false
      for (const sectionKey in payload.sections) {
        const section = payload.sections[sectionKey];
        if (section) {
          // Asegurar que todos los campos del catálogo estén presentes
          const catalogFields = dashcfgGetSectionCatalogFields(sectionKey);
          const normalizedFields = {};

          // CRÍTICO: Primero copiar TODOS los campos que existen en section.fields
          // Esto incluye los que están en false
          if (section.fields) {
            for (const fieldKey in section.fields) {
              if (catalogFields.includes(fieldKey)) {
                // Campo existe: usar ese valor (puede ser false) - CRÍTICO preservar false
                normalizedFields[fieldKey] = Boolean(section.fields[fieldKey]);
              }
            }
          }

          // Luego, agregar campos faltantes con sus defaults
          catalogFields.forEach(fieldKey => {
            if (!(fieldKey in normalizedFields)) {
              // Campo no existe: usar default según tipo
              const isOptional = fieldKey.startsWith('custom_kpi_') || fieldKey.startsWith('agent_table_custom_');
              normalizedFields[fieldKey] = !isOptional; // false para opcionales, true para otros
            }
          });

          section.fields = normalizedFields;

          // Asegurar que fieldAllowAudio esté presente en analytics_ai
          if (sectionKey === 'analytics_ai') {
            // Si no existe fieldAllowAudio, inicializarlo como objeto vacío
            if (!section.fieldAllowAudio || typeof section.fieldAllowAudio !== 'object') {
              section.fieldAllowAudio = {};
            }

            // Log temporal para debugging
            const customFields = Object.keys(normalizedFields).filter(k =>
              k.startsWith('custom_kpi_') || k.startsWith('agent_table_custom_')
            ).reduce((acc, k) => {
              acc[k] = normalizedFields[k];
              return acc;
            }, {});
            console.log('Payload analytics_ai campos personalizados ANTES de enviar:', customFields);
            console.log('Payload completo analytics_ai.fields:', normalizedFields);
            // Log de fieldAllowAudio en payload
            console.log('Payload analytics_ai.fieldAllowAudio:', section.fieldAllowAudio);
          }
        }
      }
      // Log completo del payload antes de enviar
      console.log('📤 Payload completo ANTES de enviar:', JSON.stringify(payload, null, 2));
      let url = `${API_URL}/api/admin/dashboard-config`;
      if (user?.role === 'admin') {
        if (!selectedConfigClientId) {
          throw new Error('Selecciona un cliente para guardar');
        }
        url += `?client_id=${encodeURIComponent(selectedConfigClientId)}`;
      }
      const resp = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const errorText = await resp.text();
        let errorMsg = 'No se pudo guardar la configuración de vistas';
        try {
          const errorJson = JSON.parse(errorText);
          errorMsg = errorJson.detail || errorMsg;
        } catch {
          // Si no es JSON, usar el texto del error
          if (errorText) {
            errorMsg = `${errorMsg}: ${errorText.substring(0, 200)}`;
          }
        }
        throw new Error(errorMsg);
      }
      const data = await resp.json();
      // Usar la configuración devuelta por el backend (que ya está normalizada)
      // Esto asegura que los valores False se preserven correctamente
      if (data.config && data.config.sections) {
        // Log de fieldAllowAudio en la respuesta del backend
        if (data.config.sections?.analytics_ai?.fieldAllowAudio) {
          console.log('✅ fieldAllowAudio en respuesta del backend:', data.config.sections.analytics_ai.fieldAllowAudio);
        } else {
          console.log('⚠️ fieldAllowAudio NO está en la respuesta del backend');
          // Si no está en la respuesta pero estaba en el payload, preservarlo
          if (payload.sections?.analytics_ai?.fieldAllowAudio) {
            if (!data.config.sections.analytics_ai) {
              data.config.sections.analytics_ai = {};
            }
            data.config.sections.analytics_ai.fieldAllowAudio = payload.sections.analytics_ai.fieldAllowAudio;
            console.log('✅ fieldAllowAudio preservado desde payload:', data.config.sections.analytics_ai.fieldAllowAudio);
          }
        }
        setDashboardConfig(data.config);
      } else {
        // Fallback: usar el payload si el backend no devuelve config
        setDashboardConfig({ sections: payload.sections });
      }
      setConfigDirty(false);
      showSuccess('Configuración guardada');
    } catch (err) {
      setConfigError(err.message);
    } finally {
      setConfigSaving(false);
    }
  };

  const addCustomSection = () => {
    if (!newSectionName.trim()) return;
    setConfigDirty(true);
    const fields = newSectionFields
      .split(',')
      .map((f) => f.trim())
      .filter(Boolean);
    setDashboardConfig((prev) => {
      const base = prev || dashboardDefaults || DASHBOARD_DEFAULT_CONFIG;
      const sections = { ...(base.sections || {}) };
      if (sections[newSectionName]) return base; // no sobrescribir
      sections[newSectionName] = {
        enabled: true,
        dataSource: 'main',
        fields: fields.reduce((acc, f) => ({ ...acc, [f]: true }), {}),
        fieldMappings: fields.reduce((acc, f) => ({ ...acc, [f]: null }), {}),
        fieldCalculations: fields.reduce((acc, f) => ({ ...acc, [f]: null }), {}),
        fieldCalculationSpecs: fields.reduce((acc, f) => ({ ...acc, [f]: [] }), {}),
      };
      return { sections };
    });
    setDashboardCatalog((prev) => ({
      ...(prev || {}),
      [newSectionName]: { fields: fields },
    }));
    setCfgSelectedSection(newSectionName);
    setNewSectionName('');
    setNewSectionFields('');
  };

  const dashcfgResetToDefaults = () => {
    const ok = window.confirm('¿Restaurar la configuración por defecto para este cliente? (Luego debes Guardar cambios)');
    if (!ok) return;
    setDashboardConfig(dashboardDefaults || DASHBOARD_DEFAULT_CONFIG);
    setConfigDirty(true);
    showSuccess('Restaurado a valores por defecto (pendiente de guardar)');
  };

  const dashcfgApplyTemplate = (templateKey) => {
    const current = dashboardConfig || dashboardDefaults || DASHBOARD_DEFAULT_CONFIG;
    const nextSections = { ...(current.sections || {}) };

    const setEnabled = (key, val) => {
      if (!nextSections[key]) nextSections[key] = { enabled: true, dataSource: 'main', fields: {}, fieldMappings: {}, fieldCalculations: {}, fieldCalculationSpecs: {} };
      nextSections[key] = { ...nextSections[key], enabled: Boolean(val) };
    };

    if (templateKey === 'analytics_only') {
      setEnabled('analytics_ai', true);
      setEnabled('analytics', true);
      setEnabled('conversations', false);
    } else if (templateKey === 'conversations_only') {
      setEnabled('analytics_ai', false);
      setEnabled('analytics', false);
      setEnabled('conversations', true);
    } else if (templateKey === 'minimal') {
      setEnabled('analytics_ai', true);
      setEnabled('analytics', false);
      setEnabled('conversations', false);
      // Reducir campos visibles de cliente en analytics_ai
      const sec = nextSections.analytics_ai || {};
      const meta = DASHCFG_SECTION_META.analytics_ai;
      const clientFields = meta?.clientFields || [];
      const keep = new Set(['total_calls', 'total_calls_completed', 'avg_duration', 'chart_by_hour']);
      const nextFields = { ...(sec.fields || {}) };
      clientFields.forEach((f) => { nextFields[f] = keep.has(f); });
      nextSections.analytics_ai = { ...sec, fields: nextFields };
    } else {
      // default
      setDashboardConfig(dashboardDefaults || DASHBOARD_DEFAULT_CONFIG);
      setConfigDirty(true);
      showSuccess('Plantilla aplicada (por defecto). Pendiente de guardar.');
      return;
    }

    setDashboardConfig({ sections: nextSections });
    setConfigDirty(true);
    showSuccess('Plantilla aplicada (pendiente de guardar)');
  };

  const dashcfgCopyFromClient = async () => {
    if (user?.role !== 'admin') return;
    if (!cfgCopyFromClientId) {
      setConfigError('Selecciona un cliente origen para copiar');
      return;
    }
    if (cfgCopyFromClientId === selectedConfigClientId) {
      setConfigError('El cliente origen no puede ser el mismo que el destino');
      return;
    }
    setConfigLoading(true);
    setConfigError('');
    try {
      const url = `${API_URL}/api/admin/dashboard-config?client_id=${encodeURIComponent(cfgCopyFromClientId)}`;
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) throw new Error('No se pudo cargar la configuración del cliente origen');
      const data = await resp.json();
      setDashboardConfig(data.config || null);
      setConfigDirty(true);
      showSuccess('Configuración copiada (pendiente de guardar)');
    } catch (err) {
      setConfigError(err.message);
    } finally {
      setConfigLoading(false);
    }
  };

  const dashcfgCopyJson = async () => {
    try {
      const payload = {
        sections: (dashboardConfig || dashboardDefaults || DASHBOARD_DEFAULT_CONFIG).sections,
      };
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      showSuccess('JSON copiado al portapapeles');
    } catch (e) {
      setConfigError('No se pudo copiar el JSON (permiso del navegador).');
    }
  };

  // Funciones para manejar agentes
  const addNewAgent = () => {
    setNewClientAgents([...newClientAgents, { name: '', agent_id: '', description: '' }]);
  };

  const removeNewAgent = (index) => {
    setNewClientAgents(newClientAgents.filter((_, i) => i !== index));
  };

  const updateNewAgent = (index, field, value) => {
    const updatedAgents = [...newClientAgents];
    updatedAgents[index][field] = value;
    setNewClientAgents(updatedAgents);
  };

  const addEditingAgent = () => {
    setEditingClientAgents([...editingClientAgents, { name: '', agent_id: '', description: '' }]);
  };

  const removeEditingAgent = (index) => {
    const agent = editingClientAgents[index];
    if (agent.id) {
      setAgentsToDelete([...agentsToDelete, agent.id]);
    }
    setEditingClientAgents(editingClientAgents.filter((_, i) => i !== index));
  };

  const updateEditingAgent = (index, field, value) => {
    const updatedAgents = [...editingClientAgents];
    updatedAgents[index][field] = value;
    setEditingClientAgents(updatedAgents);
  };

  const loadClientAgents = async (clientId) => {
    setAgentsToDelete([]); // Reiniciar lista de eliminación
    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}/agents`, {
        credentials: 'include'
      });
      if (response.ok) {
        const agents = await response.json();
        setEditingClientAgents(agents);
      }
    } catch (err) {
      console.error('Error loading client agents:', err);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser),
        credentials: 'include'
      });
      if (response.ok) {
        showSuccess('Usuario creado exitosamente');
        setNewUser({ username: '', email: '', full_name: '', password: '', role: 'user', client_id: '' });
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Error al crear usuario');
      }
    } catch (err) {
      setError('Error al crear usuario: ' + err.message);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newClient,
        enabled_sections: JSON.stringify(newClient.enabled_sections),
        settings: JSON.stringify({
          looker_dashboard_urls: newPerTabUrls,
          ...(newGoContactTenantId ? { gocontact_tenant_id: newGoContactTenantId } : {})
        })
      };
      const response = await fetch(`${API_URL}/api/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });
      if (response.ok) {
        const clientData = await response.json();
        const clientId = clientData.id;

        // Crear agentes si los hay
        if (newClientAgents.length > 0) {
          for (const agent of newClientAgents) {
            if (agent.name && agent.agent_id) {
              await fetch(`${API_URL}/api/agents?client_id=${clientId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(agent),
                credentials: 'include'
              });
            }
          }
        }

        showSuccess('Cliente creado exitosamente');
        setNewClient({
          name: '', slug: '', logo_url: '', primary_color: '#3b82f6', secondary_color: '#8b5cf6', looker_dashboard_url: '',
          enabled_sections: { analytics: true, campaigns: true, conversations: true, reports: true, osint: true },
          data_table_name: '',
          inbound_queues: '',
          outbound_campaigns: '',
          campaign_column: '',
          hiring_column: '',
          hiring_values: ''
        });
        setNewClientAgents([]);
        setNewPerTabUrls({ 'general-metrics': '', 'temporal-analysis': '', 'ddi-campaigns': '', 'conversion-kpis': '' });
        setNewGoContactTenantId('');
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Error al crear cliente');
      }
    } catch (err) {
      setError('Error al crear cliente: ' + err.message);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/users/${editingUser.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingUser),
        credentials: 'include'
      });
      if (response.ok) {
        showSuccess('Usuario actualizado exitosamente');
        setShowUserModal(false);
        setEditingUser(null);
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Error al actualizar usuario');
      }
    } catch (err) {
      setError('Error al actualizar usuario: ' + err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        showSuccess('Usuario eliminado exitosamente');
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Error al eliminar usuario');
      }
    } catch (err) {
      setError('Error al eliminar usuario: ' + err.message);
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    try {
      // Combinar settings existentes con las URLs por pestaña
      let body = { ...editingClient };

      // Serializar enabled_sections
      body.enabled_sections = JSON.stringify(editingClient.enabled_sections);

      try {
        const currentSettings = editingClient.settings ? (typeof editingClient.settings === 'string' ? JSON.parse(editingClient.settings) : editingClient.settings) : {};
        body.settings = JSON.stringify({
          ...currentSettings,
          looker_dashboard_urls: editingPerTabUrls,
          ...(editingGoContactTenantId ? { gocontact_tenant_id: editingGoContactTenantId } : {})
        });
      } catch (err) {
        body.settings = JSON.stringify({
          looker_dashboard_urls: editingPerTabUrls,
          ...(editingGoContactTenantId ? { gocontact_tenant_id: editingGoContactTenantId } : {})
        });
      }
      const response = await fetch(`${API_URL}/api/clients/${editingClient.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include'
      });
      if (response.ok) {
        // Eliminar agentes marcados
        if (agentsToDelete.length > 0) {
          for (const agentId of agentsToDelete) {
            await fetch(`${API_URL}/api/agents/${agentId}`, {
              method: 'DELETE',
              credentials: 'include'
            });
          }
        }

        // Manejar agentes - crear nuevos y actualizar existentes
        for (const agent of editingClientAgents) {
          if (agent.name && agent.agent_id) {
            if (agent.id) {
              // Actualizar agente existente
              await fetch(`${API_URL}/api/agents/${agent.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  name: agent.name,
                  agent_id: agent.agent_id,
                  description: agent.description
                }),
                credentials: 'include'
              });
            } else {
              // Crear nuevo agente
              await fetch(`${API_URL}/api/agents?client_id=${editingClient.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  name: agent.name,
                  agent_id: agent.agent_id,
                  description: agent.description
                }),
                credentials: 'include'
              });
            }
          }
        }

        showSuccess('Cliente actualizado exitosamente');
        setShowClientModal(false);
        setEditingClient(null);
        setEditingClientAgents([]);
        loadData();

        // Si el cliente actualizado es el cliente del usuario actual, recargar la página
        if (client?.id === editingClient.id) {
          window.location.reload();
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Error al actualizar cliente');
      }
    } catch (err) {
      setError('Error al actualizar cliente: ' + err.message);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este cliente?')) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        showSuccess('Cliente eliminado exitosamente');
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Error al eliminar cliente');
      }
    } catch (err) {
      setError('Error al eliminar cliente: ' + err.message);
    }
  };

  // ===== FUNCIONES PARA CAMPAÑAS =====
  // Las campañas se gestionan desde CampaignManagement.js
  // Aquí solo se mantiene la estructura básica del tab

  const handleLogout = async () => {
    await logout();
  };

  // Estadísticas del dashboard
  const stats = {
    totalUsers: users.length,
    activeUsers: users.filter(u => u.is_active).length,
    adminUsers: users.filter(u => u.role === 'admin').length,
    totalClients: clients.length,
    activeClients: clients.filter(c => c.is_active).length
  };

  return (
    <>
      <div className="stage">
        <div className="aurora"></div>
        <div className="glow g1"></div>
        <div className="glow g2"></div>
        <div className="waves">
          <svg className="w1" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path fill="#1a2650" fillOpacity="1" d="M0,160L60,144C120,128,240,96,360,117.3C480,139,600,213,720,208C840,203,960,117,1080,85.3C1200,53,1320,75,1380,85.3L1440,96L1440,320L0,320Z"></path>
          </svg>
          <svg className="w2" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path fill="#0f1a3e" fillOpacity="0.9" d="M0,192L120,165.3C240,139,480,85,720,101.3C960,117,1200,203,1320,240L1440,277.3L1440,320L0,320Z"></path>
          </svg>
          <svg className="w3" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path fill="#09122d" fillOpacity="0.85" d="M0,128L80,154.7C160,181,320,235,480,229.3C640,224,800,160,960,149.3C1120,139,1280,181,1360,202.7L1440,224L1440,320L0,320Z"></path>
          </svg>
        </div>
      </div>
      <div className="spot"></div>
      <div className="grain"></div>

      <div className="wrap">
        {loading && (
          <div className="card-wrap">
            <div className="card">
              <h2>Cargando datos...</h2>
              <p>Por favor espera mientras se cargan los datos del sistema.</p>
            </div>
          </div>
        )}

        {error && (
          <div className="card-wrap">
            <div className="card error-card">
              <h2 style={{ color: '#ff7b7b' }}>❌ Error</h2>
              <p>{error}</p>
              <button onClick={loadData} className="btn-retry">
                🔄 Reintentar
              </button>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="card-wrap">
            <div className="card success-card">
              <h2 style={{ color: '#4ade80' }}>✅ Éxito</h2>
              <p>{successMessage}</p>
            </div>
          </div>
        )}

        {!loading && !error && (
          <div className="admin-container">
            {/* Header Mejorado */}
            <div className="admin-header-enhanced">
              <div className="admin-header-content">
                <div className="admin-header-left">
                  <div className="admin-logo-container">
                    <img
                      src="/AMBTU_logo.svg"
                      alt="AmbTu Logo"
                      className="admin-logo"
                    />
                  </div>
                  <div className="admin-header-text">
                    <h1 className="admin-title">Panel de Administración</h1>
                    <p className="admin-subtitle">Bienvenido, <span className="username-highlight">{user?.username}</span>. Gestiona usuarios y clientes del sistema.</p>
                  </div>
                </div>
                <div className="admin-header-right">
                  <div className="admin-stats-mini">
                    <div className="stat-mini">
                      <div className="stat-mini-value">{stats.totalUsers}</div>
                      <div className="stat-mini-label">Usuarios</div>
                    </div>
                    <div className="stat-mini">
                      <div className="stat-mini-value">{stats.totalClients}</div>
                      <div className="stat-mini-label">Campañas</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="admin-header-decoration"></div>
            </div>

            {/* Content */}
            <div className="admin-content">
              {/* Estadísticas */}
              <div className="admin-stats">
                <h3>📊 Estadísticas</h3>
                <div className="sidebar-stats">
                  <div className="sidebar-stat" style={{ animationDelay: '0.1s' }}>
                    <div className="sidebar-stat-icon" style={{ color: '#8b5cf6' }}>
                      <UsersIcon />
                    </div>
                    <div className="sidebar-stat-number">{stats.totalUsers}</div>
                    <div className="sidebar-stat-label">Total Usuarios</div>
                  </div>

                  <div className="sidebar-stat" style={{ animationDelay: '0.2s' }}>
                    <div className="sidebar-stat-icon" style={{ color: '#f59e0b' }}>
                      <CrownIcon />
                    </div>
                    <div className="sidebar-stat-number">{stats.adminUsers}</div>
                    <div className="sidebar-stat-label">Administradores</div>
                  </div>

                  <div className="sidebar-stat" style={{ animationDelay: '0.3s' }}>
                    <div className="sidebar-stat-icon" style={{ color: '#06b6d4' }}>
                      <BuildingIcon />
                    </div>
                    <div className="sidebar-stat-number">{stats.totalClients}</div>
                    <div className="sidebar-stat-label">Total Campañas</div>
                  </div>

                  <div className="sidebar-stat" style={{ animationDelay: '0.4s' }}>
                    <div className="sidebar-stat-icon" style={{ color: '#10b981' }}>
                      <ZapIcon />
                    </div>
                    <div className="sidebar-stat-number">{stats.totalUsers + stats.totalClients}</div>
                    <div className="sidebar-stat-label">Total Entidades</div>
                  </div>
                </div>
              </div>

              {/* Navigation Bar */}
              <div className="admin-navigation">
                <div className="admin-tabs">
                  <button
                    className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => setActiveTab('users')}
                  >
                    <UsersIcon />
                    Usuarios
                  </button>
                  <button
                    className={`admin-tab ${activeTab === 'clients' ? 'active' : ''}`}
                    onClick={() => setActiveTab('clients')}
                  >
                    <BuildingIcon />
                    Clientes
                  </button>
                  <button
                    className={`admin-tab ${activeTab === 'dashboardConfig' ? 'active' : ''}`}
                    onClick={() => setActiveTab('dashboardConfig')}
                  >
                    <SettingsIcon />
                    Editor de vistas
                  </button>
                </div>
              </div>

              {/* Main Content */}
              <div className="admin-main">


                {/* Users Content */}
                {activeTab === 'users' && (
                  <div className="main-section">
                    <h2>👥 Gestión de Usuarios</h2>
                    <p>Administra los usuarios del sistema</p>

                    <div className="form-section">
                      <h3>➕ Crear Nuevo Usuario</h3>
                      <form onSubmit={handleCreateUser}>
                        <div className="form-row">
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Usuario *</label>
                            <input
                              type="text"
                              value={newUser.username}
                              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                              required
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Email</label>
                            <input
                              type="email"
                              value={newUser.email}
                              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Nombre Completo</label>
                            <input
                              type="text"
                              value={newUser.full_name}
                              onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Contraseña *</label>
                            <input
                              type="password"
                              value={newUser.password}
                              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                              required
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Rol</label>
                            <select
                              value={newUser.role}
                              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                            >
                              <option value="user">Usuario</option>
                              <option value="admin">Administrador</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Campaña</label>
                            <select
                              value={newUser.client_id}
                              onChange={(e) => setNewUser({ ...newUser, client_id: e.target.value })}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                            >
                              <option value="">Sin cliente</option>
                              {clients.map(client => (
                                <option key={client.id} value={client.id}>{client.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <button
                          type="submit"
                          style={{
                            background: 'var(--accent)',
                            color: 'white',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <PlusIcon />
                          Crear Usuario
                        </button>
                      </form>
                    </div>

                    <div className="form-section">
                      <h3>📋 Lista de Usuarios ({users.length})</h3>
                      <div className="users-grid">
                        {users.map((user, index) => (
                          <div key={user.id} className="user-card" style={{
                            background: 'var(--glass-bg)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid var(--glass-border)',
                            borderRadius: '16px',
                            padding: '20px',
                            animationDelay: `${index * 0.1}s`,
                            transition: 'all 0.3s ease'
                          }}>
                            <div className="user-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                              <div className="user-avatar" style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                background: user.role === 'admin' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px',
                                color: 'white',
                                marginRight: '16px',
                                fontWeight: 'bold'
                              }}>
                                {user.username.charAt(0).toUpperCase()}
                              </div>
                              <div className="user-info" style={{ flex: 1 }}>
                                <h4 style={{ margin: '0 0 4px 0', color: 'var(--text)', fontSize: '16px', fontWeight: '600' }}>{user.username}</h4>
                                <p style={{ margin: '0', color: 'var(--muted)', fontSize: '14px' }}>{user.email || 'Sin email'}</p>
                              </div>
                            </div>

                            <div className="user-details">
                              <div className="user-badges" style={{ marginBottom: '16px' }}>
                                <span style={{
                                  padding: '6px 12px',
                                  borderRadius: '16px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  background: user.role === 'admin' ? 'rgba(251, 146, 60, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                                  color: user.role === 'admin' ? '#f59e0b' : '#3b82f6',
                                  border: `1px solid ${user.role === 'admin' ? '#f59e0b' : '#3b82f6'}`,
                                  display: 'inline-block',
                                  marginRight: '8px',
                                  marginBottom: '8px'
                                }}>
                                  {user.role === 'admin' ? '👑 Admin' : '👤 Usuario'}
                                </span>
                                <span style={{
                                  padding: '6px 12px',
                                  borderRadius: '16px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  background: 'rgba(59, 130, 246, 0.2)',
                                  color: '#3b82f6',
                                  border: '1px solid #3b82f6',
                                  display: 'inline-block',
                                  marginRight: '8px',
                                  marginBottom: '8px'
                                }}>
                                  🏢 {clients.find(c => c.id === user.client_id)?.name || 'Sin campaña'}
                                </span>
                                <span style={{
                                  padding: '6px 12px',
                                  borderRadius: '16px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  background: user.is_active ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                  color: user.is_active ? '#22c55e' : '#ef4444',
                                  border: `1px solid ${user.is_active ? '#22c55e' : '#ef4444'}`,
                                  display: 'inline-block',
                                  marginBottom: '8px'
                                }}>
                                  {user.is_active ? '✅ Activo' : '❌ Inactivo'}
                                </span>
                              </div>

                              <div className="user-actions" style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={() => {
                                    setEditingUser({ ...user });
                                    setShowUserModal(true);
                                  }}
                                  style={{
                                    background: 'var(--accent)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    flex: 1,
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                  }}
                                  onMouseOver={(e) => e.target.style.background = '#2563eb'}
                                  onMouseOut={(e) => e.target.style.background = 'var(--accent)'}
                                >
                                  <EditIcon />
                                  Editar
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(user.id)}
                                  style={{
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    flex: 1,
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                  }}
                                  onMouseOver={(e) => e.target.style.background = '#dc2626'}
                                  onMouseOut={(e) => e.target.style.background = '#ef4444'}
                                >
                                  <TrashIcon />
                                  Eliminar
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Clients Content */}
                {activeTab === 'clients' && (
                  <div className="main-section">
                    <h2>🏢 Gestión de Clientes</h2>
                    <p>Administra los clientes del sistema y configura sus dashboards de Looker Studio</p>

                    <div className="form-section">
                      <h3>➕ Crear Nuevo Cliente</h3>
                      <form onSubmit={handleCreateClient}>
                        <div className="form-row">
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Nombre del Cliente *</label>
                            <input
                              type="text"
                              value={newClient.name}
                              onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                              required
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Slug del Cliente *</label>
                            <input
                              type="text"
                              value={newClient.slug}
                              onChange={(e) => setNewClient({ ...newClient, slug: e.target.value })}
                              required
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                            />
                          </div>
                          <div style={{ flex: '1 1 100%' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Logo</label>
                            <div style={{
                              border: '2px dashed var(--card-border)',
                              borderRadius: '12px',
                              padding: '24px',
                              textAlign: 'center',
                              background: 'var(--glass-bg)',
                              backdropFilter: 'blur(10px)',
                              transition: 'all 0.3s ease',
                              cursor: 'pointer',
                              minHeight: '120px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <ImageUpload
                                value={newClient.logo_url}
                                onChange={(logo_url) => setNewClient({ ...newClient, logo_url })}
                                placeholder="Subir logo del cliente"
                              />
                              <p style={{
                                margin: '8px 0 0 0',
                                color: 'var(--muted)',
                                fontSize: '12px'
                              }}>
                                PNG, JPG, GIF hasta 5MB
                              </p>
                            </div>
                          </div>
                          <div style={{ flex: '1 1 100%' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Dashboard de Looker Studio</label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <input
                                type="url"
                                value={newClient.looker_dashboard_url}
                                onChange={(e) => setNewClient({ ...newClient, looker_dashboard_url: e.target.value })}
                                placeholder="https://lookerstudio.google.com/embed/reporting/..."
                                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '14px' }}
                              />
                              <div style={{
                                width: '30px',
                                height: '30px',
                                background: newClient.looker_dashboard_url ? '#22c55e' : '#6b7280',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                color: 'white'
                              }}>
                                📊
                              </div>
                            </div>
                            <small style={{ color: 'var(--muted)', fontSize: '12px' }}>
                              URL del dashboard de Looker Studio para este cliente (opcional)
                            </small>
                          </div>

                          <div style={{ flex: '1 1 100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <label style={{ fontSize: '14px', fontWeight: '500' }}>Agentes Virtuales</label>
                              <button
                                type="button"
                                onClick={addNewAgent}
                                style={{
                                  background: 'var(--gradient-primary)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '6px 12px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <PlusIcon />
                                Agregar Agente
                              </button>
                            </div>

                            {newClientAgents.length === 0 ? (
                              <div style={{
                                padding: '20px',
                                border: '2px dashed var(--card-border)',
                                borderRadius: '8px',
                                textAlign: 'center',
                                color: 'var(--muted)',
                                fontSize: '14px'
                              }}>
                                No hay agentes configurados. Haz clic en "Agregar Agente" para comenzar.
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {newClientAgents.map((agent, index) => (
                                  <div key={index} style={{
                                    border: '1px solid var(--card-border)',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    background: 'var(--card-bg)'
                                  }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                      <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text)' }}>
                                        Agente {index + 1}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => removeNewAgent(index)}
                                        style={{
                                          background: '#ef4444',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          padding: '4px 8px',
                                          cursor: 'pointer',
                                          fontSize: '10px'
                                        }}
                                      >
                                        <TrashIcon />
                                      </button>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                      <input
                                        type="text"
                                        value={agent.name}
                                        onChange={(e) => updateNewAgent(index, 'name', e.target.value)}
                                        placeholder="Nombre del agente (ej: Agente Ventas)"
                                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '14px' }}
                                      />
                                      <input
                                        type="text"
                                        value={agent.agent_id}
                                        onChange={(e) => updateNewAgent(index, 'agent_id', e.target.value)}
                                        placeholder="agent_xxxxxxxxxxxxxxxxxx"
                                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '14px' }}
                                      />
                                      <input
                                        type="text"
                                        value={agent.description}
                                        onChange={(e) => updateNewAgent(index, 'description', e.target.value)}
                                        placeholder="Descripción opcional"
                                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '14px' }}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            <small style={{ color: 'var(--muted)', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                              Agrega tantos agentes como necesites para este cliente
                            </small>
                          </div>
                          {/* URLs por pestaña (nuevo cliente) */}
                          <div style={{ flex: '1 1 100%' }}>
                            <label style={{ display: 'block', margin: '8px 0', fontSize: '14px', fontWeight: '600' }}>URLs específicas por pestaña (opcional)</label>
                            {['general-metrics', 'temporal-analysis', 'ddi-campaigns', 'conversion-kpis'].map((key) => (
                              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <span style={{ width: '180px', fontSize: '12px', color: 'var(--muted)' }}>
                                  {key === 'general-metrics' && 'Mètriques Generals'}
                                  {key === 'temporal-analysis' && 'Anàlisi Temporal'}
                                  {key === 'ddi-campaigns' && 'Campanyes DDI'}
                                  {key === 'conversion-kpis' && 'Conversió i KPIs'}
                                </span>
                                <input
                                  type="url"
                                  value={newPerTabUrls[key]}
                                  onChange={(e) => setNewPerTabUrls(prev => ({ ...prev, [key]: e.target.value }))}
                                  placeholder="https://lookerstudio.google.com/embed/reporting/..."
                                  style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                                />
                              </div>
                            ))}
                          </div>

                          {/* Configuración de datos multi-tenant */}
                          <div style={{ flex: '1 1 100%', marginTop: '16px', padding: '16px', background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '8px' }}>
                            <h4 style={{ marginBottom: '12px', fontSize: '15px', fontWeight: '600' }}>⚙️ Configuración de Datos</h4>
                            <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '12px' }}>Configura la fuente de datos y parámetros específicos para este cliente</p>

                            <div style={{ display: 'grid', gap: '12px' }}>
                              <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Nombre de la tabla de datos</label>
                                <input
                                  type="text"
                                  value={newClient.data_table_name}
                                  onChange={(e) => setNewClient({ ...newClient, data_table_name: e.target.value })}
                                  placeholder="datos_caser, datos_soremo, etc."
                                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                                />
                                <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Nombre de la tabla en la base de datos (ej: datos_caser)</small>
                              </div>

                              <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Colas de entrada (Inbound Queues)</label>
                                <input
                                  type="text"
                                  value={newClient.inbound_queues}
                                  onChange={(e) => setNewClient({ ...newClient, inbound_queues: e.target.value })}
                                  placeholder="queue39,queue40,queue77..."
                                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                                />
                                <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Separadas por comas (ej: queue39,queue40,queue77)</small>
                              </div>

                              <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Campañas de salida (Outbound Campaigns)</label>
                                <input
                                  type="text"
                                  value={newClient.outbound_campaigns}
                                  onChange={(e) => setNewClient({ ...newClient, outbound_campaigns: e.target.value })}
                                  placeholder="campaign31,campaign2..."
                                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                                />
                                <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Separadas por comas (ej: campaign31,campaign2)</small>
                              </div>

                              <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>GoContact Tenant ID (opcional)</label>
                                <input
                                  type="text"
                                  value={newGoContactTenantId}
                                  onChange={(e) => setNewGoContactTenantId(e.target.value)}
                                  placeholder="UUID (ej: 2cb42dff-...)"
                                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                                />
                                <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Si cada cliente tiene un tenant distinto en GoContact, colócalo aquí.</small>
                              </div>

                              <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Columna de campañas</label>
                                <input
                                  type="text"
                                  value={newClient.campaign_column}
                                  onChange={(e) => setNewClient({ ...newClient, campaign_column: e.target.value })}
                                  placeholder="campana, campaign_name, etc."
                                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                                />
                                <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Nombre de la columna para campañas (por defecto: campana)</small>
                              </div>

                              <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Columna de contrataciones</label>
                                <input
                                  type="text"
                                  value={newClient.hiring_column}
                                  onChange={(e) => setNewClient({ ...newClient, hiring_column: e.target.value })}
                                  placeholder="segmento, 39_5_subtipificacion_resolucion_principal, etc."
                                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                                />
                                <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Nombre de la columna para contrataciones (por defecto: segmento)</small>
                              </div>

                              <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Valores de contratación</label>
                                <input
                                  type="text"
                                  value={newClient.hiring_values}
                                  onChange={(e) => setNewClient({ ...newClient, hiring_values: e.target.value })}
                                  placeholder="CONTRATACION,CONTRATACION PTE CASER"
                                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                                />
                                <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Valores que indican contratación, separados por comas</small>
                              </div>
                            </div>
                          </div>

                        </div>
                        <button
                          type="submit"
                          style={{
                            background: 'var(--accent)',
                            color: 'white',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            marginTop: '16px'
                          }}
                        >
                          <PlusIcon />
                          Crear Cliente
                        </button>
                      </form>
                    </div>

                    <div className="form-section">
                      <h3>📋 Lista de Clientes ({clients.length})</h3>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', width: '100%' }}>
                        {clients.map((client, index) => (
                          <div key={client.id} className="client-card" style={{
                            background: 'var(--glass-bg)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid var(--glass-border)',
                            borderRadius: '16px',
                            padding: '24px',
                            animationDelay: `${0.1 * index}s`
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                              <div style={{
                                width: '40px',
                                height: '40px',
                                background: client.primary_color,
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px'
                              }}>
                                {client.logo_url ? (
                                  <img src={client.logo_url} alt={client.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                                ) : (
                                  '🏢'
                                )}
                              </div>
                              <div>
                                <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600' }}>{client.name}</h4>
                                <p style={{ margin: '0', fontSize: '12px', color: 'var(--muted)' }}>{client.slug}</p>
                              </div>
                            </div>

                            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                              <div style={{
                                width: '20px',
                                height: '20px',
                                background: client.primary_color,
                                borderRadius: '4px',
                                border: '1px solid var(--card-border)'
                              }}></div>
                              <div style={{
                                width: '20px',
                                height: '20px',
                                background: client.secondary_color,
                                borderRadius: '4px',
                                border: '1px solid var(--card-border)'
                              }}></div>
                            </div>

                            <div style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '12px' }}>
                              Usuarios: {users.filter(u => u.client_id === client.id).length}
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                className="action-btn"
                                onClick={() => {
                                  // Parsear enabled_sections si existe
                                  let enabledSections = { analytics: true, campaigns: true, conversations: true, reports: true, osint: true };
                                  if (client.enabled_sections) {
                                    try {
                                      enabledSections = JSON.parse(client.enabled_sections);
                                    } catch (e) {
                                      console.error('Error parsing enabled_sections:', e);
                                    }
                                  }
                                  setEditingClient({ ...client, enabled_sections: enabledSections });

                                  // Pre-cargar URLs por pestaña desde settings si existen
                                  try {
                                    const s = client.settings ? (typeof client.settings === 'string' ? JSON.parse(client.settings) : client.settings) : {};
                                    setEditingPerTabUrls({
                                      'general-metrics': s?.looker_dashboard_urls?.['general-metrics'] || '',
                                      'temporal-analysis': s?.looker_dashboard_urls?.['temporal-analysis'] || '',
                                      'ddi-campaigns': s?.looker_dashboard_urls?.['ddi-campaigns'] || '',
                                      'conversion-kpis': s?.looker_dashboard_urls?.['conversion-kpis'] || ''
                                    });
                                    setEditingGoContactTenantId(s?.gocontact_tenant_id || '');
                                  } catch (e) {
                                    setEditingPerTabUrls({ 'general-metrics': '', 'temporal-analysis': '', 'ddi-campaigns': '', 'conversion-kpis': '' });
                                    setEditingGoContactTenantId('');
                                  }

                                  // Cargar agentes del cliente
                                  loadClientAgents(client.id);

                                  setShowClientModal(true);
                                }}
                                style={{
                                  background: 'var(--gradient-primary)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '10px 18px',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  flex: 1,
                                  boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px'
                                }}
                              >
                                <EditIcon />
                                Editar
                              </button>
                              <button
                                className="action-btn"
                                onClick={() => handleDeleteClient(client.id)}
                                style={{
                                  background: 'var(--gradient-danger)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '10px 18px',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  flex: 1,
                                  boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px'
                                }}
                              >
                                <TrashIcon />
                                Eliminar
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Configurar vistas (DEBE estar dentro de admin-main) */}
                {activeTab === 'dashboardConfig' && (
                  <div className="main-section">
                    <div className="dashcfg">
                      <div className="dashcfg-header">
                        <div className="dashcfg-header-text">
                          <h2>🧩 Editor de vistas</h2>
                          <p>
                            Configura segmentos, campos, fuentes de datos y cálculos (con SQL generado y pruebas).
                          </p>
                        </div>

                        <div className="dashcfg-header-actions">
                          <span className={`dashcfg-badge ${configDirty ? 'warn' : 'ok'}`}>
                            {configDirty ? 'Cambios sin guardar' : 'Al día'}
                          </span>
                          <button
                            type="button"
                            className="dashcfg-btn secondary"
                            onClick={loadDashboardConfig}
                            disabled={configLoading}
                            title="Descarta cambios locales y recarga desde el servidor"
                          >
                            {configLoading ? 'Cargando…' : 'Revertir'}
                          </button>
                          <button
                            type="button"
                            className="dashcfg-btn primary"
                            onClick={saveDashboardConfig}
                            disabled={configSaving || configLoading || !configDirty}
                            title="Guarda la configuración para este cliente"
                          >
                            {configSaving ? 'Guardando…' : 'Guardar'}
                          </button>
                        </div>
                      </div>

                      {configError && (
                        <div className="message-enhanced error" style={{ marginBottom: '12px' }}>{configError}</div>
                      )}

                      {configLoading ? (
                        <div className="loading-enhanced">
                          <div className="spinner-enhanced"></div>
                          <p>Cargando configuración…</p>
                        </div>
                      ) : (
                        <>
                          {dashcfgSectionKeys.length === 0 ? (
                            <div className="message-enhanced info">
                              No hay secciones configurables. Verifica el catálogo o recarga.
                            </div>
                          ) : (
                            true ? (
                              <div className="dashcfg-looker">
                                <aside className="dashcfg-looker-rail">
                                  <div className="dashcfg-looker-rail-title">Configuración</div>
                                  {user?.role === 'admin' && (
                                    <button
                                      type="button"
                                      className={`dashcfg-looker-rail-step ${cfgLookerStep === 0 ? 'active' : ''}`}
                                      onClick={() => setCfgLookerStep(0)}
                                    >
                                      1. Cliente
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className={`dashcfg-looker-rail-step ${cfgLookerStep === 1 ? 'active' : ''}`}
                                    onClick={() => setCfgLookerStep(1)}
                                    disabled={user?.role === 'admin' && !selectedConfigClientId}
                                    title={(user?.role === 'admin' && !selectedConfigClientId) ? 'Selecciona un cliente primero' : ''}
                                  >
                                    {user?.role === 'admin' ? '2. Segmento' : 'Segmento'}
                                  </button>
                                  <button
                                    type="button"
                                    className={`dashcfg-looker-rail-step ${cfgLookerStep === 2 ? 'active' : ''}`}
                                    onClick={() => setCfgLookerStep(2)}
                                    disabled={!dashcfgSelectedSectionKey}
                                    title={!dashcfgSelectedSectionKey ? 'Selecciona un segmento primero' : ''}
                                  >
                                    {user?.role === 'admin' ? '3. Datos' : 'Datos'}
                                  </button>
                                  <button
                                    type="button"
                                    className={`dashcfg-looker-rail-step ${cfgLookerStep === 3 ? 'active' : ''}`}
                                    onClick={() => setCfgLookerStep(3)}
                                    disabled={!dashcfgSelectedSectionKey}
                                    title={!dashcfgSelectedSectionKey ? 'Selecciona un segmento primero' : ''}
                                  >
                                    {user?.role === 'admin' ? '4. Campos' : 'Campos'}
                                  </button>
                                  <div className="dashcfg-looker-rail-foot">
                                    <div className="dashcfg-hint">
                                      Flujo guiado: eliges el <strong>cliente</strong>, luego el <strong>segmento</strong>,
                                      defines <strong>datos</strong> y finalmente configuras <strong>campos</strong> (fuente + cálculo + SQL).
                                    </div>
                                  </div>
                                </aside>

                                <section className="dashcfg-looker-stage">
                                  <div className="dashcfg-looker-stage-head">
                                    <div className="dashcfg-looker-breadcrumb">
                                      {user?.role === 'admin' ? (
                                        <span>
                                          Cliente:{' '}
                                          <strong>
                                            {(clients || []).find((c) => String(c.id) === String(selectedConfigClientId))?.name || '—'}
                                          </strong>
                                        </span>
                                      ) : (
                                        <span>
                                          Cliente: <strong>{client?.name || '—'}</strong>
                                        </span>
                                      )}
                                      <span className="dashcfg-looker-sep">·</span>
                                      <span>
                                        Segmento: <strong>{dashcfgSelectedSectionKey ? dashcfgGetSectionLabel(dashcfgSelectedSectionKey) : '—'}</strong>
                                      </span>
                                    </div>
                                  </div>

                                  {/* Paso 0: Cliente (solo admin) */}
                                  {cfgLookerStep === 0 && user?.role === 'admin' && (
                                    <div className="dashcfg-looker-step">
                                      <div className="dashcfg-looker-step-title">Selecciona el cliente</div>
                                      <div className="dashcfg-looker-step-help">
                                        Este editor funciona por cliente (SaaS). Primero eliges a quién le estás configurando la vista.
                                      </div>

                                      <div className="dashcfg-looker-card">
                                        <div className="dashcfg-looker-card-row">
                                          <div className="dashcfg-field">
                                            <label>Cliente</label>
                                            <select
                                              value={selectedConfigClientId}
                                              onChange={(e) => setSelectedConfigClientId(e.target.value)}
                                            >
                                              {clients.map((c) => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                              ))}
                                            </select>
                                          </div>
                                        </div>

                                        <div className="dashcfg-looker-nav">
                                          <button
                                            type="button"
                                            className="dashcfg-btn primary"
                                            onClick={() => setCfgLookerStep(1)}
                                            disabled={!selectedConfigClientId}
                                          >
                                            Siguiente: Segmento
                                          </button>
                                        </div>
                                      </div>

                                      <details className="dashcfg-details">
                                        <summary>Herramientas avanzadas</summary>
                                        <div className="dashcfg-details-body">
                                          <div className="dashcfg-card">
                                            <div className="dashcfg-card-title">Copiar desde otro cliente</div>
                                            <select
                                              value={cfgCopyFromClientId}
                                              onChange={(e) => setCfgCopyFromClientId(e.target.value)}
                                            >
                                              <option value="">Selecciona cliente origen…</option>
                                              {clients
                                                .filter((c) => c.id !== selectedConfigClientId)
                                                .map((c) => (
                                                  <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                            <button
                                              type="button"
                                              className="dashcfg-btn secondary"
                                              onClick={dashcfgCopyFromClient}
                                              disabled={!cfgCopyFromClientId}
                                            >
                                              Copiar configuración
                                            </button>
                                            <small className="dashcfg-hint">
                                              Copia la configuración al editor (no guarda hasta que pulses Guardar).
                                            </small>
                                          </div>

                                          <div className="dashcfg-card">
                                            <div className="dashcfg-card-title">Plantillas</div>
                                            <select value={cfgTemplate} onChange={(e) => setCfgTemplate(e.target.value)}>
                                              <option value="default">Por defecto</option>
                                              <option value="analytics_only">Solo analíticas</option>
                                              <option value="conversations_only">Solo conversaciones</option>
                                              <option value="minimal">Minimal</option>
                                            </select>
                                            <button
                                              type="button"
                                              className="dashcfg-btn secondary"
                                              onClick={() => dashcfgApplyTemplate(cfgTemplate)}
                                            >
                                              Aplicar plantilla
                                            </button>
                                          </div>

                                          <div className="dashcfg-inline-actions">
                                            <button type="button" className="btn-chip" onClick={dashcfgResetToDefaults}>
                                              Restaurar
                                            </button>
                                            <button type="button" className="btn-chip" onClick={dashcfgCopyJson}>
                                              Copiar JSON
                                            </button>
                                          </div>
                                        </div>
                                      </details>
                                    </div>
                                  )}

                                  {/* Paso 1: Segmento */}
                                  {(cfgLookerStep === 1 || (user?.role !== 'admin' && cfgLookerStep === 0)) && (
                                    <div className="dashcfg-looker-step">
                                      <div className="dashcfg-looker-step-title">Elige el segmento (sección)</div>
                                      <div className="dashcfg-looker-step-help">
                                        Un segmento agrupa campos y reglas. Piensa en “una pestaña del dashboard”.
                                      </div>

                                      <div className="dashcfg-looker-row">
                                        <input
                                          className="dashcfg-looker-search"
                                          value={cfgLookerSectionSearch}
                                          onChange={(e) => setCfgLookerSectionSearch(e.target.value)}
                                          placeholder="Buscar segmento… (ej: analíticas, conversaciones)"
                                        />
                                      </div>

                                      <div className="dashcfg-looker-grid">
                                        {dashcfgSectionKeys
                                          .filter((sectionKey) => {
                                            const q = String(cfgLookerSectionSearch || '').trim().toLowerCase();
                                            if (!q) return true;
                                            const meta = dashcfgGetSectionMeta(sectionKey);
                                            const hay = `${sectionKey} ${dashcfgGetSectionLabel(sectionKey)} ${meta?.description || ''}`.toLowerCase();
                                            return hay.includes(q);
                                          })
                                          .map((sectionKey) => {
                                            const secCfg = dashcfgGetSectionCfg(sectionKey);
                                            const meta = dashcfgGetSectionMeta(sectionKey);
                                            const fields = dashcfgGetSectionCatalogFields(sectionKey);
                                            const clientFields = meta.clientFields || fields;
                                            const visibleCount = clientFields.filter((f) => (secCfg.fields?.[f] ?? true)).length;
                                            const missing = dashcfgGetMissingRequiredMappings(sectionKey, secCfg);
                                            return (
                                              <button
                                                key={sectionKey}
                                                type="button"
                                                className={`dashcfg-looker-tile ${dashcfgSelectedSectionKey === sectionKey ? 'active' : ''}`}
                                                onClick={() => {
                                                  setCfgSelectedSection(sectionKey);
                                                  setCfgLookerStep(2);
                                                }}
                                              >
                                                <div className="dashcfg-looker-tile-head">
                                                  <div className="dashcfg-looker-tile-title">{dashcfgGetSectionLabel(sectionKey)}</div>
                                                  <span className={`dashcfg-pill ${secCfg.enabled ? 'on' : 'off'}`}>
                                                    {secCfg.enabled ? 'Activa' : 'Off'}
                                                  </span>
                                                </div>
                                                <div className="dashcfg-looker-tile-desc">{meta?.description || ''}</div>
                                                <div className="dashcfg-looker-tile-meta">
                                                  <span className="dashcfg-pill neutral">{visibleCount}/{clientFields.length} visibles</span>
                                                  {missing.length > 0 && (
                                                    <span className="dashcfg-pill warn">{missing.length} mapeos</span>
                                                  )}
                                                </div>
                                              </button>
                                            );
                                          })}
                                      </div>

                                      <div className="dashcfg-looker-nav">
                                        {user?.role === 'admin' && (
                                          <button
                                            type="button"
                                            className="dashcfg-btn secondary"
                                            onClick={() => setCfgLookerStep(0)}
                                          >
                                            Atrás
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          className="dashcfg-btn primary"
                                          onClick={() => setCfgLookerStep(2)}
                                          disabled={!dashcfgSelectedSectionKey}
                                        >
                                          Siguiente: Datos
                                        </button>
                                      </div>
                                    </div>
                                  )}

                                  {/* Paso 2: Datos */}
                                  {cfgLookerStep === 2 && (
                                    <div className="dashcfg-looker-step">
                                      <div className="dashcfg-looker-step-title">Define de dónde se sacan los datos</div>
                                      <div className="dashcfg-looker-step-help">
                                        Selecciona el origen (base) de esta sección y una tabla principal. Luego, en el siguiente paso, configuras campo por campo.
                                      </div>

                                      {dashcfgSelectedMissingRequired.length > 0 && (
                                        <div className="dashcfg-alert warn">
                                          <strong>Faltan mapeos requeridos:</strong>{' '}
                                          {dashcfgSelectedMissingRequired.map((f) => dashcfgGetFieldLabel(dashcfgSelectedSectionKey, f)).join(', ')}
                                          <div style={{ marginTop: '8px', fontSize: '12px' }}>
                                            Mapea estos campos abajo usando las columnas de la tabla seleccionada.
                                          </div>
                                        </div>
                                      )}

                                      <div className="dashcfg-looker-card">
                                        <div className="dashcfg-panel-grid">
                                          <div className="dashcfg-field">
                                            <label>Sección visible</label>
                                            <label className="dashcfg-switch">
                                              <input
                                                type="checkbox"
                                                checked={Boolean(dashcfgSelectedSectionCfg.enabled)}
                                                onChange={(e) => updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({ ...sec, enabled: e.target.checked }))}
                                              />
                                              <span>{dashcfgSelectedSectionCfg.enabled ? 'Visible' : 'Oculta'}</span>
                                            </label>
                                          </div>

                                          <div className="dashcfg-field">
                                            <label>Origen de datos</label>
                                            <select
                                              value={dashcfgSelectedSectionCfg.dataSource || 'main'}
                                              onChange={(e) => updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({ ...sec, dataSource: e.target.value }))}
                                            >
                                              {(availableDataSources && availableDataSources.length > 0
                                                ? availableDataSources
                                                : [{ key: 'main', label: 'Principal (ambtu_platform)' }]
                                              ).map((ds) => (
                                                <option key={ds.key} value={ds.key}>
                                                  {ds.label || ds.key}
                                                </option>
                                              ))}
                                            </select>
                                            <small className="dashcfg-hint">
                                              {schemaLoading && schemaDataSource === (dashcfgSelectedSectionCfg.dataSource || 'main')
                                                ? 'Cargando esquema…'
                                                : 'Selecciona la base/datasource para esta sección.'}
                                            </small>
                                          </div>

                                          <div className="dashcfg-field">
                                            <label>Tabla principal</label>
                                            <select
                                              value={dashcfgSelectedBaseTable || ''}
                                              onChange={(e) => dashcfgSetSectionBaseTable(dashcfgSelectedSectionKey, e.target.value)}
                                              disabled={schemaLoading}
                                            >
                                              <option value="">Selecciona tabla…</option>
                                              {Object.keys(schemaTables || {}).map((tbl) => (
                                                <option key={tbl} value={tbl}>{tbl}</option>
                                              ))}
                                            </select>
                                            <small className="dashcfg-hint">
                                              {schemaLoading
                                                ? 'Cargando tablas disponibles…'
                                                : Object.keys(schemaTables || {}).length === 0
                                                  ? 'No hay tablas disponibles. Verifica la conexión a la base de datos.'
                                                  : 'Recomendado: 1 tabla principal por sección (mejor rendimiento y menos errores).'}
                                            </small>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Campo de FECHA - Siempre visible y crítico */}
                                      {dashcfgSelectedBaseTable && dashcfgIsRequiredMapping(dashcfgSelectedSectionKey, 'start_time') && (
                                        <div className="dashcfg-looker-card" style={{ border: '2px solid #f59e0b', background: 'rgba(245, 158, 11, 0.15)' }}>
                                          <div className="dashcfg-card-title">
                                            📅 Campo de Fecha/Hora (CRÍTICO para filtros)
                                          </div>
                                          <div className="dashcfg-hint" style={{ marginBottom: '12px', color: '#fbbf24' }}>
                                            <strong>⚠️ IMPORTANTE:</strong> Este campo es necesario para que funcionen los filtros de fecha en el panel de analytics.
                                            Debe ser una columna de tipo DATETIME o TIMESTAMP en la tabla <strong>{dashcfgSelectedBaseTable}</strong>.
                                          </div>
                                          <div className="dashcfg-field-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {(() => {
                                              const fieldKey = 'start_time';
                                              const label = dashcfgGetFieldLabel(dashcfgSelectedSectionKey, fieldKey);
                                              const help = dashcfgGetFieldHelp(dashcfgSelectedSectionKey, fieldKey);
                                              const m = dashcfgSelectedSectionCfg.fieldMappings?.[fieldKey];
                                              const isMapped = dashcfgMappingComplete(m);
                                              const isMappedToCurrentTable = isMapped && m?.table === dashcfgSelectedBaseTable;
                                              return (
                                                <div className="dashcfg-field-row" style={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: '12px',
                                                  padding: '12px',
                                                  border: isMappedToCurrentTable ? '2px solid #10b981' : '2px solid #f59e0b',
                                                  borderRadius: '10px',
                                                  background: isMappedToCurrentTable ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)'
                                                }}>
                                                  <div style={{ flex: '1', minWidth: 0 }}>
                                                    <div className="dashcfg-field-name" style={{ fontWeight: 700, fontSize: '14px' }}>
                                                      {label}
                                                      {isMappedToCurrentTable ? (
                                                        <span className="dashcfg-pill ok" style={{ marginLeft: '8px', fontSize: '11px' }}>✓ Configurado</span>
                                                      ) : (
                                                        <span className="dashcfg-pill warn" style={{ marginLeft: '8px', fontSize: '11px' }}>⚠️ Requerido</span>
                                                      )}
                                                      {isMapped && !isMappedToCurrentTable && (
                                                        <span className="dashcfg-pill" style={{ marginLeft: '8px', fontSize: '10px', background: '#6366f1', color: '#fff' }}>
                                                          Mapeado a otra tabla: {m.table}
                                                        </span>
                                                      )}
                                                    </div>
                                                    {help && <div className="dashcfg-field-help" style={{ fontSize: '12px', marginTop: '4px', color: '#fbbf24' }}>{help}</div>}
                                                    {isMapped && !isMappedToCurrentTable && (
                                                      <div className="dashcfg-field-help" style={{ fontSize: '11px', marginTop: '4px', color: '#f59e0b' }}>
                                                        ⚠️ Actualmente mapeado a <strong>{m.table}.{m.column}</strong>, pero la tabla principal es <strong>{dashcfgSelectedBaseTable}</strong>.
                                                        Cambia el mapeo para usar la columna de fecha de la tabla principal.
                                                      </div>
                                                    )}
                                                  </div>
                                                  <div className="dashcfg-map" style={{ minWidth: '250px' }}>
                                                    <select
                                                      value={isMappedToCurrentTable ? (m?.column || '') : ''}
                                                      onChange={(e) => updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
                                                        ...sec,
                                                        fieldMappings: {
                                                          ...(sec.fieldMappings || {}),
                                                          [fieldKey]: {
                                                            table: dashcfgSelectedBaseTable,
                                                            column: e.target.value || null,
                                                          }
                                                        }
                                                      }))}
                                                      disabled={schemaLoading}
                                                      style={{
                                                        width: '100%',
                                                        padding: '8px 12px',
                                                        borderRadius: '8px',
                                                        border: '2px solid rgba(245, 158, 11, 0.5)',
                                                        background: 'rgba(13, 20, 40, 0.5)',
                                                        color: 'var(--text)',
                                                        fontSize: '13px',
                                                        fontWeight: isMappedToCurrentTable ? 'normal' : 'bold'
                                                      }}
                                                    >
                                                      <option value="">{isMappedToCurrentTable ? 'Cambiar columna…' : '⚠️ Selecciona columna de FECHA…'}</option>
                                                      {Object.keys(schemaTables || {}).includes(dashcfgSelectedBaseTable) && (schemaTables[dashcfgSelectedBaseTable] || []).map((col) => (
                                                        <option key={col} value={col}>{col}</option>
                                                      ))}
                                                    </select>
                                                    {isMappedToCurrentTable && m?.column && (
                                                      <div style={{ fontSize: '11px', marginTop: '4px', color: '#10b981' }}>
                                                        ✓ Mapeado a: <strong>{dashcfgSelectedBaseTable}.{m.column}</strong>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            })()}
                                          </div>
                                        </div>
                                      )}

                                      {/* Mapeo rápido de campos requeridos */}
                                      {dashcfgSelectedBaseTable && dashcfgSelectedMissingRequired.length > 0 && (
                                        <div className="dashcfg-looker-card" style={dashcfgSelectedMissingRequired.includes('start_time') ? { border: '2px solid #f59e0b', background: 'rgba(245, 158, 11, 0.1)' } : {}}>
                                          <div className="dashcfg-card-title">
                                            ⚠️ Mapeo rápido de campos requeridos
                                            {dashcfgSelectedMissingRequired.includes('start_time') && (
                                              <span style={{ marginLeft: '8px', fontSize: '12px', color: '#f59e0b', fontWeight: 'bold' }}>
                                                (Incluye campo de FECHA - CRÍTICO)
                                              </span>
                                            )}
                                          </div>
                                          <div className="dashcfg-hint" style={{ marginBottom: '12px' }}>
                                            Mapea los campos requeridos a las columnas de la tabla <strong>{dashcfgSelectedBaseTable}</strong>.
                                            {dashcfgSelectedMissingRequired.includes('start_time') && (
                                              <span style={{ color: '#f59e0b', fontWeight: 'bold', display: 'block', marginTop: '8px' }}>
                                                ⚠️ El campo <strong>📅 Fecha/Hora de inicio</strong> es especialmente importante para que funcionen los filtros de fecha en el panel de analytics.
                                              </span>
                                            )}
                                            {' '}Estos campos son necesarios para que las métricas funcionen correctamente.
                                          </div>
                                          <div className="dashcfg-field-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {dashcfgSelectedMissingRequired.map((fieldKey) => {
                                              const label = dashcfgGetFieldLabel(dashcfgSelectedSectionKey, fieldKey);
                                              const help = dashcfgGetFieldHelp(dashcfgSelectedSectionKey, fieldKey);
                                              const m = dashcfgSelectedSectionCfg.fieldMappings?.[fieldKey];
                                              return (
                                                <div key={fieldKey} className="dashcfg-field-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', border: '1px solid rgba(96, 120, 170, 0.25)', borderRadius: '10px', background: 'rgba(13, 20, 40, 0.22)' }}>
                                                  <div style={{ flex: '1', minWidth: 0 }}>
                                                    <div className="dashcfg-field-name" style={{ fontWeight: 700, fontSize: '13px' }}>
                                                      {label}
                                                      <span className="dashcfg-pill warn" style={{ marginLeft: '8px', fontSize: '10px' }}>Requerido</span>
                                                    </div>
                                                    {help && <div className="dashcfg-field-help" style={{ fontSize: '11px', marginTop: '2px' }}>{help}</div>}
                                                  </div>
                                                  <div className="dashcfg-map" style={{ minWidth: '200px' }}>
                                                    <select
                                                      value={m?.column || ''}
                                                      onChange={(e) => updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
                                                        ...sec,
                                                        fieldMappings: {
                                                          ...(sec.fieldMappings || {}),
                                                          [fieldKey]: {
                                                            ...(sec.fieldMappings?.[fieldKey] || {}),
                                                            table: dashcfgSelectedBaseTable || sec.fieldMappings?.[fieldKey]?.table || null,
                                                            column: e.target.value || null,
                                                          }
                                                        }
                                                      }))}
                                                      disabled={schemaLoading}
                                                      style={{ width: '100%', padding: '6px 10px', borderRadius: '8px', border: '1px solid rgba(96, 120, 170, 0.35)', background: 'rgba(13, 20, 40, 0.35)', color: 'var(--text)', fontSize: '12px' }}
                                                    >
                                                      <option value="">Selecciona columna…</option>
                                                      {Object.keys(schemaTables || {}).includes(dashcfgSelectedBaseTable) && (schemaTables[dashcfgSelectedBaseTable] || []).map((col) => (
                                                        <option key={col} value={col}>{col}</option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}


                                      {/* Configuración de Jerarquía JSON para Desglose de Subestados - Solo para analytics_ai */}
                                      {dashcfgSelectedSectionKey === 'analytics_ai' && (
                                        <div className="dashcfg-looker-card" style={{ marginTop: '20px' }}>
                                          <div className="dashcfg-card-title">
                                            📊 Configuración de Desglose de Subestados
                                          </div>
                                          <div className="dashcfg-hint" style={{ marginBottom: '12px' }}>
                                            <strong>¿Qué es esto?</strong> Configura la jerarquía de estados y subestados de llamadas que se mostrará en el panel "Desglose de Subestados" del dashboard del cliente.
                                            <br />
                                            <strong>⚠️ IMPORTANTE:</strong> Esta configuración es específica por cliente. Cada cliente puede tener su propia estructura de subestados completamente diferente.
                                            Define niveles padre-hijo personalizados según las necesidades de cada cliente.
                                          </div>
                                          {/* Toggle para cambiar entre modos */}
                                          <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'rgba(13, 20, 40, 0.3)', borderRadius: '8px' }}>
                                            <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)' }}>
                                              Modo de edición:
                                            </label>
                                            <button
                                              onClick={() => setCfgSubestadosVisualMode(true)}
                                              style={{
                                                padding: '8px 16px',
                                                background: cfgSubestadosVisualMode ? '#3b82f6' : 'rgba(59, 130, 246, 0.2)',
                                                color: cfgSubestadosVisualMode ? 'white' : '#3b82f6',
                                                border: `1px solid #3b82f6`,
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                fontWeight: '500',
                                                transition: 'all 0.2s ease'
                                              }}
                                            >
                                              🎨 Visual (Recomendado)
                                            </button>
                                            <button
                                              onClick={() => setCfgSubestadosVisualMode(false)}
                                              style={{
                                                padding: '8px 16px',
                                                background: !cfgSubestadosVisualMode ? '#3b82f6' : 'rgba(59, 130, 246, 0.2)',
                                                color: !cfgSubestadosVisualMode ? 'white' : '#3b82f6',
                                                border: `1px solid #3b82f6`,
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                fontWeight: '500',
                                                transition: 'all 0.2s ease'
                                              }}
                                            >
                                              💻 JSON Avanzado
                                            </button>
                                          </div>

                                          {/* Editor Visual de Jerarquía */}
                                          {cfgSubestadosVisualMode ? (
                                            <SubestadosHierarchyEditor
                                              hierarchy={getSubestadosHierarchy()}
                                              availableColumns={dashcfgSelectedBaseTable && schemaTables[dashcfgSelectedBaseTable] ? schemaTables[dashcfgSelectedBaseTable] : []}
                                              baseTable={dashcfgSelectedBaseTable || ''}
                                              onUpdate={(updatedHierarchy) => {
                                                updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
                                                  ...sec,
                                                  subestadosConfig: {
                                                    ...(sec.subestadosConfig || {}),
                                                    hierarchy: updatedHierarchy
                                                  }
                                                }));
                                                setCfgSubestadosJsonError('');
                                              }}
                                              onError={(error) => {
                                                setCfgSubestadosJsonError(error);
                                              }}
                                            />
                                          ) : (
                                            <div style={{ marginBottom: '16px' }}>
                                              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                                                Configuración JSON de Jerarquía (Modo Avanzado)
                                              </label>
                                              <textarea
                                                value={cfgSubestadosJsonText}
                                                onChange={(e) => {
                                                  const newText = e.target.value;
                                                  setCfgSubestadosJsonText(newText);

                                                  // Marcar que el usuario está editando para evitar que el useEffect sobrescriba
                                                  subestadosJsonSyncingRef.current = true;

                                                  // Intentar parsear el JSON
                                                  try {
                                                    const parsed = JSON.parse(newText);
                                                    if (Array.isArray(parsed)) {
                                                      // JSON válido: actualizar el estado global
                                                      updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
                                                        ...sec,
                                                        subestadosConfig: {
                                                          ...(sec.subestadosConfig || {}),
                                                          hierarchy: parsed
                                                        }
                                                      }));
                                                      setCfgSubestadosJsonError('');
                                                      subestadosJsonLastSyncedRef.current = newText;
                                                    } else {
                                                      setCfgSubestadosJsonError('El JSON debe ser un array');
                                                    }
                                                  } catch (err) {
                                                    // JSON inválido temporalmente: solo mostrar error, no actualizar estado
                                                    setCfgSubestadosJsonError(`Error de sintaxis: ${err.message}`);
                                                  }

                                                  // Resetear el flag después de un delay para permitir sincronización futura
                                                  setTimeout(() => {
                                                    subestadosJsonSyncingRef.current = false;
                                                  }, 500);
                                                }}
                                                onBlur={(e) => {
                                                  // Al perder el foco, intentar formatear el JSON si es válido
                                                  try {
                                                    const parsed = JSON.parse(e.target.value);
                                                    if (Array.isArray(parsed)) {
                                                      const formatted = JSON.stringify(parsed, null, 2);
                                                      setCfgSubestadosJsonText(formatted);
                                                      setCfgSubestadosJsonError('');
                                                    }
                                                  } catch (err) {
                                                    // Mantener el texto tal cual si hay error
                                                  }
                                                }}
                                                style={{
                                                  width: '100%',
                                                  minHeight: '300px',
                                                  padding: '12px',
                                                  borderRadius: '8px',
                                                  border: cfgSubestadosJsonError ? '2px solid #ef4444' : '1px solid rgba(96, 120, 170, 0.35)',
                                                  background: 'rgba(13, 20, 40, 0.5)',
                                                  color: 'var(--text)',
                                                  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                                                  fontSize: '13px',
                                                  lineHeight: '1.5'
                                                }}
                                              />
                                              {cfgSubestadosJsonError && (
                                                <div style={{
                                                  marginTop: '8px',
                                                  padding: '8px 12px',
                                                  background: 'rgba(239, 68, 68, 0.1)',
                                                  border: '1px solid rgba(239, 68, 68, 0.3)',
                                                  borderRadius: '6px',
                                                  color: '#ef4444',
                                                  fontSize: '12px'
                                                }}>
                                                  ⚠️ {cfgSubestadosJsonError}
                                                </div>
                                              )}
                                              {!cfgSubestadosJsonError && cfgSubestadosJsonText && (
                                                <div style={{
                                                  marginTop: '8px',
                                                  padding: '8px 12px',
                                                  background: 'rgba(34, 197, 94, 0.1)',
                                                  border: '1px solid rgba(34, 197, 94, 0.3)',
                                                  borderRadius: '6px',
                                                  color: '#22c55e',
                                                  fontSize: '12px'
                                                }}>
                                                  ✓ JSON válido
                                                </div>
                                              )}
                                              <div className="dashcfg-hint" style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                                                💡 <strong>Consejo:</strong> Puedes editar el JSON libremente. El sistema validará automáticamente cuando el JSON sea válido.
                                              </div>
                                            </div>
                                          )}
                                          <div className="dashcfg-hint" style={{ fontSize: '13px' }}>
                                            <strong>📖 Guía de Configuración:</strong>
                                            <br />
                                            <strong>1. Estructura de 3 niveles (ejemplo común):</strong>
                                            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                              <li><strong>Nivel 1:</strong> Contactabilidad (SI/NO) - Raíz de la jerarquía</li>
                                              <li><strong>Nivel 2:</strong> Subestados - Dependen de la contactabilidad</li>
                                              <li><strong>Nivel 3:</strong> Subcategorías (ej: acepta_propuesta) - Solo para algunos subestados</li>
                                            </ul>
                                            <strong>2. Campos requeridos por nivel:</strong>
                                            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                              <li><strong>level:</strong> Nivel en la jerarquía (1 = raíz, 2 = hijo de nivel 1, 3 = hijo de nivel 2)</li>
                                              <li><strong>key:</strong> Identificador único del nivel (sin espacios, usar guiones bajos, ej: "contactabilidad_si")</li>
                                              <li><strong>label:</strong> Etiqueta que se mostrará al cliente (ej: "Contactabilidad: SÍ")</li>
                                              <li><strong>parentKey:</strong> Key del nivel padre (opcional para nivel 1, requerido para niveles 2 y 3)</li>
                                              <li><strong>column:</strong> Nombre de la columna en la tabla (debe estar mapeada en "Campos de Datos Base" arriba)</li>
                                              <li><strong>value:</strong> Valor a buscar en la columna (ej: "SI", "NO", "Valor específico")</li>
                                              <li><strong>operator:</strong> Tipo de comparación ("equals", "contains", "in")</li>
                                            </ul>
                                            <strong>3. Ejemplo completo para tu estructura:</strong>
                                            <pre style={{ margin: '8px 0', padding: '8px', background: 'rgba(13, 20, 40, 0.5)', borderRadius: '4px', fontSize: '11px', overflow: 'auto' }}>{`// NIVEL 1: Contactabilidad
{
  "level": 1,
  "key": "contactabilidad_si",
  "label": "Contactabilidad: SÍ",
  "column": "contactabilidad",
  "value": "SI",
  "operator": "equals"
},
{
  "level": 1,
  "key": "contactabilidad_no",
  "label": "Contactabilidad: NO",
  "column": "contactabilidad",
  "value": "NO",
  "operator": "equals"
},

// NIVEL 2: Subestados (dependen de contactabilidad)
{
  "level": 2,
  "key": "subestado_interesado",
  "label": "Interesado",
  "parentKey": "contactabilidad_si",  // ← Hijo de "contactabilidad_si"
  "column": "subestado",
  "value": "Interesado",
  "operator": "equals"
},

// NIVEL 3: Subcategorías (solo para algunos subestados)
{
  "level": 3,
  "key": "acepta_propuesta_si",
  "label": "Acepta Propuesta: SÍ",
  "parentKey": "subestado_interesado",  // ← Hijo de un subestado
  "column": "acepta_propuesta",
  "value": "SI",
  "operator": "equals"
}`}</pre>
                                            <strong>4. Operadores disponibles:</strong>
                                            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                              <li><code>"equals"</code>: Valor exacto (ej: <code>"value": "SI"</code>) - Recomendado para valores fijos</li>
                                              <li><code>"contains"</code>: Contiene el texto (ej: <code>"value": "Interesado"</code>) - Útil para búsquedas parciales</li>
                                              <li><code>"in"</code>: Lista de valores (ej: <code>"value": ["SI", "SÍ", "Si"]</code>) - Para múltiples valores posibles</li>
                                            </ul>
                                            <strong>5. ⚠️ IMPORTANTE - Pasos previos:</strong>
                                            <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                              <li>Primero mapea las columnas en "Campos de Datos Base" arriba: <code>contactabilidad</code>, <code>subestado</code>, <code>acepta_propuesta</code></li>
                                              <li>Luego configura la jerarquía aquí usando los nombres exactos de las columnas mapeadas</li>
                                              <li>Los valores en <code>value</code> deben coincidir exactamente con los valores en tu base de datos</li>
                                            </ol>
                                            <strong>6. 💡 Nota sobre subcategorías opcionales:</strong>
                                            <br />
                                            No todos los subestados necesitan tener nivel 3. Solo agrega niveles 3 para los subestados que tienen subcategorías. Los subestados sin subcategorías simplemente no tendrán hijos.
                                          </div>
                                        </div>
                                      )}

                                      {/* Gestión de Archivos de Informes - Solo para informes */}
                                      {dashcfgSelectedSectionKey === 'informes' && (
                                        <div className="dashcfg-looker-card" style={{ marginTop: '20px' }}>
                                          <div className="dashcfg-card-title">
                                            📁 Gestión de Archivos de Informes
                                          </div>
                                          <div className="dashcfg-hint" style={{ marginBottom: '12px' }}>
                                            <strong>¿Qué es esto?</strong> Sube y gestiona archivos de informes (PDF, Word, Excel, TXT) que los clientes podrán visualizar y descargar.
                                            <br />
                                            <strong>⚠️ IMPORTANTE:</strong> Solo los administradores pueden subir archivos. Los archivos se almacenan por cliente.
                                          </div>

                                          <InformesFileManager selectedClientId={selectedConfigClientId} />
                                        </div>
                                      )}

                                      {/* Configuración de Distribución Proporcional - Solo para analytics_ai */}
                                      {dashcfgSelectedSectionKey === 'analytics_ai' && (
                                        <div className="dashcfg-looker-card" style={{ marginTop: '20px' }}>
                                          <div className="dashcfg-card-title">
                                            📊 Configuración de Distribución Proporcional
                                          </div>
                                          <div className="dashcfg-hint" style={{ marginBottom: '12px' }}>
                                            <strong>¿Qué es esto?</strong> Configura qué campos del paso 4 (Campos) se mostrarán en la gráfica de "Distribución Proporcional" del panel de subestados.
                                            <br />
                                            <strong>⚠️ IMPORTANTE:</strong> Solo se mostrarán los campos que selecciones aquí. Los campos deben estar configurados en el paso 4 (Campos) para aparecer aquí.
                                          </div>

                                          {/* Obtener campos configurados del paso 4 */}
                                          {(() => {
                                            // Obtener todos los campos que tienen cálculo configurado (spec o formula)
                                            const fieldCalculations = dashcfgSelectedSectionCfg.fieldCalculations || {};
                                            const fieldCalculationSpecs = dashcfgSelectedSectionCfg.fieldCalculationSpecs || {};
                                            const fieldLabels = dashcfgSelectedSectionCfg.fieldLabels || {};
                                            const fieldFormats = dashcfgSelectedSectionCfg.fieldFormats || {};

                                            // Obtener campos que tienen configuración (spec o formula)
                                            const configuredFields = [];
                                            const allFieldKeys = new Set([
                                              ...Object.keys(fieldCalculations || {}),
                                              ...Object.keys(fieldCalculationSpecs || {}),
                                              ...(dashcfgSelectedMeta?.clientFields || [])
                                            ]);

                                            allFieldKeys.forEach(fieldKey => {
                                              const hasSpec = fieldCalculationSpecs[fieldKey] && Array.isArray(fieldCalculationSpecs[fieldKey]) && fieldCalculationSpecs[fieldKey].length > 0;
                                              const hasFormula = fieldCalculations[fieldKey] && typeof fieldCalculations[fieldKey] === 'string' && fieldCalculations[fieldKey].trim();

                                              // Solo incluir campos que tienen spec o formula configurada
                                              if (hasSpec || hasFormula) {
                                                const label = fieldLabels[fieldKey] || dashcfgGetFieldLabel(dashcfgSelectedSectionKey, fieldKey) || fieldKey;
                                                const format = fieldFormats[fieldKey];

                                                configuredFields.push({
                                                  key: fieldKey,
                                                  label: label,
                                                  hasSpec: hasSpec,
                                                  hasFormula: hasFormula,
                                                  format: format
                                                });
                                              }
                                            });

                                            // Ordenar por label
                                            configuredFields.sort((a, b) => a.label.localeCompare(b.label));

                                            const currentConfig = dashcfgSelectedSectionCfg.subestadosConfig?.distributionProportionalKeys;
                                            // Manejar null/undefined como array vacío
                                            const currentConfigArray = Array.isArray(currentConfig) ? currentConfig : [];
                                            const currentConfigSet = new Set(currentConfigArray.map(k => String(k).toLowerCase().trim()));

                                            return (
                                              <div style={{
                                                padding: '16px',
                                                background: 'rgba(13, 20, 40, 0.35)',
                                                border: '1px solid rgba(96, 120, 170, 0.28)',
                                                borderRadius: '14px'
                                              }}>
                                                {configuredFields.length === 0 ? (
                                                  <div style={{
                                                    padding: '20px',
                                                    textAlign: 'center',
                                                    color: 'var(--muted)',
                                                    background: 'rgba(245, 158, 11, 0.12)',
                                                    borderRadius: '12px',
                                                    border: '1px dashed rgba(245, 158, 11, 0.4)'
                                                  }}>
                                                    <strong style={{ color: 'var(--text)' }}>No hay campos configurados en el paso 4 (Campos).</strong>
                                                    <br />
                                                    <small style={{ fontSize: '12px', color: 'var(--muted)' }}>
                                                      Ve al paso 4 (Campos) y configura al menos un campo con cálculo (spec o fórmula) para que aparezca aquí.
                                                    </small>
                                                  </div>
                                                ) : (
                                                  <>
                                                    <div style={{ marginBottom: '16px' }}>
                                                      <label style={{
                                                        display: 'block',
                                                        marginBottom: '12px',
                                                        fontSize: '14px',
                                                        fontWeight: '700',
                                                        color: 'var(--text)'
                                                      }}>
                                                        Selecciona los campos a mostrar en la gráfica de distribución proporcional:
                                                      </label>
                                                      <div style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '8px',
                                                        maxHeight: '400px',
                                                        overflowY: 'auto',
                                                        padding: '8px',
                                                        background: 'rgba(13, 20, 40, 0.45)',
                                                        borderRadius: '12px',
                                                        border: '1px solid rgba(96, 120, 170, 0.25)'
                                                      }}>
                                                        {configuredFields.map((field, idx) => {
                                                          const fieldKeyLower = String(field.key || '').toLowerCase().trim();
                                                          // Determinar si está marcado:
                                                          // - Si currentConfigArray está vacío, todos están marcados por defecto
                                                          // - Si currentConfigArray tiene elementos, verificar si este campo está en la lista
                                                          const isChecked = currentConfigArray.length === 0
                                                            ? true  // Por defecto todos están marcados cuando no hay configuración
                                                            : currentConfigSet.has(fieldKeyLower);

                                                          return (
                                                            <label key={idx} style={{
                                                              display: 'flex',
                                                              alignItems: 'center',
                                                              gap: '10px',
                                                              padding: '10px',
                                                              borderRadius: '10px',
                                                              cursor: 'pointer',
                                                              transition: 'all 0.2s ease',
                                                              background: isChecked ? 'rgba(245, 131, 25, 0.15)' : 'rgba(13, 20, 40, 0.3)',
                                                              border: isChecked ? '1px solid rgba(245, 131, 25, 0.4)' : '1px solid rgba(96, 120, 170, 0.2)'
                                                            }}
                                                              onMouseEnter={(e) => {
                                                                e.currentTarget.style.background = isChecked ? 'rgba(245, 131, 25, 0.2)' : 'rgba(20, 30, 60, 0.5)';
                                                                e.currentTarget.style.borderColor = isChecked ? 'rgba(245, 131, 25, 0.5)' : 'rgba(96, 120, 170, 0.35)';
                                                              }}
                                                              onMouseLeave={(e) => {
                                                                e.currentTarget.style.background = isChecked ? 'rgba(245, 131, 25, 0.15)' : 'rgba(13, 20, 40, 0.3)';
                                                                e.currentTarget.style.borderColor = isChecked ? 'rgba(245, 131, 25, 0.4)' : 'rgba(96, 120, 170, 0.2)';
                                                              }}
                                                            >
                                                              <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={(e) => {
                                                                  const checked = e.target.checked;
                                                                  let newKeys = [];

                                                                  // Si es la primera vez que se configura (currentConfigArray está vacío)
                                                                  if (currentConfigArray.length === 0) {
                                                                    if (checked) {
                                                                      // Si se marca y no hay configuración, inicializar con todos los campos
                                                                      newKeys = configuredFields.map(f => f.key);
                                                                    } else {
                                                                      // Si se desmarca y no hay configuración, inicializar con todos excepto el desmarcado
                                                                      newKeys = configuredFields
                                                                        .filter(f => f.key !== field.key)
                                                                        .map(f => f.key);
                                                                    }
                                                                  } else {
                                                                    // Comportamiento normal: agregar o remover de la lista existente
                                                                    newKeys = [...currentConfigArray];
                                                                    if (checked) {
                                                                      // Agregar si no está
                                                                      if (!currentConfigSet.has(fieldKeyLower)) {
                                                                        newKeys.push(field.key);
                                                                      }
                                                                    } else {
                                                                      // Remover si está
                                                                      const index = newKeys.findIndex(k => String(k).toLowerCase().trim() === fieldKeyLower);
                                                                      if (index >= 0) {
                                                                        newKeys.splice(index, 1);
                                                                      }
                                                                    }
                                                                  }

                                                                  // Actualizar la configuración
                                                                  updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
                                                                    ...sec,
                                                                    subestadosConfig: {
                                                                      ...(sec.subestadosConfig || {}),
                                                                      distributionProportionalKeys: newKeys
                                                                    }
                                                                  }));
                                                                }}
                                                                style={{
                                                                  width: '18px',
                                                                  height: '18px',
                                                                  cursor: 'pointer',
                                                                  accentColor: '#f58319',
                                                                  pointerEvents: 'auto',
                                                                  flexShrink: 0
                                                                }}
                                                              />
                                                              <div style={{ flex: 1 }}>
                                                                <div style={{
                                                                  fontWeight: '700',
                                                                  fontSize: '14px',
                                                                  color: 'var(--text)'
                                                                }}>
                                                                  {field.label}
                                                                </div>
                                                                <div style={{
                                                                  fontSize: '12px',
                                                                  color: 'var(--muted)',
                                                                  marginTop: '2px',
                                                                  display: 'flex',
                                                                  gap: '8px',
                                                                  alignItems: 'center'
                                                                }}>
                                                                  <span>
                                                                    Key: <code style={{
                                                                      background: 'rgba(96, 120, 170, 0.25)',
                                                                      padding: '2px 6px',
                                                                      borderRadius: '4px',
                                                                      fontSize: '11px',
                                                                      color: 'var(--text)',
                                                                      border: '1px solid rgba(96, 120, 170, 0.2)'
                                                                    }}>{field.key}</code>
                                                                  </span>
                                                                  {field.format && (
                                                                    <span style={{
                                                                      background: 'rgba(59, 130, 246, 0.2)',
                                                                      padding: '2px 6px',
                                                                      borderRadius: '4px',
                                                                      fontSize: '11px',
                                                                      color: '#60a5fa',
                                                                      border: '1px solid rgba(59, 130, 246, 0.3)'
                                                                    }}>
                                                                      {field.format}
                                                                    </span>
                                                                  )}
                                                                  {field.hasSpec && (
                                                                    <span style={{
                                                                      background: 'rgba(34, 197, 94, 0.2)',
                                                                      padding: '2px 6px',
                                                                      borderRadius: '4px',
                                                                      fontSize: '11px',
                                                                      color: '#4ade80',
                                                                      border: '1px solid rgba(34, 197, 94, 0.3)'
                                                                    }}>
                                                                      Spec
                                                                    </span>
                                                                  )}
                                                                  {field.hasFormula && (
                                                                    <span style={{
                                                                      background: 'rgba(168, 85, 247, 0.2)',
                                                                      padding: '2px 6px',
                                                                      borderRadius: '4px',
                                                                      fontSize: '11px',
                                                                      color: '#a78bfa',
                                                                      border: '1px solid rgba(168, 85, 247, 0.3)'
                                                                    }}>
                                                                      Fórmula
                                                                    </span>
                                                                  )}
                                                                </div>
                                                              </div>
                                                            </label>
                                                          );
                                                        })}
                                                      </div>
                                                    </div>

                                                    <div style={{
                                                      padding: '12px',
                                                      background: 'rgba(245, 131, 25, 0.12)',
                                                      borderRadius: '10px',
                                                      fontSize: '13px',
                                                      color: 'var(--text)',
                                                      border: '1px solid rgba(245, 131, 25, 0.25)'
                                                    }}>
                                                      <strong>💡 Consejo:</strong> Estos son los campos configurados en el paso 4 (Campos).
                                                      {currentConfigArray.length === 0 ? (
                                                        <> Todos están seleccionados por defecto. Desmarca los que no quieras mostrar.</>
                                                      ) : (
                                                        <> Selecciona los que quieras mostrar en la gráfica.</>
                                                      )}
                                                      <br />
                                                      <strong>Campos seleccionados:</strong> {currentConfigArray.length === 0
                                                        ? `Todos (${configuredFields.length} campos)`
                                                        : `${currentConfigArray.length} de ${configuredFields.length}`}
                                                    </div>
                                                  </>
                                                )}
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      )}

                                      <div className="dashcfg-looker-nav">
                                        <button
                                          type="button"
                                          className="dashcfg-btn secondary"
                                          onClick={() => setCfgLookerStep(1)}
                                        >
                                          Atrás
                                        </button>
                                        <button
                                          type="button"
                                          className="dashcfg-btn primary"
                                          onClick={() => setCfgLookerStep(3)}
                                          disabled={!dashcfgSelectedSectionKey}
                                        >
                                          Siguiente: Campos
                                        </button>
                                      </div>
                                    </div>
                                  )}

                                  {/* Paso 3: Campos */}
                                  {cfgLookerStep === 3 && (
                                    <div className="dashcfg-looker-step">
                                      <div className="dashcfg-looker-step-title">Configura los campos (fuente + cálculo + SQL)</div>
                                      <div className="dashcfg-looker-step-help">
                                        Selecciona un campo a la izquierda. A la derecha verás su configuración, el constructor visual y el SQL generado.
                                      </div>

                                      {(() => {
                                        const fields = (dashcfgSelectedMeta?.clientFields || dashcfgSelectedFieldsAll || []);
                                        if (!fields.length) {
                                          return (
                                            <div className="dashcfg-empty">
                                              Esta sección no tiene campos configurables. Solo puedes activarla/desactivarla.
                                            </div>
                                          );
                                        }

                                        const q = String(cfgLookerFieldSearch || '').trim().toLowerCase();
                                        const list = fields.filter((fieldKey) => {
                                          if (!q) return true;
                                          const label = dashcfgGetFieldLabel(dashcfgSelectedSectionKey, fieldKey);
                                          const help = dashcfgGetFieldHelp(dashcfgSelectedSectionKey, fieldKey);
                                          const m = dashcfgSelectedSectionCfg.fieldMappings?.[fieldKey];
                                          const c = dashcfgSelectedSectionCfg.fieldCalculations?.[fieldKey] || '';
                                          const hay = `${fieldKey} ${label} ${help} ${m?.table || ''} ${m?.column || ''} ${c}`.toLowerCase();
                                          return hay.includes(q);
                                        });

                                        const selected = (cfgLookerSelectedFieldKey && fields.includes(cfgLookerSelectedFieldKey))
                                          ? cfgLookerSelectedFieldKey
                                          : (fields[0] || '');

                                        const renderFieldEditor = (fieldKey) => {
                                          const label = dashcfgGetFieldLabel(dashcfgSelectedSectionKey, fieldKey);
                                          const help = dashcfgGetFieldHelp(dashcfgSelectedSectionKey, fieldKey);
                                          const isDataField = dashcfgIsDataField(dashcfgSelectedSectionKey, fieldKey);
                                          // Verificar explícitamente si el campo está en false, no usar ?? true
                                          const fieldValue = dashcfgSelectedSectionCfg.fields?.[fieldKey];
                                          const isOn = fieldValue !== undefined ? Boolean(fieldValue) : true;

                                          const calc = dashcfgSelectedSectionCfg.fieldCalculations?.[fieldKey] || '';
                                          const specRaw = dashcfgSelectedSectionCfg.fieldCalculationSpecs?.[fieldKey];
                                          const calcSpec = dashcfgCalcNormalizeSpec(Array.isArray(specRaw) ? specRaw : []);
                                          const calcFromSpec = calcSpec.length > 0 ? dashcfgCalcBuildFormulaFromSpec(calcSpec) : '';
                                          const calcEffective = (calcSpec.length > 0 ? (calcFromSpec || '') : (calc || ''));

                                          const schemaReadyForCalc = (
                                            !schemaLoading
                                            && (schemaDataSource === (dashcfgSelectedSectionCfg.dataSource || 'main'))
                                          );
                                          const calcTables = schemaReadyForCalc ? Object.keys(schemaTables || {}) : [];
                                          const mappedFieldKeys = (dashcfgSelectedMeta?.dataFields || [])
                                            .filter((k) => {
                                              const m = dashcfgSelectedSectionCfg.fieldMappings?.[k];
                                              return Boolean(m && m.table && m.column);
                                            });
                                          const calcSafeId = `${dashcfgSelectedSectionKey}-${fieldKey}`.replace(/[^a-zA-Z0-9_-]/g, '_');
                                          const calcTableListId = `dashcfg-tables-${calcSafeId}`;
                                          const selectedTermIdxs = dashcfgGetSelectedTermIdxs(dashcfgSelectedSectionKey, fieldKey);
                                          const selectedTermSet = new Set(selectedTermIdxs);
                                          const quickAdd = dashcfgGetQuickAddState(dashcfgSelectedSectionKey, fieldKey, dashcfgSelectedBaseTable || '');
                                          const quickCols = schemaReadyForCalc ? (schemaTables?.[quickAdd.table] || []) : [];

                                          // Campos disponibles (para drag & drop)
                                          const availTable = String((isDataField ? dashcfgSelectedBaseTable : (quickAdd?.table || dashcfgSelectedBaseTable || '')) || '');
                                          const availSearch = String(quickAdd?.search || '').trim().toLowerCase();
                                          const availColsAll = (schemaReadyForCalc && availTable) ? (schemaTables?.[availTable] || []) : [];
                                          const availCols = (availColsAll || [])
                                            .filter((c) => {
                                              if (!availSearch) return true;
                                              return String(c || '').toLowerCase().includes(availSearch);
                                            })
                                            .slice(0, 260);
                                          const availMappedFields = (mappedFieldKeys || [])
                                            .filter((k) => {
                                              if (!availSearch) return true;
                                              const label = dashcfgGetFieldLabel(dashcfgSelectedSectionKey, k);
                                              return `${k} ${label}`.toLowerCase().includes(availSearch);
                                            })
                                            .slice(0, 120);

                                          // Panel de columnas (para mapear “Dato” por drag & drop)
                                          const mapTable = String(dashcfgSelectedBaseTable || '');
                                          const mapColsAll = (schemaReadyForCalc && mapTable) ? (schemaTables?.[mapTable] || []) : [];
                                          const mapSearch = String(quickAdd?.search || '').trim().toLowerCase();
                                          const mapCols = (mapColsAll || [])
                                            .filter((c) => {
                                              if (!mapSearch) return true;
                                              return String(c || '').toLowerCase().includes(mapSearch);
                                            })
                                            .slice(0, 260);

                                          const mapPanel = (
                                            <aside className="dashcfg-calc-fields" aria-label="Columnas disponibles">
                                              <div className="dashcfg-calc-fields-title">Columnas disponibles</div>
                                              {!schemaReadyForCalc ? (
                                                <div className="dashcfg-field-help">
                                                  Cargando esquema… (necesario para listar columnas).
                                                </div>
                                              ) : !mapTable ? (
                                                <div className="dashcfg-field-help">
                                                  Primero selecciona la <strong>Tabla principal</strong> en el paso “Datos”.
                                                </div>
                                              ) : (
                                                <>
                                                  <div className="dashcfg-field-help">
                                                    Tabla: <strong>{mapTable}</strong>
                                                  </div>
                                                  <input
                                                    className="dashcfg-term-input"
                                                    value={quickAdd.search}
                                                    onChange={(e) => dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { search: e.target.value })}
                                                    placeholder="Buscar columna…"
                                                    disabled={!schemaReadyForCalc}
                                                  />
                                                  <div className="dashcfg-calc-fields-list">
                                                    {mapCols.length === 0 ? (
                                                      <div className="dashcfg-field-help">No hay columnas para mostrar (revisa la búsqueda).</div>
                                                    ) : (
                                                      mapCols.map((c) => (
                                                        <div
                                                          key={`${mapTable}.${c}`}
                                                          className="dashcfg-draggable"
                                                          draggable
                                                          onDragStart={(e) => dashcfgDndSet(e, { kind: 'column', table: mapTable, column: c })}
                                                          title="Arrastra y suelta para mapear el campo"
                                                        >
                                                          <span className="dashcfg-pill neutral">col</span>
                                                          <span className="dashcfg-draggable-text">{c}</span>
                                                        </div>
                                                      ))
                                                    )}
                                                  </div>
                                                </>
                                              )}
                                            </aside>
                                          );

                                          const dndResolveTableColumn = (payload) => {
                                            const p = payload && typeof payload === 'object' ? payload : {};
                                            if (p.kind === 'column') {
                                              return { table: String(p.table || ''), column: String(p.column || '') };
                                            }
                                            if (p.kind === 'field') {
                                              const fk = String(p.fieldKey || '');
                                              const m = dashcfgSelectedSectionCfg.fieldMappings?.[fk] || {};
                                              return { table: String(m.table || ''), column: String(m.column || '') };
                                            }
                                            return { table: '', column: '' };
                                          };

                                          const dndAddValue = (payload) => {
                                            const base = dashcfgCalcNormalizeSpec(calcSpec);
                                            const wasEmpty = base.length === 0;
                                            // Usar el operador configurado en quickAdd.op, o '+' por defecto si no hay términos previos
                                            const opBase = DASHCFG_CALC_OPS.includes(quickAdd.op) ? quickAdd.op : '+';
                                            // Si ya hay términos, usar el operador configurado; si es el primer término, no usar operador
                                            const op = (base.length === 0 ? null : opBase);
                                            // PRESERVAR la función configurada - no cambiarla automáticamente
                                            const func = (typeof quickAdd.func === 'string' ? quickAdd.func.toUpperCase() : 'COUNT');
                                            const safeFunc = DASHCFG_CALC_FUNCS.includes(func) ? func : 'COUNT';
                                            // autoFunc solo se usa para sugerir, pero respetamos la función configurada
                                            const autoFunc = (fn, hint) => {
                                              const f = String(fn || 'COUNT').toUpperCase();
                                              const h = String(hint || '').toLowerCase();
                                              const looksText = /(status|estado|state|subestado|sub_state|tipo|category|categoria|nombre|name|result|outcome)/i.test(h);
                                              // Solo cambiar si la función configurada no es válida para texto
                                              if ((f === 'SUM' || f === 'AVG') && looksText && safeFunc === 'COUNT') return 'COUNT';
                                              // Usar la función configurada, no la sugerida
                                              return safeFunc;
                                            };
                                            const next = [...base];

                                            if (payload?.kind === 'column') {
                                              const table = String(payload.table || '');
                                              const column = String(payload.column || '');
                                              if (!table || !column) return;
                                              // NO limpiar las selecciones previas - permitir agregar múltiples columnas
                                              // dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { columns: [], table });
                                              next.push({
                                                op: op, // Usar el operador calculado (null si es el primer término, o el operador configurado)
                                                func: safeFunc, // Usar directamente la función configurada, sin autoFunc
                                                sourceType: 'table',
                                                table,
                                                column,
                                                fieldKey: '',
                                                number: '',
                                                openParen: 0,
                                                closeParen: 0,
                                              });
                                              // Actualizar la tabla si cambió, pero PRESERVAR la función y operador configurados
                                              if (table && table !== quickAdd.table) {
                                                dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { table });
                                              }
                                              // NO resetear func ni op - el usuario debe poder cambiar la función antes de arrastrar la siguiente columna
                                            } else if (payload?.kind === 'field') {
                                              const fk = String(payload.fieldKey || '');
                                              if (!fk) return;
                                              next.push({
                                                op: op, // Usar el operador calculado (null si es el primer término, o el operador configurado)
                                                func: safeFunc, // Usar directamente la función configurada, sin autoFunc
                                                sourceType: 'field',
                                                fieldKey: fk,
                                                table: '',
                                                column: '',
                                                number: '',
                                                openParen: 0,
                                                closeParen: 0,
                                              });
                                            } else {
                                              return;
                                            }

                                            dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, next, { syncFormula: true });
                                            if (!cfgCalcDrawerAdvanced && wasEmpty) setCfgCalcWizardStep(1);
                                          };

                                          const dndMapDataField = (payload) => {
                                            const p = payload && typeof payload === 'object' ? payload : {};
                                            if (p.kind !== 'column') return;
                                            const baseTable = String(dashcfgSelectedBaseTable || '');
                                            const table = String(p.table || '');
                                            const column = String(p.column || '');
                                            if (!baseTable) {
                                              setConfigError('Primero selecciona una Tabla principal en el paso “Datos”.');
                                              return;
                                            }
                                            if (!column) return;
                                            // Para mantener el modelo mental simple: el mapeo de “Dato” usa la tabla principal.
                                            if (table && table !== baseTable) {
                                              setConfigError('Este campo “Dato” se mapea desde la tabla principal. Cambia la Tabla principal en “Datos” o arrastra una columna de esa tabla.');
                                              return;
                                            }
                                            updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
                                              ...sec,
                                              fieldMappings: {
                                                ...(sec.fieldMappings || {}),
                                                [fieldKey]: {
                                                  ...(sec.fieldMappings?.[fieldKey] || {}),
                                                  table: baseTable,
                                                  column,
                                                },
                                              },
                                            }));
                                            showSuccess(`Mapeo actualizado: ${fieldKey} → ${baseTable}.${column}`);
                                          };

                                          const availablePanel = (
                                            <aside className="dashcfg-calc-fields" aria-label="Campos disponibles">
                                              <div className="dashcfg-calc-fields-title">Campos disponibles</div>
                                              <div className="dashcfg-calc-fields-controls">
                                                <select
                                                  className="dashcfg-term-input"
                                                  value={availTable}
                                                  onChange={(e) => dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { table: e.target.value, columns: [] })}
                                                  disabled={!schemaReadyForCalc}
                                                  title="Tabla para explorar"
                                                >
                                                  <option value="">Tabla…</option>
                                                  {calcTables.map((t) => (
                                                    <option key={t} value={t}>{t}</option>
                                                  ))}
                                                </select>
                                                <input
                                                  className="dashcfg-term-input"
                                                  value={quickAdd.search}
                                                  onChange={(e) => dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { search: e.target.value })}
                                                  placeholder="Buscar…"
                                                  disabled={!schemaReadyForCalc}
                                                />
                                              </div>

                                              {!schemaReadyForCalc ? (
                                                <div className="dashcfg-field-help">
                                                  Cargando esquema… (necesario para listar tablas/columnas).
                                                </div>
                                              ) : !availTable ? (
                                                <div className="dashcfg-field-help">
                                                  Selecciona una tabla para ver sus columnas.
                                                </div>
                                              ) : (
                                                <>
                                                  <div className="dashcfg-calc-fields-subtitle">Columnas ({availColsAll.length})</div>
                                                  <div className="dashcfg-calc-fields-list">
                                                    {availCols.length === 0 ? (
                                                      <div className="dashcfg-field-help">No hay columnas para mostrar (revisa la búsqueda).</div>
                                                    ) : (
                                                      availCols.map((c) => (
                                                        <div
                                                          key={`${availTable}.${c}`}
                                                          className="dashcfg-draggable"
                                                          draggable
                                                          onDragStart={(e) => dashcfgDndSet(e, { kind: 'column', table: availTable, column: c })}
                                                          title="Arrastra a Valores o Filtros"
                                                        >
                                                          <span className="dashcfg-pill neutral">col</span>
                                                          <span className="dashcfg-draggable-text">{c}</span>
                                                        </div>
                                                      ))
                                                    )}
                                                  </div>
                                                </>
                                              )}

                                              {availMappedFields.length > 0 && (
                                                <>
                                                  <div className="dashcfg-calc-fields-subtitle">Campos mapeados</div>
                                                  <div className="dashcfg-calc-fields-list">
                                                    {availMappedFields.map((k) => (
                                                      <div
                                                        key={`field-${k}`}
                                                        className="dashcfg-draggable"
                                                        draggable
                                                        onDragStart={(e) => dashcfgDndSet(e, { kind: 'field', fieldKey: k })}
                                                        title="Arrastra a Valores"
                                                      >
                                                        <span className="dashcfg-pill neutral">{'{{ }}'}</span>
                                                        <span className="dashcfg-draggable-text">{dashcfgGetFieldLabel(dashcfgSelectedSectionKey, k)}</span>
                                                      </div>
                                                    ))}
                                                  </div>
                                                </>
                                              )}
                                            </aside>
                                          );

                                          const customLabel = dashcfgSelectedSectionCfg.fieldLabels?.[fieldKey] || '';
                                          return (
                                            <div className="dashcfg-explore-editor-inner">
                                              <div className="dashcfg-explore-editor-head">
                                                <div>
                                                  <div className="dashcfg-explore-editor-title">{label}</div>
                                                  {help && <div className="dashcfg-field-help">{help}</div>}
                                                  <div style={{ marginTop: '12px' }}>
                                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'rgba(255, 255, 255, 0.7)' }}>
                                                      Nombre personalizado del campo:
                                                    </label>
                                                    <input
                                                      type="text"
                                                      className="dashcfg-term-input"
                                                      value={customLabel}
                                                      onChange={(e) => {
                                                        const newLabel = e.target.value.trim();
                                                        updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
                                                          ...sec,
                                                          fieldLabels: {
                                                            ...(sec.fieldLabels || {}),
                                                            [fieldKey]: newLabel || undefined,
                                                          },
                                                        }));
                                                      }}
                                                      placeholder={`${label} (nombre por defecto)`}
                                                      style={{ width: '100%', maxWidth: '400px' }}
                                                    />
                                                    <div className="dashcfg-field-help" style={{ fontSize: '11px', marginTop: '4px' }}>
                                                      Este nombre se mostrará en el panel del cliente. Déjalo vacío para usar el nombre por defecto.
                                                    </div>
                                                  </div>
                                                  <div style={{ marginTop: '16px' }}>
                                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'rgba(255, 255, 255, 0.7)' }}>
                                                      Formato de visualización:
                                                    </label>
                                                    <select
                                                      className="dashcfg-term-input"
                                                      value={dashcfgSelectedSectionCfg.fieldFormats?.[fieldKey] || ''}
                                                      onChange={(e) => {
                                                        const newFormat = e.target.value || undefined;
                                                        updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
                                                          ...sec,
                                                          fieldFormats: {
                                                            ...(sec.fieldFormats || {}),
                                                            [fieldKey]: newFormat,
                                                          },
                                                        }));
                                                      }}
                                                      style={{ width: '100%', maxWidth: '400px' }}
                                                    >
                                                      <option value="">Por defecto (automático)</option>
                                                      <option value="integer">Número entero (1, 2, 3...)</option>
                                                      <option value="decimal">Número decimal (1.23, 4.56...)</option>
                                                      <option value="duration">Duración (mm:ss)</option>
                                                      <option value="percentage">Porcentaje (45%)</option>
                                                      <option value="currency">Moneda ($1,234.56)</option>
                                                      <option value="date">Fecha (DD/MM/YYYY)</option>
                                                      <option value="datetime">Fecha y hora (DD/MM/YYYY HH:MM)</option>
                                                      <option value="time">Hora (HH:MM:SS)</option>
                                                    </select>
                                                    <div className="dashcfg-field-help" style={{ fontSize: '11px', marginTop: '4px' }}>
                                                      Define cómo se mostrará el valor de este campo en el panel del cliente.
                                                      Si cambias el tipo de campo (ej: de "Total llamadas" a "Tiempo total"), cambia el formato aquí.
                                                    </div>
                                                  </div>
                                                </div>
                                                <label className="dashcfg-switch">
                                                  <input
                                                    type="checkbox"
                                                    checked={Boolean(isOn)}
                                                    onChange={(e) => {
                                                      const newValue = e.target.checked;
                                                      console.log(`Toggle ${fieldKey}: ${isOn} -> ${newValue}`);
                                                      updateSectionConfig(dashcfgSelectedSectionKey, (sec) => {
                                                        const updatedFields = { ...(sec.fields || {}), [fieldKey]: newValue };
                                                        console.log(`Actualizando campos para ${fieldKey}:`, updatedFields);
                                                        return {
                                                          ...sec,
                                                          fields: updatedFields
                                                        };
                                                      });
                                                    }}
                                                  />
                                                  <span>{isOn ? 'Visible' : 'Oculto'}</span>
                                                </label>
                                              </div>

                                              {/* Configuración de Audio/Transcripciones - Solo para analytics_ai */}
                                              {dashcfgSelectedSectionKey === 'analytics_ai' && (
                                                <div className="dashcfg-card">
                                                  <div className="dashcfg-card-title">Acciones disponibles (Audio y Transcripciones)</div>
                                                  <div className="dashcfg-hint" style={{ marginBottom: '12px' }}>
                                                    Configura qué acciones estarán disponibles cuando el cliente vea los detalles de este campo.
                                                  </div>
                                                  <div style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '10px'
                                                  }}>
                                                    {/* Checkbox: Escuchar llamada */}
                                                    <label className="dashcfg-switch">
                                                      <input
                                                        type="checkbox"
                                                        checked={Boolean(dashcfgSelectedSectionCfg.fieldAllowAudio?.[fieldKey])}
                                                        onChange={(e) => {
                                                          const newValue = e.target.checked;
                                                          console.log(`📝 Actualizando fieldAllowAudio para ${fieldKey}: ${newValue}`);
                                                          console.log(`📝 Estado actual de fieldAllowAudio:`, dashcfgSelectedSectionCfg.fieldAllowAudio);
                                                          updateSectionConfig(dashcfgSelectedSectionKey, (sec) => {
                                                            const currentAudioConfig = sec.fieldAllowAudio || {};
                                                            const updatedAudioConfig = {
                                                              ...currentAudioConfig,
                                                              [fieldKey]: newValue
                                                            };
                                                            console.log(`📝 Nuevo estado de fieldAllowAudio:`, updatedAudioConfig);
                                                            return {
                                                              ...sec,
                                                              fieldAllowAudio: updatedAudioConfig
                                                            };
                                                          });
                                                        }}
                                                      />
                                                      <span>{dashcfgSelectedSectionCfg.fieldAllowAudio?.[fieldKey] ? 'Habilitado' : 'Deshabilitado'}</span>
                                                    </label>
                                                    <small className="dashcfg-hint" style={{ marginTop: '-8px', marginBottom: '4px' }}>
                                                      Cuando está habilitado, el cliente podrá: <strong>Ver transcripción</strong>, <strong>Descargar audio</strong> y <strong>Escuchar llamada</strong> en los detalles de este campo.
                                                    </small>
                                                  </div>
                                                </div>
                                              )}

                                              <div className="dashcfg-card">
                                                <div className="dashcfg-card-title">Contexto de prueba (preview)</div>
                                                <div className="dashcfg-panel-grid">
                                                  <div className="dashcfg-field">
                                                    <label>Periodo</label>
                                                    <select
                                                      value={cfgFormulaPeriod}
                                                      onChange={(e) => setCfgFormulaPeriod(e.target.value)}
                                                    >
                                                      <option value="today">Hoy</option>
                                                      <option value="last_7d">Últimos 7 días</option>
                                                      <option value="last_30d">Últimos 30 días</option>
                                                    </select>
                                                  </div>
                                                  {dashcfgSelectedSectionKey === 'analytics' && (
                                                    <div className="dashcfg-field">
                                                      <label>Categoría</label>
                                                      <select
                                                        value={cfgFormulaCategory}
                                                        onChange={(e) => setCfgFormulaCategory(e.target.value)}
                                                      >
                                                        <option value="outbound">Outbound</option>
                                                        <option value="inbound">Inbound</option>
                                                      </select>
                                                    </div>
                                                  )}
                                                </div>
                                                <small className="dashcfg-hint">
                                                  Los filtros ahora solo se aplican cuando los defines explícitamente en el constructor visual (paso "Filtros").
                                                  El periodo y categoría aquí son solo para referencia.
                                                </small>
                                              </div>

                                              {isDataField ? (
                                                <div className="dashcfg-card">
                                                  <div className="dashcfg-card-title">Fuente (tabla/columna)</div>
                                                  {!dashcfgSelectedBaseTable ? (
                                                    <div className="dashcfg-empty">
                                                      Primero selecciona una <strong>Tabla principal</strong> en el paso “Datos”.
                                                    </div>
                                                  ) : (
                                                    <div className="dashcfg-calc-step-grid">
                                                      {mapPanel}
                                                      <div className="dashcfg-calc-step-main">
                                                        <div
                                                          className="dashcfg-dropzone"
                                                          onDragOver={(e) => {
                                                            e.preventDefault();
                                                            e.currentTarget.classList.add('over');
                                                          }}
                                                          onDragLeave={(e) => e.currentTarget.classList.remove('over')}
                                                          onDrop={(e) => {
                                                            e.preventDefault();
                                                            e.currentTarget.classList.remove('over');
                                                            const p = dashcfgDndGet(e);
                                                            if (p) dndMapDataField(p);
                                                          }}
                                                        >
                                                          <div className="dashcfg-dropzone-title">Mapear por arrastrar y soltar</div>
                                                          <div className="dashcfg-hint">
                                                            Suelta una columna aquí para asignarla a <strong>{label}</strong>.
                                                          </div>
                                                        </div>

                                                        <div className="dashcfg-panel-grid">
                                                          <div className="dashcfg-field">
                                                            <label>Tabla</label>
                                                            <input value={dashcfgSelectedBaseTable} disabled />
                                                            <small className="dashcfg-hint">
                                                              Para cambiar la tabla principal, vuelve a “Datos”.
                                                            </small>
                                                          </div>
                                                          <div className="dashcfg-field">
                                                            <label>Columna</label>
                                                            <select
                                                              value={dashcfgSelectedSectionCfg.fieldMappings?.[fieldKey]?.column || ''}
                                                              onChange={(e) => updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
                                                                ...sec,
                                                                fieldMappings: {
                                                                  ...(sec.fieldMappings || {}),
                                                                  [fieldKey]: {
                                                                    ...(sec.fieldMappings?.[fieldKey] || {}),
                                                                    table: dashcfgSelectedBaseTable || sec.fieldMappings?.[fieldKey]?.table || null,
                                                                    column: e.target.value || null,
                                                                  }
                                                                }
                                                              }))}
                                                              disabled={!dashcfgSelectedBaseTable || schemaLoading || (schemaDataSource !== (dashcfgSelectedSectionCfg.dataSource || 'main'))}
                                                            >
                                                              <option value="">{dashcfgSelectedBaseTable ? 'Columna…' : 'Selecciona tabla…'}</option>
                                                              {(
                                                                (schemaDataSource === (dashcfgSelectedSectionCfg.dataSource || 'main')
                                                                  ? (schemaTables?.[dashcfgSelectedBaseTable] || [])
                                                                  : [])
                                                              ).map((col) => (
                                                                <option key={col} value={col}>{col}</option>
                                                              ))}
                                                            </select>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              ) : (
                                                <div className="dashcfg-card">
                                                  <div className="dashcfg-card-title">Constructor visual (arrastrar y soltar)</div>

                                                  <div className="dashcfg-drawer-body dashcfg-looker-builder-body">
                                                    <div className="dashcfg-drawer-toolbar">
                                                      <span className="dashcfg-pill neutral">Modo</span>
                                                      <button
                                                        type="button"
                                                        className={`btn-chip ${!cfgCalcDrawerAdvanced ? 'dashcfg-active' : ''}`}
                                                        onClick={() => setCfgCalcDrawerAdvanced(false)}
                                                      >
                                                        Simple
                                                      </button>
                                                      <button
                                                        type="button"
                                                        className={`btn-chip ${cfgCalcDrawerAdvanced ? 'dashcfg-active' : ''}`}
                                                        onClick={() => setCfgCalcDrawerAdvanced(true)}
                                                      >
                                                        Avanzado
                                                      </button>
                                                    </div>

                                                    <div className="dashcfg-wizard-tabs">
                                                      <button
                                                        type="button"
                                                        className={`dashcfg-wizard-tab ${cfgCalcWizardStep === 0 ? 'active' : ''}`}
                                                        onClick={() => setCfgCalcWizardStep(0)}
                                                      >
                                                        1. Valores
                                                      </button>
                                                      <button
                                                        type="button"
                                                        className={`dashcfg-wizard-tab ${cfgCalcWizardStep === 1 ? 'active' : ''}`}
                                                        onClick={() => setCfgCalcWizardStep(1)}
                                                        disabled={calcSpec.length === 0}
                                                        title={calcSpec.length === 0 ? 'Agrega al menos un valor primero' : ''}
                                                      >
                                                        2. Filtros
                                                      </button>
                                                      <button
                                                        type="button"
                                                        className={`dashcfg-wizard-tab ${cfgCalcWizardStep === 2 ? 'active' : ''}`}
                                                        onClick={() => setCfgCalcWizardStep(2)}
                                                        disabled={calcSpec.length === 0}
                                                        title={calcSpec.length === 0 ? 'Agrega al menos un valor primero' : ''}
                                                      >
                                                        3. Probar
                                                      </button>
                                                    </div>

                                                    {/* ======= Paso 1: Valores ======= */}
                                                    {cfgCalcWizardStep === 0 && (
                                                      <div className="dashcfg-calc-step-grid">
                                                        {availablePanel}
                                                        <div className="dashcfg-calc-step-main">
                                                          {!schemaReadyForCalc && (
                                                            <div className="dashcfg-field-help">
                                                              Cargando/actualizando el esquema de la base seleccionada… (necesario para listar tablas/columnas)
                                                            </div>
                                                          )}

                                                          <div
                                                            className="dashcfg-dropzone"
                                                            onDragOver={(e) => {
                                                              e.preventDefault();
                                                              e.currentTarget.classList.add('over');
                                                            }}
                                                            onDragLeave={(e) => e.currentTarget.classList.remove('over')}
                                                            onDrop={(e) => {
                                                              e.preventDefault();
                                                              e.currentTarget.classList.remove('over');
                                                              const p = dashcfgDndGet(e);
                                                              if (p) dndAddValue(p);
                                                            }}
                                                          >
                                                            <div className="dashcfg-dropzone-title">Valores</div>
                                                            <div className="dashcfg-hint">
                                                              Arrastra columnas/campos desde “Campos disponibles” y suéltalos aquí.
                                                            </div>
                                                          </div>

                                                          <div className="dashcfg-builder">
                                                            <div className="dashcfg-builder-head">
                                                              <div className="dashcfg-builder-title">Constructor visual</div>
                                                              <div className="dashcfg-hint">
                                                                Tip: selecciona varias columnas para generar sumas rápidamente, y usa Agrupar para (A+B)/C.
                                                              </div>
                                                            </div>

                                                            <div className="dashcfg-quickadd">
                                                              <div className="dashcfg-quickadd-row">
                                                                <span className="dashcfg-pill neutral">Añadir valores</span>

                                                                <select
                                                                  className="dashcfg-term-func"
                                                                  value={quickAdd.func}
                                                                  onChange={(e) => {
                                                                    const v = e.target.value;
                                                                    // Solo afecta a los siguientes términos a agregar; NO reescribe los ya agregados
                                                                    dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { func: v });
                                                                  }}
                                                                  disabled={!schemaReadyForCalc}
                                                                  title="Función a aplicar"
                                                                >
                                                                  {DASHCFG_CALC_FUNCS.map((f) => (
                                                                    <option key={f} value={f}>
                                                                      {f === 'COUNT_DISTINCT' ? 'COUNT_DISTINCT (únicos)' : f}
                                                                    </option>
                                                                  ))}
                                                                </select>

                                                                <select
                                                                  className="dashcfg-term-op"
                                                                  value={quickAdd.op}
                                                                  onChange={(e) => {
                                                                    const v = e.target.value;
                                                                    dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { op: v });
                                                                    // Modo Simple: aplicar la operación a todos los términos (A / B, A - B, etc.)
                                                                    if (!cfgCalcDrawerAdvanced && Array.isArray(calcSpec) && calcSpec.length > 1) {
                                                                      const op = DASHCFG_CALC_OPS.includes(v) ? v : '+';
                                                                      const nextSpec = calcSpec.map((t, idx) => {
                                                                        if (!t) return t;
                                                                        if (idx === 0) return { ...(t || {}), op: null };
                                                                        return { ...(t || {}), op };
                                                                      });
                                                                      dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, nextSpec, { syncFormula: true });
                                                                    }
                                                                  }}
                                                                  disabled={!schemaReadyForCalc}
                                                                  title={!cfgCalcDrawerAdvanced ? 'Operación entre valores (Simple)' : 'Operación entre columnas'}
                                                                >
                                                                  {DASHCFG_CALC_OPS.map((o) => (
                                                                    <option key={o} value={o}>{o}</option>
                                                                  ))}
                                                                </select>

                                                                {cfgCalcDrawerAdvanced && (
                                                                  <>
                                                                    <label className="dashcfg-quickadd-wrap" title="Agrupa automáticamente las columnas añadidas con paréntesis">
                                                                      <input
                                                                        type="checkbox"
                                                                        checked={Boolean(quickAdd.wrap)}
                                                                        onChange={(e) => dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { wrap: e.target.checked })}
                                                                        disabled={!schemaReadyForCalc}
                                                                      />
                                                                      <span>Agrupar ( )</span>
                                                                    </label>
                                                                  </>
                                                                )}

                                                                <input
                                                                  className="dashcfg-term-input"
                                                                  list={calcTableListId}
                                                                  value={quickAdd.table}
                                                                  onChange={(e) => {
                                                                    const t = e.target.value;
                                                                    dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { table: t, columns: [] });
                                                                  }}
                                                                  placeholder="Tabla…"
                                                                  disabled={!schemaReadyForCalc}
                                                                />
                                                                <datalist id={calcTableListId}>
                                                                  {calcTables.map((tbl) => (
                                                                    <option key={tbl} value={tbl} />
                                                                  ))}
                                                                </datalist>

                                                                <div className="dashcfg-col-picker">
                                                                  <input
                                                                    className="dashcfg-term-input"
                                                                    value={quickAdd.search}
                                                                    onChange={(e) => dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { search: e.target.value })}
                                                                    placeholder="Buscar columna…"
                                                                    disabled={!schemaReadyForCalc || !quickAdd.table}
                                                                  />

                                                                  <div className="dashcfg-col-list" aria-label="Columnas disponibles">
                                                                    {quickCols
                                                                      .filter((c) => {
                                                                        const q = String(quickAdd.search || '').trim().toLowerCase();
                                                                        if (!q) return true;
                                                                        return String(c || '').toLowerCase().includes(q);
                                                                      })
                                                                      .slice(0, 400)
                                                                      .map((c) => {
                                                                        const checked = (quickAdd.columns || []).includes(c);
                                                                        return (
                                                                          <label key={c} className={`dashcfg-col-item ${checked ? 'on' : ''}`}>
                                                                            <input
                                                                              type="checkbox"
                                                                              checked={checked}
                                                                              onChange={(e) => {
                                                                                const set = new Set(quickAdd.columns || []);
                                                                                if (e.target.checked) set.add(c);
                                                                                else set.delete(c);
                                                                                dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { columns: Array.from(set) });
                                                                              }}
                                                                            />
                                                                            <span>{c}</span>
                                                                          </label>
                                                                        );
                                                                      })}
                                                                  </div>

                                                                  <div className="dashcfg-col-actions">
                                                                    <button
                                                                      type="button"
                                                                      className="btn-chip"
                                                                      disabled={!schemaReadyForCalc || !quickAdd.table}
                                                                      onClick={() => {
                                                                        const q = String(quickAdd.search || '').trim().toLowerCase();
                                                                        const cols = quickCols
                                                                          .filter((c) => !q || String(c || '').toLowerCase().includes(q))
                                                                          .slice(0, 400);
                                                                        dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { columns: cols });
                                                                      }}
                                                                    >
                                                                      Seleccionar todo
                                                                    </button>
                                                                    <button
                                                                      type="button"
                                                                      className="btn-chip"
                                                                      disabled={(quickAdd.columns || []).length === 0}
                                                                      onClick={() => dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { columns: [] })}
                                                                    >
                                                                      Limpiar
                                                                    </button>
                                                                  </div>
                                                                </div>

                                                                <button
                                                                  type="button"
                                                                  className="btn-chip"
                                                                  disabled={!schemaReadyForCalc || !quickAdd.table || (quickAdd.columns || []).length === 0}
                                                                  onClick={() => {
                                                                    const base = dashcfgCalcNormalizeSpec(calcSpec);
                                                                    const wasEmpty = base.length === 0;
                                                                    const cols = (quickAdd.columns || []).filter(Boolean);
                                                                    if (!quickAdd.table || cols.length === 0) return;

                                                                    const opBase = DASHCFG_CALC_OPS.includes(quickAdd.op) ? quickAdd.op : '+';
                                                                    const op = opBase;
                                                                    const func = (typeof quickAdd.func === 'string' ? quickAdd.func.toUpperCase() : 'COUNT');
                                                                    const safeFunc = DASHCFG_CALC_FUNCS.includes(func) ? func : 'COUNT';

                                                                    const startIdx = base.length;
                                                                    const next = [...base];
                                                                    // Si solo hay una columna seleccionada, agregarla como un solo término
                                                                    // Si hay múltiples columnas, agregarlas todas con el operador configurado
                                                                    cols.forEach((col, colIdx) => {
                                                                      // Para el primer término agregado en este lote, usar el operador configurado
                                                                      // Para términos subsecuentes en el mismo lote, también usar el operador configurado
                                                                      const termOp = (startIdx === 0 && colIdx === 0) ? null : op;
                                                                      next.push({
                                                                        op: termOp,
                                                                        func: safeFunc,
                                                                        sourceType: 'table',
                                                                        table: quickAdd.table,
                                                                        column: col,
                                                                        fieldKey: '',
                                                                        number: '',
                                                                        openParen: 0,
                                                                        closeParen: 0,
                                                                      });
                                                                    });

                                                                    const wrap = cfgCalcDrawerAdvanced ? Boolean(quickAdd.wrap) : false;
                                                                    if (wrap && cols.length > 1) {
                                                                      const firstIdx = startIdx;
                                                                      const lastIdx = next.length - 1;
                                                                      const first = next[firstIdx] || {};
                                                                      const last = next[lastIdx] || {};
                                                                      first.openParen = Math.min((parseInt(first.openParen ?? 0, 10) || 0) + 1, 3);
                                                                      last.closeParen = Math.min((parseInt(last.closeParen ?? 0, 10) || 0) + 1, 3);
                                                                      next[firstIdx] = first;
                                                                      next[lastIdx] = last;
                                                                    }

                                                                    dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, next, { syncFormula: true });
                                                                    dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { columns: [] });
                                                                    if (!cfgCalcDrawerAdvanced && wasEmpty) {
                                                                      // Flujo estilo tabla dinámica: después de elegir valores, ir a filtros
                                                                      setCfgCalcWizardStep(1);
                                                                    }
                                                                  }}
                                                                >
                                                                  Añadir
                                                                </button>
                                                              </div>

                                                              <small className="dashcfg-hint">
                                                                Esto crea términos automáticamente (uno por columna). Luego puedes cambiar +/−/×/÷ y agrupar.
                                                              </small>
                                                            </div>

                                                            <div className="dashcfg-quickadd-row">
                                                              <span className="dashcfg-pill neutral">Añadir número</span>
                                                              <input
                                                                className="dashcfg-term-input"
                                                                value={quickAdd.number}
                                                                onChange={(e) => dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { number: e.target.value })}
                                                                placeholder="Ej: 100"
                                                                disabled={!schemaReadyForCalc}
                                                                style={{ width: '110px' }}
                                                              />
                                                              <button
                                                                type="button"
                                                                className="dashcfg-btn secondary"
                                                                disabled={!schemaReadyForCalc || quickAdd.number === '' || quickAdd.number === null || quickAdd.number === undefined}
                                                                onClick={() => {
                                                                  const base = dashcfgCalcNormalizeSpec(calcSpec);
                                                                  const wasEmpty = base.length === 0;
                                                                  const opBase = DASHCFG_CALC_OPS.includes(quickAdd.op) ? quickAdd.op : '+';
                                                                  const op = (base.length === 0 ? null : opBase);
                                                                  const next = [...base, {
                                                                    op,
                                                                    func: '', // no aplicar función sobre números
                                                                    sourceType: 'number',
                                                                    number: quickAdd.number,
                                                                    table: '',
                                                                    column: '',
                                                                    fieldKey: '',
                                                                    openParen: 0,
                                                                    closeParen: 0,
                                                                  }];
                                                                  dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, next, { syncFormula: true });
                                                                  if (!cfgCalcDrawerAdvanced && wasEmpty) setCfgCalcWizardStep(1);
                                                                }}
                                                              >
                                                                Añadir
                                                              </button>
                                                            </div>

                                                            {calcSpec.length > 0 && (
                                                              <div className="dashcfg-pivot-grid">
                                                                <div className="dashcfg-pivot-block">
                                                                  <div className="dashcfg-pivot-title">Valores seleccionados</div>
                                                                  <div className="dashcfg-chip-list">
                                                                    {calcSpec.map((t, i) => {
                                                                      const open = '('.repeat(Math.max(0, parseInt(t?.openParen ?? 0, 10) || 0));
                                                                      const close = ')'.repeat(Math.max(0, parseInt(t?.closeParen ?? 0, 10) || 0));
                                                                      const opTxt = (i === 0 ? '=' : (t?.op || '+'));
                                                                      let src = '';
                                                                      if ((t?.sourceType || '') === 'number') {
                                                                        src = String(t?.number ?? '0');
                                                                      } else if ((t?.sourceType || '') === 'field') {
                                                                        src = `{{${t?.fieldKey || ''}}}`;
                                                                      } else {
                                                                        src = `${t?.table || ''}.${t?.column || ''}`;
                                                                      }
                                                                      const fn = ((t?.sourceType || '') === 'number') ? '' : (t?.func || 'SUM');
                                                                      const fc = Array.isArray(t?.filters) ? t.filters.length : 0;
                                                                      const f0 = (Array.isArray(t?.filters) && t.filters[0]) ? t.filters[0] : null;
                                                                      const fVals = Array.isArray(f0?.values) ? f0.values : [];
                                                                      const fTitle = (f0 && f0.column)
                                                                        ? `Filtro: ${String(f0.column)} ${String(f0.operator || '')} ${fVals.slice(0, 8).join(', ')}${fVals.length > 8 ? '…' : ''}`
                                                                        : '';
                                                                      return (
                                                                        <span key={i} className="dashcfg-chip">
                                                                          <span className="dashcfg-chip-op">{opTxt}</span>
                                                                          <span className="dashcfg-chip-body">
                                                                            {open}{fn ? `${fn}(${src})` : src}{close}
                                                                          </span>
                                                                          {fc > 0 && <span className="dashcfg-chip-tag" title={fTitle || 'Filtro aplicado'}>Filtro</span>}
                                                                          <button
                                                                            type="button"
                                                                            className="dashcfg-chip-x"
                                                                            title="Quitar este valor"
                                                                            onClick={() => {
                                                                              const next = calcSpec.filter((_, idx2) => idx2 !== i);
                                                                              dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, next, { syncFormula: true });
                                                                            }}
                                                                          >
                                                                            ×
                                                                          </button>
                                                                        </span>
                                                                      );
                                                                    })}
                                                                  </div>
                                                                  <small className="dashcfg-hint">
                                                                    Si necesitas una operación especial, cambia a <strong>Avanzado</strong>.
                                                                  </small>
                                                                </div>
                                                              </div>
                                                            )}

                                                            {cfgCalcDrawerAdvanced && (
                                                              <div className="dashcfg-calc-actions">
                                                                <button
                                                                  type="button"
                                                                  className="btn-chip"
                                                                  disabled={!schemaReadyForCalc}
                                                                  onClick={() => {
                                                                    const next = [
                                                                      ...calcSpec,
                                                                      {
                                                                        op: (calcSpec.length === 0 ? null : '+'),
                                                                        func: 'SUM',
                                                                        sourceType: 'table',
                                                                        table: dashcfgSelectedBaseTable || '',
                                                                        column: '',
                                                                        fieldKey: '',
                                                                        number: '',
                                                                        openParen: 0,
                                                                        closeParen: 0,
                                                                        filters: [],
                                                                      },
                                                                    ];
                                                                    dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, next, { syncFormula: true });
                                                                  }}
                                                                >
                                                                  + Término
                                                                </button>

                                                                {calcSpec.length > 0 && (
                                                                  <>
                                                                    <button
                                                                      type="button"
                                                                      className="btn-chip"
                                                                      disabled={!schemaReadyForCalc || selectedTermIdxs.length < 2}
                                                                      onClick={() => dashcfgGroupSelectedTerms(dashcfgSelectedSectionKey, fieldKey, calcSpec, 'group')}
                                                                    >
                                                                      Agrupar
                                                                    </button>
                                                                    <button
                                                                      type="button"
                                                                      className="btn-chip"
                                                                      disabled={!schemaReadyForCalc || selectedTermIdxs.length < 1}
                                                                      onClick={() => dashcfgGroupSelectedTerms(dashcfgSelectedSectionKey, fieldKey, calcSpec, 'ungroup')}
                                                                    >
                                                                      Desagrupar
                                                                    </button>
                                                                    <button
                                                                      type="button"
                                                                      className="btn-chip"
                                                                      disabled={selectedTermIdxs.length < 1}
                                                                      onClick={() => dashcfgClearSelection(dashcfgSelectedSectionKey, fieldKey)}
                                                                    >
                                                                      Selección ({selectedTermIdxs.length})
                                                                    </button>
                                                                  </>
                                                                )}
                                                              </div>
                                                            )}

                                                            {/* Editor avanzado por término (solo en modo avanzado) */}
                                                            {calcSpec.length > 0 && cfgCalcDrawerAdvanced && (
                                                              <div className="dashcfg-term-list">
                                                                {calcSpec.map((term, idx) => {
                                                                  const colListId = `dashcfg-cols-${calcSafeId}-${idx}`;
                                                                  const t = term || {};
                                                                  const sourceType = t.sourceType || 'table';
                                                                  const opVal = (idx === 0 ? '' : (t.op || '+'));
                                                                  const funcVal = (t.func || 'COUNT');
                                                                  const tableVal = (t.table || '');
                                                                  const colVal = (t.column || '');
                                                                  const fieldVal = (t.fieldKey || '');
                                                                  const numVal = (t.number ?? '');
                                                                  const openParenVal = parseInt(t.openParen ?? 0, 10) || 0;
                                                                  const closeParenVal = parseInt(t.closeParen ?? 0, 10) || 0;
                                                                  const termSelected = selectedTermSet.has(idx);
                                                                  const termFilters = Array.isArray(t.filters) ? t.filters : [];
                                                                  const f0 = (termFilters[0] && typeof termFilters[0] === 'object') ? termFilters[0] : null;
                                                                  const filterEnabled = Boolean(f0 && f0.column);
                                                                  const filterOp = (f0?.operator || 'in');
                                                                  const filterVals = Array.isArray(f0?.values) ? f0.values : [];

                                                                  const updateTerm = (patch) => {
                                                                    const next = calcSpec.map((x, i) => (i === idx ? { ...(x || {}), ...patch } : x));
                                                                    dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, next, { syncFormula: true });
                                                                  };
                                                                  const removeTerm = () => {
                                                                    dashcfgClearSelection(dashcfgSelectedSectionKey, fieldKey);
                                                                    const next = calcSpec.filter((_, i) => i !== idx);
                                                                    dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, next, { syncFormula: true });
                                                                  };

                                                                  return (
                                                                    <div key={idx} className={`dashcfg-term-row ${termSelected ? 'selected' : ''}`}>
                                                                      <label className="dashcfg-term-select" title="Seleccionar término para agrupar/desagrupar">
                                                                        <input
                                                                          type="checkbox"
                                                                          checked={termSelected}
                                                                          onChange={() => dashcfgToggleSelectedTerm(dashcfgSelectedSectionKey, fieldKey, idx)}
                                                                          disabled={!schemaReadyForCalc}
                                                                        />
                                                                      </label>
                                                                      {idx === 0 ? (
                                                                        <span className="dashcfg-term-op">=</span>
                                                                      ) : (
                                                                        <select
                                                                          className="dashcfg-term-op"
                                                                          value={opVal || '+'}
                                                                          onChange={(e) => updateTerm({ op: e.target.value })}
                                                                          disabled={!schemaReadyForCalc}
                                                                        >
                                                                          {DASHCFG_CALC_OPS.map((o) => (
                                                                            <option key={o} value={o}>{o}</option>
                                                                          ))}
                                                                        </select>
                                                                      )}

                                                                      <button
                                                                        type="button"
                                                                        className={`btn-chip dashcfg-paren ${openParenVal > 0 ? 'paren-on' : ''}`}
                                                                        onClick={() => updateTerm({ openParen: openParenVal > 0 ? 0 : 1 })}
                                                                        disabled={!schemaReadyForCalc}
                                                                        title="Abrir paréntesis antes de este término (para agrupar)"
                                                                      >
                                                                        (
                                                                      </button>
                                                                      <button
                                                                        type="button"
                                                                        className={`btn-chip dashcfg-paren ${closeParenVal > 0 ? 'paren-on' : ''}`}
                                                                        onClick={() => updateTerm({ closeParen: closeParenVal > 0 ? 0 : 1 })}
                                                                        disabled={!schemaReadyForCalc}
                                                                        title="Cerrar paréntesis después de este término (para agrupar)"
                                                                      >
                                                                        )
                                                                      </button>

                                                                      <select
                                                                        className="dashcfg-term-source"
                                                                        value={sourceType}
                                                                        onChange={(e) => {
                                                                          const nextType = e.target.value;
                                                                          if (nextType === 'number') {
                                                                            updateTerm({ sourceType: 'number', number: numVal, table: '', column: '', fieldKey: '' });
                                                                          } else if (nextType === 'field') {
                                                                            updateTerm({ sourceType: 'field', fieldKey: mappedFieldKeys?.[0] || '', table: '', column: '' });
                                                                          } else {
                                                                            updateTerm({ sourceType: 'table', table: dashcfgSelectedBaseTable || '', column: '' });
                                                                          }
                                                                        }}
                                                                        disabled={!schemaReadyForCalc}
                                                                      >
                                                                        <option value="table">Tabla/Columna</option>
                                                                        <option value="field">Campo mapeado</option>
                                                                        <option value="number">Número</option>
                                                                      </select>

                                                                      {sourceType !== 'number' && (
                                                                        <select
                                                                          className="dashcfg-term-func"
                                                                          value={funcVal}
                                                                          onChange={(e) => updateTerm({ func: e.target.value })}
                                                                          disabled={!schemaReadyForCalc}
                                                                        >
                                                                          {DASHCFG_CALC_FUNCS.map((f) => (
                                                                            <option key={f} value={f}>
                                                                              {f === 'COUNT_DISTINCT' ? 'COUNT_DISTINCT (únicos)' : f}
                                                                            </option>
                                                                          ))}
                                                                        </select>
                                                                      )}

                                                                      {sourceType === 'field' && (
                                                                        <select
                                                                          className="dashcfg-term-input"
                                                                          value={fieldVal}
                                                                          onChange={(e) => updateTerm({ fieldKey: e.target.value })}
                                                                          disabled={!schemaReadyForCalc}
                                                                        >
                                                                          <option value="">Selecciona campo…</option>
                                                                          {(mappedFieldKeys || []).map((k) => (
                                                                            <option key={k} value={k}>{dashcfgGetFieldLabel(dashcfgSelectedSectionKey, k)} ({k})</option>
                                                                          ))}
                                                                        </select>
                                                                      )}

                                                                      {sourceType === 'table' && (
                                                                        <>
                                                                          <input
                                                                            className="dashcfg-term-input"
                                                                            list={calcTableListId}
                                                                            value={tableVal}
                                                                            onChange={(e) => updateTerm({ table: e.target.value, column: '' })}
                                                                            placeholder="Tabla…"
                                                                            disabled={!schemaReadyForCalc}
                                                                          />

                                                                          <input
                                                                            className="dashcfg-term-input"
                                                                            list={colListId}
                                                                            value={colVal}
                                                                            onChange={(e) => updateTerm({ column: e.target.value })}
                                                                            placeholder="Columna…"
                                                                            disabled={!schemaReadyForCalc || !tableVal}
                                                                          />
                                                                          <datalist id={colListId}>
                                                                            {(schemaTables?.[tableVal] || []).map((col) => (
                                                                              <option key={col} value={col} />
                                                                            ))}
                                                                          </datalist>
                                                                        </>
                                                                      )}

                                                                      {sourceType === 'number' && (
                                                                        <input
                                                                          className="dashcfg-term-input"
                                                                          value={numVal}
                                                                          onChange={(e) => updateTerm({ number: e.target.value })}
                                                                          placeholder="Número…"
                                                                          disabled={!schemaReadyForCalc}
                                                                        />
                                                                      )}

                                                                      {(sourceType === 'table' || sourceType === 'field') && (
                                                                        <details className="dashcfg-filter" open={filterEnabled}>
                                                                          <summary className="dashcfg-filter-summary">
                                                                            <span className={`dashcfg-pill ${filterEnabled ? 'ok' : 'neutral'}`}>
                                                                              {filterEnabled ? 'Filtro activo' : 'Filtro'}
                                                                            </span>
                                                                          </summary>
                                                                          <div className="dashcfg-filter-body">
                                                                            {(() => {
                                                                              // Resolver tabla/columnas disponibles para el filtro
                                                                              let fTable = '';
                                                                              if (sourceType === 'table') fTable = tableVal || '';
                                                                              if (sourceType === 'field') {
                                                                                const m = dashcfgSelectedSectionCfg.fieldMappings?.[fieldVal] || {};
                                                                                fTable = m?.table || '';
                                                                              }
                                                                              const fCols = schemaReadyForCalc && fTable ? (schemaTables?.[fTable] || []) : [];
                                                                              const prefer = (fCols || []).find((c) => /(^|_)(status|estado|state)(_|$)/i.test(String(c || '')))
                                                                                || (fCols || []).find((c) => /(status|estado|state)/i.test(String(c || '')))
                                                                                || (fCols[0] || '');
                                                                              const fCol = (f0?.column || prefer);
                                                                              const valsKey = fTable && fCol ? dashcfgValuesKey({
                                                                                clientId: (user?.role === 'admin' ? selectedConfigClientId : ''),
                                                                                sectionKey: dashcfgSelectedSectionKey,
                                                                                dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                                table: fTable,
                                                                                column: fCol,
                                                                                period: cfgFormulaPeriod,
                                                                                category: cfgFormulaCategory,
                                                                              }) : '';
                                                                              const valsState = valsKey ? (cfgColumnValues?.[valsKey] || {}) : {};
                                                                              const valsList = Array.isArray(valsState.values) ? valsState.values : [];
                                                                              const loadingVals = Boolean(valsState.loading);
                                                                              const valsErr = valsState.error ? String(valsState.error) : '';

                                                                              const setFilter = (nextCol, nextOp, nextVals) => {
                                                                                const nf = { column: nextCol, operator: nextOp, values: Array.isArray(nextVals) ? nextVals : [] };
                                                                                updateTerm({ filters: [nf] });
                                                                                if (nextCol && fTable) {
                                                                                  dashcfgLoadColumnValues({
                                                                                    sectionKey: dashcfgSelectedSectionKey,
                                                                                    table: fTable,
                                                                                    column: nextCol,
                                                                                    dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                                  });
                                                                                }
                                                                              };

                                                                              const disableFilter = () => updateTerm({ filters: [] });

                                                                              if (!schemaReadyForCalc) {
                                                                                return <div className="dashcfg-field-help">Carga el esquema para habilitar filtros.</div>;
                                                                              }
                                                                              if (!fTable) {
                                                                                return <div className="dashcfg-field-help">Selecciona primero una tabla/columna para poder filtrar.</div>;
                                                                              }

                                                                              return (
                                                                                <>
                                                                                  <div className="dashcfg-filter-row">
                                                                                    <select
                                                                                      className="dashcfg-term-input"
                                                                                      value={fCol || ''}
                                                                                      onChange={(e) => setFilter(e.target.value, filterOp, [])}
                                                                                    >
                                                                                      <option value="">Columna filtro…</option>
                                                                                      {fCols.map((c) => (
                                                                                        <option key={c} value={c}>{c}</option>
                                                                                      ))}
                                                                                    </select>

                                                                                    <select
                                                                                      className="dashcfg-term-op"
                                                                                      value={filterOp}
                                                                                      onChange={(e) => setFilter(fCol, e.target.value, filterVals)}
                                                                                      title="Operador del filtro"
                                                                                    >
                                                                                      <option value="in">IN</option>
                                                                                      <option value="not_in">NOT IN</option>
                                                                                      <option value="equals">=</option>
                                                                                      <option value="not_equals">!=</option>
                                                                                      <option value="contains">contiene</option>
                                                                                    </select>

                                                                                    <button type="button" className="btn-chip" onClick={disableFilter}>
                                                                                      Quitar filtro
                                                                                    </button>
                                                                                  </div>

                                                                                  {fCol && (
                                                                                    <>
                                                                                      <div className="dashcfg-filter-row">
                                                                                        <button
                                                                                          type="button"
                                                                                          className="btn-chip"
                                                                                          onClick={() => dashcfgLoadColumnValues({
                                                                                            sectionKey: dashcfgSelectedSectionKey,
                                                                                            table: fTable,
                                                                                            column: fCol,
                                                                                            dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                                          })}
                                                                                        >
                                                                                          {loadingVals ? 'Cargando valores…' : 'Cargar valores'}
                                                                                        </button>
                                                                                        {valsErr && <span className="dashcfg-hint" style={{ color: '#fca5a5' }}>{valsErr}</span>}
                                                                                      </div>

                                                                                      {filterOp === 'contains' ? (
                                                                                        <input
                                                                                          className="dashcfg-term-input"
                                                                                          value={(filterVals[0] || '')}
                                                                                          onChange={(e) => setFilter(fCol, 'contains', [e.target.value])}
                                                                                          placeholder="Texto a buscar…"
                                                                                        />
                                                                                      ) : (
                                                                                        <>
                                                                                          <select
                                                                                            multiple
                                                                                            className="dashcfg-multiselect"
                                                                                            value={filterVals}
                                                                                            onChange={(e) => {
                                                                                              const v = Array.from(e.target.selectedOptions || []).map((o) => o.value);
                                                                                              setFilter(fCol, filterOp, v);
                                                                                            }}
                                                                                            disabled={loadingVals}
                                                                                            title="Selecciona valores (Ctrl/Cmd o Shift)"
                                                                                          >
                                                                                            {valsList.map((v) => (
                                                                                              <option key={v} value={v}>{v}</option>
                                                                                            ))}
                                                                                          </select>
                                                                                          {!loadingVals && !valsErr && fCol && valsList.length === 0 && (
                                                                                            <div className="dashcfg-field-help">
                                                                                              No se encontraron valores. Prueba a cambiar el <strong>Periodo</strong> a “Últimos 30 días” o revisa que la columna tenga datos.
                                                                                            </div>
                                                                                          )}
                                                                                        </>
                                                                                      )}
                                                                                    </>
                                                                                  )}
                                                                                </>
                                                                              );
                                                                            })()}
                                                                          </div>
                                                                        </details>
                                                                      )}

                                                                      <button type="button" className="btn-chip" onClick={removeTerm} disabled={!schemaReadyForCalc}>
                                                                        Quitar
                                                                      </button>
                                                                    </div>
                                                                  );
                                                                })}
                                                              </div>
                                                            )}
                                                          </div>

                                                          {calcSpec.length === 0 && (
                                                            <div className="dashcfg-field-help">
                                                              Agrega valores con el constructor (arriba). Luego podrás filtrar y probar.
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                    )}

                                                    {/* ======= Paso 2: Filtros ======= */}
                                                    {cfgCalcWizardStep === 1 && (
                                                      <div className="dashcfg-calc-step-grid">
                                                        {availablePanel}
                                                        <div className="dashcfg-calc-step-main">
                                                          {!cfgCalcDrawerAdvanced ? (
                                                            <div className="dashcfg-card">
                                                              <div className="dashcfg-card-title">Filtro global (tipo Excel)</div>
                                                              {(() => {
                                                                const baseTerm = (calcSpec || []).find((t) => (t?.sourceType || '') !== 'number') || null;
                                                                const gfTable = (quickAdd?.table || baseTerm?.table || dashcfgSelectedBaseTable || '').toString();
                                                                const cols = schemaReadyForCalc && gfTable ? (schemaTables?.[gfTable] || []) : [];
                                                                const preferStatus = (cols || []).find((c) => /(^|_)(status|estado|state)(_|$)/i.test(String(c || '')))
                                                                  || (cols || []).find((c) => /(status|estado|state)/i.test(String(c || '')))
                                                                  || '';
                                                                const preferSub = (cols || []).find((c) => /(subestado|sub_status|substatus|sub_state)/i.test(String(c || '')))
                                                                  || '';

                                                                const resolveTermTable = (t) => {
                                                                  const st = String(t?.sourceType || '').toLowerCase();
                                                                  if (st === 'table') return String(t?.table || '');
                                                                  if (st === 'field') {
                                                                    const fk = String(t?.fieldKey || '');
                                                                    const m = dashcfgSelectedSectionCfg.fieldMappings?.[fk] || {};
                                                                    return String(m?.table || '');
                                                                  }
                                                                  return '';
                                                                };

                                                                const currentFilters = Array.isArray(baseTerm?.filters) ? baseTerm.filters : [];
                                                                const filters = (currentFilters.length ? currentFilters : [{ column: preferStatus || '', operator: 'in', values: [] }])
                                                                  .slice(0, 3)
                                                                  .map((f, idx) => ({
                                                                    column: String((f?.column || (idx === 0 ? preferStatus : '')) || ''),
                                                                    operator: String((f?.operator || 'in') || 'in'),
                                                                    values: Array.isArray(f?.values) ? f.values : [],
                                                                  }));

                                                                const applyAllFilters = (nextFilters) => {
                                                                  const cleaned = (Array.isArray(nextFilters) ? nextFilters : [])
                                                                    .filter((f) => f && typeof f === 'object')
                                                                    .slice(0, 5)
                                                                    .map((f) => ({
                                                                      column: String(f.column || '').trim(),
                                                                      operator: String(f.operator || 'in').trim().toLowerCase(),
                                                                      values: Array.isArray(f.values) ? f.values : [],
                                                                    }))
                                                                    .filter((f) => Boolean(f.column));

                                                                  const nextSpec = calcSpec.map((t) => {
                                                                    if (!t || (t?.sourceType || '') === 'number') return t;
                                                                    const tTable = resolveTermTable(t);
                                                                    if (gfTable && tTable && tTable !== gfTable) return t; // evita errores si hay varias tablas
                                                                    return { ...(t || {}), filters: cleaned };
                                                                  });

                                                                  dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, nextSpec, { syncFormula: true });
                                                                };

                                                                const setFilterAt = (idx, patch) => {
                                                                  const cur = filters[idx] || { column: '', operator: 'in', values: [] };
                                                                  const next = [...filters];
                                                                  const nextCol = Object.prototype.hasOwnProperty.call(patch || {}, 'column') ? String(patch.column || '') : cur.column;
                                                                  const nextOp = Object.prototype.hasOwnProperty.call(patch || {}, 'operator') ? String(patch.operator || 'in') : cur.operator;
                                                                  const nextVals = Object.prototype.hasOwnProperty.call(patch || {}, 'values') ? (Array.isArray(patch.values) ? patch.values : []) : cur.values;
                                                                  next[idx] = { column: nextCol, operator: nextOp, values: nextVals };
                                                                  applyAllFilters(next);
                                                                  if (gfTable && nextCol && Object.prototype.hasOwnProperty.call(patch || {}, 'column')) {
                                                                    dashcfgLoadColumnValues({
                                                                      sectionKey: dashcfgSelectedSectionKey,
                                                                      table: gfTable,
                                                                      column: nextCol,
                                                                      dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                    });
                                                                  }
                                                                };

                                                                const removeFilterAt = (idx) => {
                                                                  const next = filters.filter((_, i) => i !== idx);
                                                                  applyAllFilters(next);
                                                                };

                                                                const addCondition = () => {
                                                                  if (filters.length >= 3) return;
                                                                  const used = new Set(filters.map((f) => String(f.column || '')).filter(Boolean));
                                                                  const pick = preferSub && !used.has(preferSub)
                                                                    ? preferSub
                                                                    : (cols || []).find((c) => c && !used.has(String(c))) || '';
                                                                  const next = [...filters, { column: String(pick || ''), operator: 'in', values: [] }];
                                                                  applyAllFilters(next);
                                                                };

                                                                return (
                                                                  <>
                                                                    <div
                                                                      className="dashcfg-dropzone small"
                                                                      onDragOver={(e) => {
                                                                        e.preventDefault();
                                                                        e.currentTarget.classList.add('over');
                                                                      }}
                                                                      onDragLeave={(e) => e.currentTarget.classList.remove('over')}
                                                                      onDrop={(e) => {
                                                                        e.preventDefault();
                                                                        e.currentTarget.classList.remove('over');
                                                                        const p = dashcfgDndGet(e);
                                                                        if (!p) return;
                                                                        const r = dndResolveTableColumn(p);
                                                                        if (!r || !r.column) return;
                                                                        // si hay varias condiciones, rellena la primera vacía; si no, reemplaza la primera
                                                                        const emptyIdx = filters.findIndex((f) => !String(f?.column || '').trim());
                                                                        const targetIdx = emptyIdx >= 0 ? emptyIdx : 0;
                                                                        setFilterAt(targetIdx, { column: r.column, values: [] });
                                                                      }}
                                                                    >
                                                                      <div className="dashcfg-dropzone-title">Filtro</div>
                                                                      <div className="dashcfg-hint">
                                                                        Arrastra una columna desde “Campos disponibles” y suéltala aquí para filtrar.
                                                                      </div>
                                                                    </div>

                                                                    {filters.map((f, idx) => {
                                                                      const col = String(f.column || '').trim();
                                                                      const op = String(f.operator || 'in').toLowerCase();
                                                                      const vals = Array.isArray(f.values) ? f.values : [];

                                                                      const valsKey = col && gfTable ? dashcfgValuesKey({
                                                                        clientId: (user?.role === 'admin' ? selectedConfigClientId : ''),
                                                                        sectionKey: dashcfgSelectedSectionKey,
                                                                        dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                        table: gfTable,
                                                                        column: col,
                                                                        period: cfgFormulaPeriod,
                                                                        category: cfgFormulaCategory,
                                                                      }) : '';
                                                                      const state = valsKey ? (cfgColumnValues?.[valsKey] || {}) : {};
                                                                      const list = Array.isArray(state.values) ? state.values : [];
                                                                      const loading = Boolean(state.loading);
                                                                      const err = state.error ? String(state.error) : '';

                                                                      return (
                                                                        <div key={`${idx}-${col || 'col'}`} className="dashcfg-filter">
                                                                          <div className="dashcfg-filter-row">
                                                                            <span className="dashcfg-pill neutral">{`Condición ${idx + 1}`}</span>
                                                                            <select
                                                                              className="dashcfg-term-input"
                                                                              value={col}
                                                                              onChange={(e) => setFilterAt(idx, { column: e.target.value, values: [] })}
                                                                              disabled={!schemaReadyForCalc || !gfTable}
                                                                            >
                                                                              <option value="">{gfTable ? 'Columna…' : 'Selecciona tabla…'}</option>
                                                                              {cols.map((c) => (
                                                                                <option key={c} value={c}>{c}</option>
                                                                              ))}
                                                                            </select>
                                                                            <select
                                                                              className="dashcfg-term-op"
                                                                              value={op}
                                                                              onChange={(e) => setFilterAt(idx, { operator: e.target.value })}
                                                                              disabled={!schemaReadyForCalc || !col}
                                                                            >
                                                                              <option value="in">IN</option>
                                                                              <option value="not_in">NOT IN</option>
                                                                              <option value="equals">=</option>
                                                                              <option value="not_equals">!=</option>
                                                                              <option value="contains">contiene</option>
                                                                            </select>
                                                                            <button
                                                                              type="button"
                                                                              className="btn-chip"
                                                                              onClick={() => removeFilterAt(idx)}
                                                                              disabled={!schemaReadyForCalc || filters.length <= 1}
                                                                            >
                                                                              Quitar
                                                                            </button>
                                                                          </div>

                                                                          {col ? (
                                                                            <>
                                                                              <div className="dashcfg-filter-row">
                                                                                <button
                                                                                  type="button"
                                                                                  className="btn-chip"
                                                                                  disabled={!schemaReadyForCalc || !col || !gfTable}
                                                                                  onClick={() => dashcfgLoadColumnValues({
                                                                                    sectionKey: dashcfgSelectedSectionKey,
                                                                                    table: gfTable,
                                                                                    column: col,
                                                                                    dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                                  })}
                                                                                >
                                                                                  {loading ? 'Cargando…' : 'Cargar valores'}
                                                                                </button>
                                                                                {err && <span className="dashcfg-hint" style={{ color: '#fca5a5' }}>{err}</span>}
                                                                                {((op === 'in' || op === 'not_in') && vals.length === 0) && (
                                                                                  <span className="dashcfg-hint" style={{ color: '#fde68a' }}>
                                                                                    Selecciona al menos 1 valor.
                                                                                  </span>
                                                                                )}
                                                                              </div>

                                                                              {op === 'contains' ? (
                                                                                <input
                                                                                  className="dashcfg-term-input"
                                                                                  value={(vals[0] || '')}
                                                                                  onChange={(e) => setFilterAt(idx, { values: [e.target.value] })}
                                                                                  placeholder="Texto a buscar…"
                                                                                />
                                                                              ) : (
                                                                                <>
                                                                                  <div className="dashcfg-col-list" aria-label="Valores disponibles">
                                                                                    {list.length === 0 ? (
                                                                                      <div className="dashcfg-field-help">
                                                                                        Pulsa <strong>Cargar valores</strong> para ver opciones de esta columna.
                                                                                      </div>
                                                                                    ) : (
                                                                                      list.map((v) => {
                                                                                        const checked = vals.includes(v);
                                                                                        return (
                                                                                          <label key={v} className={`dashcfg-col-item ${checked ? 'on' : ''}`}>
                                                                                            <input
                                                                                              type="checkbox"
                                                                                              checked={checked}
                                                                                              onChange={(e) => {
                                                                                                const set = new Set(vals);
                                                                                                if (e.target.checked) set.add(v);
                                                                                                else set.delete(v);
                                                                                                setFilterAt(idx, { values: Array.from(set) });
                                                                                              }}
                                                                                            />
                                                                                            <span>{v}</span>
                                                                                          </label>
                                                                                        );
                                                                                      })
                                                                                    )}
                                                                                  </div>
                                                                                  <div className="dashcfg-col-actions">
                                                                                    <button
                                                                                      type="button"
                                                                                      className="btn-chip"
                                                                                      disabled={list.length === 0}
                                                                                      onClick={() => setFilterAt(idx, { values: list.slice(0, 200) })}
                                                                                    >
                                                                                      Seleccionar todo
                                                                                    </button>
                                                                                    <button
                                                                                      type="button"
                                                                                      className="btn-chip"
                                                                                      disabled={vals.length === 0}
                                                                                      onClick={() => setFilterAt(idx, { values: [] })}
                                                                                    >
                                                                                      Limpiar
                                                                                    </button>
                                                                                  </div>
                                                                                </>
                                                                              )}
                                                                            </>
                                                                          ) : (
                                                                            <div className="dashcfg-field-help">Elige una columna para esta condición.</div>
                                                                          )}
                                                                        </div>
                                                                      );
                                                                    })}

                                                                    <div className="dashcfg-col-actions" style={{ marginTop: 10 }}>
                                                                      <button
                                                                        type="button"
                                                                        className="btn-chip"
                                                                        onClick={addCondition}
                                                                        disabled={!schemaReadyForCalc || filters.length >= 3}
                                                                        title={filters.length >= 3 ? 'Máximo 3 condiciones en modo Simple' : 'Añadir otra condición (AND)'}
                                                                      >
                                                                        + Condición
                                                                      </button>
                                                                      <button
                                                                        type="button"
                                                                        className="btn-chip"
                                                                        onClick={() => applyAllFilters([])}
                                                                        disabled={!schemaReadyForCalc}
                                                                      >
                                                                        Quitar filtros
                                                                      </button>
                                                                    </div>
                                                                  </>
                                                                );
                                                              })()}
                                                            </div>
                                                          ) : (
                                                            <div className="dashcfg-empty">
                                                              Estás en modo <strong>Avanzado</strong>. En este modo, los filtros se ajustan por término en “Valores”.
                                                              Si quieres un filtro global tipo Excel, cambia a <strong>Simple</strong>.
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                    )}

                                                    {/* ======= Paso 3: Probar ======= */}
                                                    {cfgCalcWizardStep === 2 && (
                                                      <>
                                                        <div className="dashcfg-step">
                                                          <div className="dashcfg-step-title">Probar</div>
                                                          <div className="dashcfg-step-help">
                                                            Revisa la expresión y pulsa “Probar” para ver el resultado con el periodo/categoría seleccionados.
                                                          </div>
                                                        </div>

                                                        <input
                                                          type="text"
                                                          className="dashcfg-calc-input"
                                                          value={calcEffective}
                                                          disabled={calcSpec.length > 0}
                                                          onChange={(e) => updateSectionConfig(dashcfgSelectedSectionKey, (sec) => {
                                                            const nextCalcs = { ...(sec.fieldCalculations || {}) };
                                                            nextCalcs[fieldKey] = e.target.value;
                                                            const nextSpecs = { ...(sec.fieldCalculationSpecs || {}) };
                                                            nextSpecs[fieldKey] = []; // si editas manualmente, desactiva el constructor
                                                            return { ...sec, fieldCalculations: nextCalcs, fieldCalculationSpecs: nextSpecs };
                                                          })}
                                                          placeholder="Ej: SUM({{talk_time}}) + SUM(otra_tabla.no_atendidas)"
                                                        />
                                                        <div className="dashcfg-field-help">
                                                          Puedes usar <strong>{'{{campo}}'}</strong> (usa el mapeo) o <strong>tabla.columna</strong>.
                                                        </div>
                                                        <div className="dashcfg-formula-preview">
                                                          <div className="dashcfg-formula-label">Expresión</div>
                                                          <code className="dashcfg-formula-code">{String(calcEffective || '').trim() ? calcEffective : '--'}</code>
                                                        </div>

                                                        <div className="dashcfg-calc-actions">
                                                          {calcSpec.length > 0 && (
                                                            <button
                                                              type="button"
                                                              className="btn-chip"
                                                              onClick={() => dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, [], { syncFormula: true })}
                                                            >
                                                              Limpiar
                                                            </button>
                                                          )}

                                                          {calcSpec.length > 0 && (
                                                            <button
                                                              type="button"
                                                              className="btn-chip"
                                                              onClick={() => dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, [], { syncFormula: false })}
                                                              title="Desactiva el constructor y permite editar la fórmula a mano"
                                                            >
                                                              Modo manual
                                                            </button>
                                                          )}

                                                          <button
                                                            type="button"
                                                            className="btn-chip"
                                                            disabled={!String(calcEffective || '').trim()}
                                                            onClick={() => dashcfgPreviewFormula(
                                                              dashcfgSelectedSectionKey,
                                                              fieldKey,
                                                              calcEffective,
                                                              (calcSpec.length > 0 ? calcSpec : null)
                                                            )}
                                                          >
                                                            Probar
                                                          </button>
                                                          {(() => {
                                                            const k = dashcfgPreviewKey(dashcfgSelectedSectionKey, fieldKey);
                                                            const st = cfgFormulaPreview?.[k] || {};
                                                            if (st.loading) return <span className="dashcfg-hint">Calculando…</span>;
                                                            if (st.error) return <span className="dashcfg-hint" style={{ color: '#fca5a5' }}>{st.error}</span>;
                                                            if (st.value == null) return null;
                                                            const debug = st.debug || null;
                                                            const items = Array.isArray(debug?.terms)
                                                              ? debug.terms
                                                              : (Array.isArray(debug?.calls) ? debug.calls : []);
                                                            const anyNoDate = items.some((c) => c?.applied_filters && c.applied_filters.date_filter === false);
                                                            const anyNoType = (dashcfgSelectedSectionKey === 'analytics')
                                                              ? items.some((c) => c?.applied_filters && c.applied_filters.call_type_filter === false)
                                                              : false;
                                                            return (
                                                              <span className="dashcfg-hint">
                                                                Resultado: <strong>{String(st.value)}</strong>
                                                                {(anyNoDate || anyNoType) && (
                                                                  <span>
                                                                    {' '}· Aviso:{' '}
                                                                    {anyNoDate ? 'sin filtro de fecha en alguna tabla' : ''}
                                                                    {anyNoDate && anyNoType ? ' / ' : ''}
                                                                    {anyNoType ? 'sin filtro IN/OUT en alguna tabla' : ''}
                                                                  </span>
                                                                )}
                                                              </span>
                                                            );
                                                          })()}
                                                        </div>

                                                        {(() => {
                                                          const k = dashcfgPreviewKey(dashcfgSelectedSectionKey, fieldKey);
                                                          const st = cfgFormulaPreview?.[k] || {};
                                                          if (st.loading || st.error || !st.debug) return null;
                                                          const debug = st.debug || {};
                                                          const exprPreview = typeof debug.expression === 'string' ? debug.expression : '';
                                                          const sqlPreview = typeof debug.sql_preview === 'string' ? debug.sql_preview : '';
                                                          const sqlPreviewFull = typeof debug.sql_preview_full === 'string' ? debug.sql_preview_full : '';
                                                          const terms = Array.isArray(debug.terms) ? debug.terms : [];
                                                          const humanCalc = (calcSpec.length > 0) ? dashcfgCalcBuildHumanFromSpec(calcSpec) : '';
                                                          return (
                                                            <div className="dashcfg-debug">
                                                              {humanCalc && (
                                                                <details className="dashcfg-details">
                                                                  <summary>Ver cálculo (con filtros)</summary>
                                                                  <div className="dashcfg-details-body">
                                                                    <pre className="dashcfg-sql-pre small">
                                                                      <code className="dashcfg-sql-code">{humanCalc}</code>
                                                                    </pre>
                                                                    <div className="dashcfg-field-help">
                                                                      Esto es una vista legible del builder. Los filtros de periodo/agente se aplican aparte según el contexto.
                                                                    </div>
                                                                  </div>
                                                                </details>
                                                              )}

                                                              {exprPreview && (
                                                                <details className="dashcfg-details">
                                                                  <summary>Ver expresión evaluada</summary>
                                                                  <div className="dashcfg-details-body">
                                                                    <pre className="dashcfg-sql-pre small">
                                                                      <code className="dashcfg-sql-code">{exprPreview}</code>
                                                                    </pre>
                                                                    <div className="dashcfg-field-help">
                                                                      Esta es la operación final aplicada sobre los bloques (<strong>v0</strong>, <strong>v1</strong>, …).
                                                                    </div>
                                                                  </div>
                                                                </details>
                                                              )}

                                                              <details className="dashcfg-details">
                                                                <summary>{sqlPreviewFull ? 'Ver SQL generado (compacto)' : 'Ver SQL generado (preview)'}</summary>
                                                                <div className="dashcfg-details-body">
                                                                  <div className="dashcfg-details-actions">
                                                                    <button
                                                                      type="button"
                                                                      className="btn-chip"
                                                                      disabled={!sqlPreview}
                                                                      onClick={async () => {
                                                                        try {
                                                                          await navigator.clipboard.writeText(sqlPreview || '');
                                                                          showSuccess('SQL copiado al portapapeles');
                                                                        } catch (e) {
                                                                          setConfigError('No se pudo copiar el SQL (permiso del navegador).');
                                                                        }
                                                                      }}
                                                                    >
                                                                      Copiar SQL
                                                                    </button>
                                                                  </div>
                                                                  <pre className="dashcfg-sql-pre">
                                                                    <code className="dashcfg-sql-code">{sqlPreview || 'Pulsa “Probar” para generar el SQL.'}</code>
                                                                  </pre>
                                                                  <div className="dashcfg-field-help">
                                                                    Este SQL es una representación legible (estilo Looker) de lo que se está calculando: cada bloque es un agregado + filtros, y luego se aplica la operación final.
                                                                  </div>
                                                                </div>
                                                              </details>

                                                              {sqlPreviewFull && (
                                                                <details className="dashcfg-details">
                                                                  <summary>Ver SQL generado (completo)</summary>
                                                                  <div className="dashcfg-details-body">
                                                                    <div className="dashcfg-details-actions">
                                                                      <button
                                                                        type="button"
                                                                        className="btn-chip"
                                                                        disabled={!sqlPreviewFull}
                                                                        onClick={async () => {
                                                                          try {
                                                                            await navigator.clipboard.writeText(sqlPreviewFull || '');
                                                                            showSuccess('SQL completo copiado al portapapeles');
                                                                          } catch (e) {
                                                                            setConfigError('No se pudo copiar el SQL (permiso del navegador).');
                                                                          }
                                                                        }}
                                                                      >
                                                                        Copiar SQL completo
                                                                      </button>
                                                                    </div>
                                                                    <pre className="dashcfg-sql-pre">
                                                                      <code className="dashcfg-sql-code">{sqlPreviewFull}</code>
                                                                    </pre>
                                                                  </div>
                                                                </details>
                                                              )}

                                                              {terms.length > 0 && (
                                                                <details className="dashcfg-details">
                                                                  <summary>Ver desglose por bloques</summary>
                                                                  <div className="dashcfg-details-body">
                                                                    <div className="dashcfg-debug-terms">
                                                                      {terms.map((t, i) => {
                                                                        const stype = (typeof t?.sourceType === 'string' ? t.sourceType : '').toLowerCase();
                                                                        const label = (stype === 'number')
                                                                          ? `Número`
                                                                          : `${t?.func || 'AGG'}(${t?.table || 'tabla'}.${t?.column || 'col'})`;
                                                                        const val = (t && t.value != null) ? t.value : null;
                                                                        const applied = t?.applied_filters || {};
                                                                        const termSql = typeof t?.sql_preview === 'string' ? t.sql_preview : '';
                                                                        const tf = Array.isArray(applied?.term_filters) ? applied.term_filters : [];
                                                                        const inf = Array.isArray(applied?.in_filters) ? applied.in_filters : [];
                                                                        const varName = (typeof t?.var === 'string' && t.var) ? t.var : `v${i}`;
                                                                        const rowCount = (t && t.row_count != null) ? Number(t.row_count) : null;
                                                                        const opMap = { in: 'IN', not_in: 'NOT IN', equals: '=', not_equals: '!=', contains: 'contiene' };
                                                                        const tfTxt = tf.slice(0, 2).map((f) => {
                                                                          const col = String(f?.column || '');
                                                                          const op = opMap[String(f?.operator || '').toLowerCase()] || String(f?.operator || '');
                                                                          const vs = Array.isArray(f?.values) ? f.values : [];
                                                                          const shown = vs.slice(0, 4).join(', ');
                                                                          const tail = vs.length > 4 ? ` +${vs.length - 4}…` : '';
                                                                          const rhs = (String(f?.operator || '').toLowerCase() === 'contains')
                                                                            ? (vs[0] || '')
                                                                            : (String(f?.operator || '').toLowerCase() === 'equals' || String(f?.operator || '').toLowerCase() === 'not_equals')
                                                                              ? (vs[0] || '')
                                                                              : `(${shown}${tail})`;
                                                                          return col ? `${col} ${op} ${rhs}` : '';
                                                                        }).filter(Boolean).join(' · ');
                                                                        return (
                                                                          <div key={`${label}-${i}`} className="dashcfg-debug-term">
                                                                            <div className="dashcfg-debug-term-head">
                                                                              <div className="dashcfg-debug-term-title">
                                                                                <span className="dashcfg-pill neutral">{varName}</span>{' '}
                                                                                <strong>{label}</strong>
                                                                              </div>
                                                                              <div className="dashcfg-debug-term-value">
                                                                                {val == null ? '—' : <span>Valor: <strong>{String(val)}</strong></span>}
                                                                              </div>
                                                                            </div>
                                                                            <div className="dashcfg-debug-term-meta">
                                                                              <span className="dashcfg-hint">
                                                                                Filtros:{' '}
                                                                                {applied?.date_filter ? 'fecha' : 'sin fecha'}
                                                                                {' · '}
                                                                                {(dashcfgSelectedSectionKey === 'analytics')
                                                                                  ? (applied?.call_type_filter ? 'IN/OUT' : 'sin IN/OUT')
                                                                                  : '—'}
                                                                                {tf.length ? ` · ${tf.length} filtro(s) de columna` : ''}
                                                                                {tfTxt ? ` · ${tfTxt}` : ''}
                                                                                {inf.length ? ` · ${inf.reduce((acc, x) => acc + (x?.count || 0), 0)} IN(s)` : ''}
                                                                                {(rowCount != null && !Number.isNaN(rowCount)) ? ` · filas: ${rowCount}` : ''}
                                                                              </span>
                                                                            </div>
                                                                            {termSql && (
                                                                              <pre className="dashcfg-sql-pre small">
                                                                                <code className="dashcfg-sql-code">{termSql}</code>
                                                                              </pre>
                                                                            )}
                                                                          </div>
                                                                        );
                                                                      })}
                                                                    </div>
                                                                  </div>
                                                                </details>
                                                              )}
                                                            </div>
                                                          );
                                                        })()}
                                                      </>
                                                    )}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        };

                                        return (
                                          <div className={`dashcfg-explore ${cfgLookerCompact ? 'compact' : ''}`}>
                                            <aside className="dashcfg-explore-list">
                                              <div className="dashcfg-explore-list-head">
                                                <input
                                                  className="dashcfg-looker-search"
                                                  value={cfgLookerFieldSearch}
                                                  onChange={(e) => setCfgLookerFieldSearch(e.target.value)}
                                                  placeholder="Buscar campo…"
                                                />
                                                <div className="dashcfg-explore-actions">
                                                  <button
                                                    type="button"
                                                    className={`btn-chip ${cfgLookerCompact ? 'dashcfg-active' : ''}`}
                                                    title="Reduce el alto de cada campo para ver más elementos sin hacer zoom"
                                                    onClick={() => setCfgLookerCompact((v) => !v)}
                                                  >
                                                    Compacto
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="btn-chip"
                                                    onClick={() => {
                                                      const meta = dashcfgGetSectionMeta(dashcfgSelectedSectionKey);
                                                      const clientFields = meta.clientFields || fields;
                                                      updateSectionConfig(dashcfgSelectedSectionKey, (sec) => {
                                                        const nextFields = { ...(sec.fields || {}) };
                                                        clientFields.forEach((f) => { nextFields[f] = true; });
                                                        return { ...sec, fields: nextFields };
                                                      });
                                                    }}
                                                  >
                                                    Mostrar todo
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="btn-chip"
                                                    onClick={() => {
                                                      const meta = dashcfgGetSectionMeta(dashcfgSelectedSectionKey);
                                                      const clientFields = meta.clientFields || fields;
                                                      updateSectionConfig(dashcfgSelectedSectionKey, (sec) => {
                                                        const nextFields = { ...(sec.fields || {}) };
                                                        clientFields.forEach((f) => { nextFields[f] = false; });
                                                        return { ...sec, fields: nextFields };
                                                      });
                                                    }}
                                                  >
                                                    Ocultar todo
                                                  </button>
                                                </div>
                                              </div>

                                              <div className="dashcfg-explore-items">
                                                {list.map((fieldKey) => {
                                                  const label = dashcfgGetFieldLabel(dashcfgSelectedSectionKey, fieldKey);
                                                  const isDataField = dashcfgIsDataField(dashcfgSelectedSectionKey, fieldKey);
                                                  const isOn = (dashcfgSelectedSectionCfg.fields?.[fieldKey] ?? true);
                                                  const m = dashcfgSelectedSectionCfg.fieldMappings?.[fieldKey];
                                                  const mappingOk = isDataField ? Boolean(dashcfgSelectedBaseTable && (m?.column || '')) : true;
                                                  const calc = dashcfgSelectedSectionCfg.fieldCalculations?.[fieldKey] || '';
                                                  const specRaw = dashcfgSelectedSectionCfg.fieldCalculationSpecs?.[fieldKey];
                                                  const calcSpec = dashcfgCalcNormalizeSpec(Array.isArray(specRaw) ? specRaw : []);
                                                  const calcOk = !isDataField ? Boolean(calcSpec.length > 0 || String(calc || '').trim()) : true;
                                                  const pills = [];
                                                  pills.push(isDataField ? 'Dato' : 'Calculado');
                                                  if (isDataField && !mappingOk) pills.push('Sin fuente');
                                                  if (!isDataField && !calcOk) pills.push('Sin cálculo');
                                                  if (!isOn) pills.push('Oculto');
                                                  return (
                                                    <button
                                                      key={fieldKey}
                                                      type="button"
                                                      className={`dashcfg-explore-item ${selected === fieldKey ? 'active' : ''}`}
                                                      onClick={() => setCfgLookerSelectedFieldKey(fieldKey)}
                                                    >
                                                      <div className="dashcfg-explore-item-title">{label}</div>
                                                      <div className="dashcfg-explore-item-key">{fieldKey}</div>
                                                      <div className="dashcfg-explore-item-meta">
                                                        {pills.slice(0, 3).map((p) => (
                                                          <span key={p} className={`dashcfg-pill ${p === 'Sin fuente' || p === 'Sin cálculo' ? 'warn' : (p === 'Oculto' ? 'off' : 'neutral')}`}>
                                                            {p}
                                                          </span>
                                                        ))}
                                                      </div>
                                                    </button>
                                                  );
                                                })}
                                              </div>
                                            </aside>

                                            <div className="dashcfg-explore-editor">
                                              {selected ? renderFieldEditor(selected) : (
                                                <div className="dashcfg-empty">
                                                  Selecciona un campo a la izquierda.
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })()}

                                      <div className="dashcfg-looker-nav">
                                        <button
                                          type="button"
                                          className="dashcfg-btn secondary"
                                          onClick={() => setCfgLookerStep(2)}
                                        >
                                          Atrás
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </section>
                              </div>
                            ) : (
                              <div className="dashcfg-layout">
                                <aside className="dashcfg-sidebar">
                                  <div className="dashcfg-sidebar-title">Secciones</div>
                                  <div className="dashcfg-section-list">
                                    {dashcfgSectionKeys.map((sectionKey) => {
                                      const secCfg = dashcfgGetSectionCfg(sectionKey);
                                      const meta = dashcfgGetSectionMeta(sectionKey);
                                      const fields = dashcfgGetSectionCatalogFields(sectionKey);
                                      const clientFields = meta.clientFields || fields;
                                      const visibleCount = clientFields.filter((f) => (secCfg.fields?.[f] ?? true)).length;
                                      const missing = dashcfgGetMissingRequiredMappings(sectionKey, secCfg);
                                      return (
                                        <button
                                          key={sectionKey}
                                          type="button"
                                          className={`dashcfg-section-item ${dashcfgSelectedSectionKey === sectionKey ? 'active' : ''}`}
                                          onClick={() => setCfgSelectedSection(sectionKey)}
                                        >
                                          <div className="dashcfg-section-item-top">
                                            <div className="dashcfg-section-item-name">{dashcfgGetSectionLabel(sectionKey)}</div>
                                            <span className={`dashcfg-pill ${secCfg.enabled ? 'on' : 'off'}`}>
                                              {secCfg.enabled ? 'Activa' : 'Off'}
                                            </span>
                                          </div>
                                          <div className="dashcfg-section-item-meta">
                                            {clientFields.length > 0 ? (
                                              <span className="dashcfg-pill neutral">{visibleCount}/{clientFields.length} visibles</span>
                                            ) : (
                                              <span className="dashcfg-pill neutral">Sin campos</span>
                                            )}
                                            {missing.length > 0 && (
                                              <span className="dashcfg-pill warn">{missing.length} mapeos</span>
                                            )}
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>

                                  <div className="dashcfg-card">
                                    <div className="dashcfg-card-title">Nueva sección</div>
                                    <input
                                      value={newSectionName}
                                      onChange={(e) => setNewSectionName(e.target.value)}
                                      placeholder="Nombre (ej: ventas_kpis)"
                                    />
                                    <input
                                      value={newSectionFields}
                                      onChange={(e) => setNewSectionFields(e.target.value)}
                                      placeholder="Campos (coma): total, completadas, …"
                                    />
                                    <button type="button" className="dashcfg-btn primary" onClick={addCustomSection}>
                                      Agregar sección
                                    </button>
                                    <small className="dashcfg-hint">
                                      Tip: usa nombres sin espacios. Luego podrás mapear columnas.
                                    </small>
                                  </div>

                                  {user?.role === 'admin' && (
                                    <div className="dashcfg-card">
                                      <div className="dashcfg-card-title">Copiar desde otro cliente</div>
                                      <select
                                        value={cfgCopyFromClientId}
                                        onChange={(e) => setCfgCopyFromClientId(e.target.value)}
                                      >
                                        <option value="">Selecciona cliente origen…</option>
                                        {clients
                                          .filter((c) => c.id !== selectedConfigClientId)
                                          .map((c) => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                          ))}
                                      </select>
                                      <button
                                        type="button"
                                        className="dashcfg-btn secondary"
                                        onClick={dashcfgCopyFromClient}
                                        disabled={!cfgCopyFromClientId}
                                      >
                                        Copiar configuración
                                      </button>
                                      <small className="dashcfg-hint">
                                        Copia la configuración al editor (no guarda hasta que pulses Guardar).
                                      </small>
                                    </div>
                                  )}

                                  <div className="dashcfg-card">
                                    <div className="dashcfg-card-title">Plantillas</div>
                                    <select value={cfgTemplate} onChange={(e) => setCfgTemplate(e.target.value)}>
                                      <option value="default">Por defecto</option>
                                      <option value="analytics_only">Solo analíticas</option>
                                      <option value="conversations_only">Solo conversaciones</option>
                                      <option value="minimal">Minimal</option>
                                    </select>
                                    <button
                                      type="button"
                                      className="dashcfg-btn secondary"
                                      onClick={() => dashcfgApplyTemplate(cfgTemplate)}
                                    >
                                      Aplicar plantilla
                                    </button>
                                  </div>
                                </aside>

                                <section className="dashcfg-panel">
                                  <div className="dashcfg-panel-head">
                                    <div>
                                      <h3 className="dashcfg-panel-title">{dashcfgGetSectionLabel(dashcfgSelectedSectionKey)}</h3>
                                      <p className="dashcfg-muted">{dashcfgSelectedMeta?.description || ''}</p>
                                    </div>

                                    <label className="dashcfg-switch">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(dashcfgSelectedSectionCfg.enabled)}
                                        onChange={(e) => updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({ ...sec, enabled: e.target.checked }))}
                                      />
                                      <span>Sección visible</span>
                                    </label>
                                  </div>

                                  {dashcfgSelectedMissingRequired.length > 0 && (
                                    <div className="dashcfg-alert warn">
                                      <strong>Faltan mapeos requeridos:</strong>{' '}
                                      {dashcfgSelectedMissingRequired.map((f) => dashcfgGetFieldLabel(dashcfgSelectedSectionKey, f)).join(', ')}
                                    </div>
                                  )}

                                  <div className="dashcfg-panel-grid">
                                    <div className="dashcfg-field">
                                      <label>Origen de datos</label>
                                      <select
                                        value={dashcfgSelectedSectionCfg.dataSource || 'main'}
                                        onChange={(e) => updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({ ...sec, dataSource: e.target.value }))}
                                      >
                                        {(availableDataSources && availableDataSources.length > 0
                                          ? availableDataSources
                                          : [{ key: 'main', label: 'Principal (ambtu_platform)' }]
                                        ).map((ds) => (
                                          <option key={ds.key} value={ds.key}>
                                            {ds.label || ds.key}
                                          </option>
                                        ))}
                                      </select>
                                      <small className="dashcfg-hint">
                                        {schemaLoading && schemaDataSource === (dashcfgSelectedSectionCfg.dataSource || 'main')
                                          ? 'Cargando esquema…'
                                          : 'Selecciona la base/datasource para esta sección.'}
                                      </small>
                                    </div>

                                    <div className="dashcfg-field">
                                      <label>Tabla / Dataset</label>
                                      <select
                                        value={dashcfgSelectedBaseTable || ''}
                                        onChange={(e) => dashcfgSetSectionBaseTable(dashcfgSelectedSectionKey, e.target.value)}
                                        disabled={!cfgShowAdvanced || schemaLoading}
                                      >
                                        <option value="">Selecciona tabla…</option>
                                        {Object.keys(schemaTables || {}).map((tbl) => (
                                          <option key={tbl} value={tbl}>{tbl}</option>
                                        ))}
                                      </select>
                                      <small className="dashcfg-hint">
                                        {schemaLoading
                                          ? 'Cargando tablas disponibles…'
                                          : Object.keys(schemaTables || {}).length === 0
                                            ? 'No hay tablas disponibles. Verifica la conexión a la base de datos.'
                                            : 'Recomendado: usa 1 tabla por sección (mejor rendimiento y menos errores).'}
                                      </small>
                                    </div>

                                    <div className="dashcfg-inline-actions">
                                      <button
                                        type="button"
                                        className="btn-chip"
                                        onClick={() => {
                                          const meta = dashcfgGetSectionMeta(dashcfgSelectedSectionKey);
                                          const clientFields = meta.clientFields || dashcfgSelectedFieldsAll;
                                          updateSectionConfig(dashcfgSelectedSectionKey, (sec) => {
                                            const nextFields = { ...(sec.fields || {}) };
                                            clientFields.forEach((f) => { nextFields[f] = true; });
                                            return { ...sec, fields: nextFields };
                                          });
                                        }}
                                      >
                                        Mostrar todo
                                      </button>
                                      <button
                                        type="button"
                                        className="btn-chip"
                                        onClick={() => {
                                          const meta = dashcfgGetSectionMeta(dashcfgSelectedSectionKey);
                                          const clientFields = meta.clientFields || dashcfgSelectedFieldsAll;
                                          updateSectionConfig(dashcfgSelectedSectionKey, (sec) => {
                                            const nextFields = { ...(sec.fields || {}) };
                                            clientFields.forEach((f) => { nextFields[f] = false; });
                                            return { ...sec, fields: nextFields };
                                          });
                                        }}
                                      >
                                        Ocultar todo
                                      </button>
                                    </div>
                                  </div>

                                  <div className="dashcfg-group">
                                    <div className="dashcfg-group-title">Lo que ve el cliente</div>
                                    {(dashcfgSelectedMeta.clientFields || dashcfgSelectedFieldsAll).length === 0 ? (
                                      <div className="dashcfg-empty">
                                        Esta sección no tiene campos configurables. Solo puedes activarla/desactivarla.
                                      </div>
                                    ) : (
                                      <div className="dashcfg-field-list">
                                        {(dashcfgSelectedMeta.clientFields || dashcfgSelectedFieldsAll)
                                          .filter((fieldKey) => {
                                            const q = (cfgSearch || '').trim().toLowerCase();
                                            if (!q) return true;
                                            const label = dashcfgGetFieldLabel(dashcfgSelectedSectionKey, fieldKey);
                                            const help = dashcfgGetFieldHelp(dashcfgSelectedSectionKey, fieldKey);
                                            const m = dashcfgSelectedSectionCfg.fieldMappings?.[fieldKey];
                                            const c = dashcfgSelectedSectionCfg.fieldCalculations?.[fieldKey] || '';
                                            const hay = `${fieldKey} ${label} ${help} ${m?.table || ''} ${m?.column || ''} ${c}`.toLowerCase();
                                            return hay.includes(q);
                                          })
                                          .map((fieldKey) => {
                                            const label = dashcfgGetFieldLabel(dashcfgSelectedSectionKey, fieldKey);
                                            const help = dashcfgGetFieldHelp(dashcfgSelectedSectionKey, fieldKey);
                                            const calc = dashcfgSelectedSectionCfg.fieldCalculations?.[fieldKey] || '';
                                            const specRaw = dashcfgSelectedSectionCfg.fieldCalculationSpecs?.[fieldKey];
                                            const calcSpec = dashcfgCalcNormalizeSpec(Array.isArray(specRaw) ? specRaw : []);
                                            const calcFromSpec = calcSpec.length > 0 ? dashcfgCalcBuildFormulaFromSpec(calcSpec) : '';
                                            const calcEffective = (calcSpec.length > 0 ? (calcFromSpec || '') : (calc || ''));
                                            const isDataField = (dashcfgSelectedMeta?.dataFields || []).includes(fieldKey);
                                            const isOn = (dashcfgSelectedSectionCfg.fields?.[fieldKey] ?? true);
                                            const schemaReadyForCalc = (
                                              !schemaLoading
                                              && (schemaDataSource === (dashcfgSelectedSectionCfg.dataSource || 'main'))
                                            );
                                            const calcTables = schemaReadyForCalc ? Object.keys(schemaTables || {}) : [];
                                            const mappedFieldKeys = (dashcfgSelectedMeta?.dataFields || [])
                                              .filter((k) => {
                                                const m = dashcfgSelectedSectionCfg.fieldMappings?.[k];
                                                return Boolean(m && m.table && m.column);
                                              });
                                            const calcSafeId = `${dashcfgSelectedSectionKey}-${fieldKey}`.replace(/[^a-zA-Z0-9_-]/g, '_');
                                            const calcTableListId = `dashcfg-tables-${calcSafeId}`;
                                            const selectedTermIdxs = dashcfgGetSelectedTermIdxs(dashcfgSelectedSectionKey, fieldKey);
                                            const selectedTermSet = new Set(selectedTermIdxs);
                                            const quickAdd = dashcfgGetQuickAddState(dashcfgSelectedSectionKey, fieldKey, dashcfgSelectedBaseTable || '');
                                            const quickCols = schemaReadyForCalc ? (schemaTables?.[quickAdd.table] || []) : [];
                                            const calcRowKey = dashcfgCalcKey(dashcfgSelectedSectionKey, fieldKey);
                                            const calcExpanded = (cfgCalcExpandedKey === calcRowKey);
                                            const calcTermCount = Array.isArray(calcSpec) ? calcSpec.length : 0;
                                            const calcFilterCount = (Array.isArray(calcSpec) ? calcSpec : [])
                                              .reduce((acc, t) => acc + (Array.isArray(t?.filters) ? t.filters.length : 0), 0);
                                            return (
                                              <div key={fieldKey} className={`dashcfg-field-row ${isOn ? '' : 'off'}`}>
                                                <div className="dashcfg-field-main">
                                                  <label className="dashcfg-check">
                                                    <input
                                                      type="checkbox"
                                                      checked={Boolean(isOn)}
                                                      onChange={(e) => updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
                                                        ...sec,
                                                        fields: { ...(sec.fields || {}), [fieldKey]: e.target.checked }
                                                      }))}
                                                    />
                                                    <span className="dashcfg-field-name">{label}</span>
                                                  </label>
                                                  {help && <div className="dashcfg-field-help">{help}</div>}
                                                  {cfgShowAdvanced && !isDataField && (
                                                    <div className="dashcfg-calc">
                                                      <div className="dashcfg-calc-head">
                                                        <div className="dashcfg-calc-head-left">
                                                          <span className="dashcfg-pill neutral">Cálculo del campo</span>
                                                          {calcTermCount > 0 && <span className="dashcfg-pill ok">{calcTermCount} términos</span>}
                                                          {calcFilterCount > 0 && <span className="dashcfg-pill warn">{calcFilterCount} filtros</span>}
                                                          {calcTermCount === 0 && String(calc || '').trim() && <span className="dashcfg-pill neutral">Manual</span>}
                                                        </div>
                                                        <div className="dashcfg-calc-head-right">
                                                          <button
                                                            type="button"
                                                            className="btn-chip"
                                                            onClick={() => setCfgCalcExpandedKey(calcExpanded ? '' : calcRowKey)}
                                                          >
                                                            {calcExpanded ? 'Cerrar' : 'Editar'}
                                                          </button>
                                                        </div>
                                                      </div>

                                                      <div className="dashcfg-calc-summary">
                                                        <code className="dashcfg-calc-summary-code">
                                                          {String(calcEffective || '').trim()
                                                            ? calcEffective
                                                            : 'Sin cálculo configurado (usa la lógica estándar del sistema)'}
                                                        </code>
                                                      </div>

                                                      {calcExpanded && createPortal(
                                                        <div
                                                          className="dashcfg-drawer-overlay"
                                                          onClick={() => setCfgCalcExpandedKey('')}
                                                        >
                                                          <div
                                                            className="dashcfg-drawer"
                                                            onClick={(e) => e.stopPropagation()}
                                                          >
                                                            <div className="dashcfg-drawer-head">
                                                              <div>
                                                                <div className="dashcfg-drawer-title">{label}</div>
                                                                <div className="dashcfg-drawer-subtitle">
                                                                  {dashcfgGetSectionLabel(dashcfgSelectedSectionKey)} · {fieldKey}
                                                                </div>
                                                              </div>
                                                              <button
                                                                type="button"
                                                                className="dashcfg-drawer-close"
                                                                onClick={() => setCfgCalcExpandedKey('')}
                                                                title="Cerrar"
                                                              >
                                                                ×
                                                              </button>
                                                            </div>

                                                            <div className="dashcfg-drawer-body">
                                                              <div className="dashcfg-drawer-toolbar">
                                                                <span className="dashcfg-pill neutral">Modo</span>
                                                                <button
                                                                  type="button"
                                                                  className={`btn-chip ${!cfgCalcDrawerAdvanced ? 'dashcfg-active' : ''}`}
                                                                  onClick={() => setCfgCalcDrawerAdvanced(false)}
                                                                >
                                                                  Simple
                                                                </button>
                                                                <button
                                                                  type="button"
                                                                  className={`btn-chip ${cfgCalcDrawerAdvanced ? 'dashcfg-active' : ''}`}
                                                                  onClick={() => setCfgCalcDrawerAdvanced(true)}
                                                                >
                                                                  Avanzado
                                                                </button>
                                                              </div>

                                                              <div className="dashcfg-wizard-tabs">
                                                                <button
                                                                  type="button"
                                                                  className={`dashcfg-wizard-tab ${cfgCalcWizardStep === 0 ? 'active' : ''}`}
                                                                  onClick={() => setCfgCalcWizardStep(0)}
                                                                >
                                                                  1. Valores
                                                                </button>
                                                                <button
                                                                  type="button"
                                                                  className={`dashcfg-wizard-tab ${cfgCalcWizardStep === 1 ? 'active' : ''}`}
                                                                  onClick={() => setCfgCalcWizardStep(1)}
                                                                  disabled={calcSpec.length === 0}
                                                                  title={calcSpec.length === 0 ? 'Agrega al menos un valor primero' : ''}
                                                                >
                                                                  2. Filtros
                                                                </button>
                                                                <button
                                                                  type="button"
                                                                  className={`dashcfg-wizard-tab ${cfgCalcWizardStep === 2 ? 'active' : ''}`}
                                                                  onClick={() => setCfgCalcWizardStep(2)}
                                                                  disabled={calcSpec.length === 0}
                                                                  title={calcSpec.length === 0 ? 'Agrega al menos un valor primero' : ''}
                                                                >
                                                                  3. Probar
                                                                </button>
                                                              </div>

                                                              {cfgCalcWizardStep === 0 && (
                                                                <div className="dashcfg-step">
                                                                  <div className="dashcfg-step-title">Valores</div>
                                                                  <div className="dashcfg-step-help">
                                                                    Como una tabla dinámica: selecciona tabla/columnas y la función (SUM/COUNT/etc.).
                                                                  </div>
                                                                </div>
                                                              )}
                                                              {cfgCalcWizardStep === 0 && (
                                                                <>
                                                                  {!schemaReadyForCalc && (
                                                                    <div className="dashcfg-field-help">
                                                                      Cargando/actualizando el esquema de la base seleccionada… (necesario para listar tablas/columnas)
                                                                    </div>
                                                                  )}

                                                                  <div className="dashcfg-builder">
                                                                    <div className="dashcfg-builder-head">
                                                                      <div className="dashcfg-builder-title">Constructor visual</div>
                                                                      <div className="dashcfg-hint">
                                                                        Tip: selecciona varias columnas para generar sumas rápidamente, y usa Agrupar para (A+B)/C.
                                                                      </div>
                                                                    </div>

                                                                    <div className="dashcfg-quickadd">
                                                                      <div className="dashcfg-quickadd-row">
                                                                        <span className="dashcfg-pill neutral">Añadir valores</span>

                                                                        <select
                                                                          className="dashcfg-term-func"
                                                                          value={quickAdd.func}
                                                                          onChange={(e) => {
                                                                            const v = e.target.value;
                                                                            dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { func: v });
                                                                            // Modo Simple: aplicar a todos los términos (reduce confusión: SUM vs COUNT)
                                                                            if (!cfgCalcDrawerAdvanced && Array.isArray(calcSpec) && calcSpec.length > 0) {
                                                                              const fn = (typeof v === 'string' ? v.toUpperCase() : 'COUNT');
                                                                              const safeFn = DASHCFG_CALC_FUNCS.includes(fn) ? fn : 'COUNT';
                                                                              const nextSpec = calcSpec.map((t) => {
                                                                                if (!t || (t.sourceType || '') === 'number') return t;
                                                                                return { ...(t || {}), func: safeFn };
                                                                              });
                                                                              dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, nextSpec, { syncFormula: true });
                                                                            }
                                                                          }}
                                                                          disabled={!schemaReadyForCalc}
                                                                          title="Función a aplicar"
                                                                        >
                                                                          {DASHCFG_CALC_FUNCS.map((f) => (
                                                                            <option key={f} value={f}>
                                                                              {f === 'COUNT_DISTINCT' ? 'COUNT_DISTINCT (únicos)' : f}
                                                                            </option>
                                                                          ))}
                                                                        </select>

                                                                        <select
                                                                          className="dashcfg-term-op"
                                                                          value={quickAdd.op}
                                                                          onChange={(e) => {
                                                                            const v = e.target.value;
                                                                            dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { op: v });
                                                                            // Modo Simple: aplicar la operación a todos los términos (A / B, A - B, etc.)
                                                                            if (!cfgCalcDrawerAdvanced && Array.isArray(calcSpec) && calcSpec.length > 1) {
                                                                              const op = DASHCFG_CALC_OPS.includes(v) ? v : '+';
                                                                              const nextSpec = calcSpec.map((t, idx) => {
                                                                                if (!t) return t;
                                                                                if (idx === 0) return { ...(t || {}), op: null };
                                                                                return { ...(t || {}), op };
                                                                              });
                                                                              dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, nextSpec, { syncFormula: true });
                                                                            }
                                                                          }}
                                                                          disabled={!schemaReadyForCalc}
                                                                          title={!cfgCalcDrawerAdvanced ? 'Operación entre valores (Simple)' : 'Operación entre columnas'}
                                                                        >
                                                                          {DASHCFG_CALC_OPS.map((o) => (
                                                                            <option key={o} value={o}>{o}</option>
                                                                          ))}
                                                                        </select>

                                                                        {cfgCalcDrawerAdvanced && (
                                                                          <>
                                                                            <label className="dashcfg-quickadd-wrap" title="Agrupa automáticamente las columnas añadidas con paréntesis">
                                                                              <input
                                                                                type="checkbox"
                                                                                checked={Boolean(quickAdd.wrap)}
                                                                                onChange={(e) => dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { wrap: e.target.checked })}
                                                                                disabled={!schemaReadyForCalc}
                                                                              />
                                                                              <span>Agrupar ( )</span>
                                                                            </label>
                                                                          </>
                                                                        )}

                                                                        <input
                                                                          className="dashcfg-term-input"
                                                                          list={calcTableListId}
                                                                          value={quickAdd.table}
                                                                          onChange={(e) => {
                                                                            const t = e.target.value;
                                                                            dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { table: t, columns: [] });
                                                                          }}
                                                                          placeholder="Tabla…"
                                                                          disabled={!schemaReadyForCalc}
                                                                        />

                                                                        <div className="dashcfg-col-picker">
                                                                          <input
                                                                            className="dashcfg-term-input"
                                                                            value={quickAdd.search}
                                                                            onChange={(e) => dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { search: e.target.value })}
                                                                            placeholder="Buscar columna…"
                                                                            disabled={!schemaReadyForCalc || !quickAdd.table}
                                                                          />

                                                                          <div className="dashcfg-col-list" aria-label="Columnas disponibles">
                                                                            {quickCols
                                                                              .filter((c) => {
                                                                                const q = String(quickAdd.search || '').trim().toLowerCase();
                                                                                if (!q) return true;
                                                                                return String(c || '').toLowerCase().includes(q);
                                                                              })
                                                                              .slice(0, 400)
                                                                              .map((c) => {
                                                                                const checked = (quickAdd.columns || []).includes(c);
                                                                                return (
                                                                                  <label key={c} className={`dashcfg-col-item ${checked ? 'on' : ''}`}>
                                                                                    <input
                                                                                      type="checkbox"
                                                                                      checked={checked}
                                                                                      onChange={(e) => {
                                                                                        const set = new Set(quickAdd.columns || []);
                                                                                        if (e.target.checked) set.add(c);
                                                                                        else set.delete(c);
                                                                                        dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { columns: Array.from(set) });
                                                                                      }}
                                                                                    />
                                                                                    <span>{c}</span>
                                                                                  </label>
                                                                                );
                                                                              })}
                                                                          </div>

                                                                          <div className="dashcfg-col-actions">
                                                                            <button
                                                                              type="button"
                                                                              className="btn-chip"
                                                                              disabled={!schemaReadyForCalc || !quickAdd.table}
                                                                              onClick={() => {
                                                                                const q = String(quickAdd.search || '').trim().toLowerCase();
                                                                                const cols = quickCols
                                                                                  .filter((c) => !q || String(c || '').toLowerCase().includes(q))
                                                                                  .slice(0, 400);
                                                                                dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { columns: cols });
                                                                              }}
                                                                            >
                                                                              Seleccionar todo
                                                                            </button>
                                                                            <button
                                                                              type="button"
                                                                              className="btn-chip"
                                                                              disabled={(quickAdd.columns || []).length === 0}
                                                                              onClick={() => dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { columns: [] })}
                                                                            >
                                                                              Limpiar
                                                                            </button>
                                                                          </div>
                                                                        </div>

                                                                        <button
                                                                          type="button"
                                                                          className="btn-chip"
                                                                          disabled={!schemaReadyForCalc || !quickAdd.table || (quickAdd.columns || []).length === 0}
                                                                          onClick={() => {
                                                                            const base = dashcfgCalcNormalizeSpec(calcSpec);
                                                                            const wasEmpty = base.length === 0;
                                                                            const cols = (quickAdd.columns || []).filter(Boolean);
                                                                            if (!quickAdd.table || cols.length === 0) return;

                                                                            const opBase = DASHCFG_CALC_OPS.includes(quickAdd.op) ? quickAdd.op : '+';
                                                                            const op = opBase;
                                                                            const func = (typeof quickAdd.func === 'string' ? quickAdd.func.toUpperCase() : 'COUNT');
                                                                            const safeFunc = DASHCFG_CALC_FUNCS.includes(func) ? func : 'COUNT';
                                                                            const autoFunc = (fn, hint) => {
                                                                              const f = String(fn || 'COUNT').toUpperCase();
                                                                              const h = String(hint || '').toLowerCase();
                                                                              const looksText = /(status|estado|state|subestado|sub_state|tipo|category|categoria|nombre|name|result|outcome)/i.test(h);
                                                                              if ((f === 'SUM' || f === 'AVG') && looksText) return 'COUNT';
                                                                              return f;
                                                                            };

                                                                            const startIdx = base.length;
                                                                            const next = [...base];
                                                                            // Agregar todas las columnas seleccionadas con el operador configurado
                                                                            cols.forEach((col, colIdx) => {
                                                                              // El primer término del lote no tiene operador (o null si es el primer término de toda la fórmula)
                                                                              // Los términos subsecuentes usan el operador configurado
                                                                              const termOp = (startIdx === 0 && colIdx === 0) ? null : op;
                                                                              next.push({
                                                                                op: termOp,
                                                                                func: autoFunc(safeFunc, col),
                                                                                sourceType: 'table',
                                                                                table: quickAdd.table,
                                                                                column: col,
                                                                                fieldKey: '',
                                                                                number: '',
                                                                                openParen: 0,
                                                                                closeParen: 0,
                                                                              });
                                                                            });

                                                                            const wrap = cfgCalcDrawerAdvanced ? Boolean(quickAdd.wrap) : false;
                                                                            if (wrap && cols.length > 1) {
                                                                              const firstIdx = startIdx;
                                                                              const lastIdx = next.length - 1;
                                                                              const first = next[firstIdx] || {};
                                                                              const last = next[lastIdx] || {};
                                                                              first.openParen = Math.min((parseInt(first.openParen ?? 0, 10) || 0) + 1, 3);
                                                                              last.closeParen = Math.min((parseInt(last.closeParen ?? 0, 10) || 0) + 1, 3);
                                                                              next[firstIdx] = first;
                                                                              next[lastIdx] = last;
                                                                            }

                                                                            dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, next, { syncFormula: true });
                                                                            dashcfgSetQuickAddState(dashcfgSelectedSectionKey, fieldKey, { columns: [] });
                                                                            if (!cfgCalcDrawerAdvanced && wasEmpty) {
                                                                              // Flujo estilo tabla dinámica: después de elegir valores, ir a filtros
                                                                              setCfgCalcWizardStep(1);
                                                                            }
                                                                          }}
                                                                        >
                                                                          Añadir
                                                                        </button>
                                                                      </div>
                                                                      <small className="dashcfg-hint">
                                                                        Esto crea términos automáticamente (uno por columna). Luego puedes cambiar +/−/×/÷ y agrupar.
                                                                      </small>
                                                                    </div>

                                                                    {calcSpec.length > 0 && (
                                                                      <div className="dashcfg-pivot-grid">
                                                                        <div className="dashcfg-pivot-block">
                                                                          <div className="dashcfg-pivot-title">Valores seleccionados</div>
                                                                          <div className="dashcfg-chip-list">
                                                                            {calcSpec.map((t, i) => {
                                                                              const open = '('.repeat(Math.max(0, parseInt(t?.openParen ?? 0, 10) || 0));
                                                                              const close = ')'.repeat(Math.max(0, parseInt(t?.closeParen ?? 0, 10) || 0));
                                                                              const opTxt = (i === 0 ? '=' : (t?.op || '+'));
                                                                              let src = '';
                                                                              if ((t?.sourceType || '') === 'number') {
                                                                                src = String(t?.number ?? '0');
                                                                              } else if ((t?.sourceType || '') === 'field') {
                                                                                src = `{{${t?.fieldKey || ''}}}`;
                                                                              } else {
                                                                                src = `${t?.table || ''}.${t?.column || ''}`;
                                                                              }
                                                                              const fn = (t?.func || 'SUM');
                                                                              const fc = Array.isArray(t?.filters) ? t.filters.length : 0;
                                                                              const f0 = (Array.isArray(t?.filters) && t.filters[0]) ? t.filters[0] : null;
                                                                              const fVals = Array.isArray(f0?.values) ? f0.values : [];
                                                                              const fTitle = (f0 && f0.column)
                                                                                ? `Filtro: ${String(f0.column)} ${String(f0.operator || '')} ${fVals.slice(0, 8).join(', ')}${fVals.length > 8 ? '…' : ''}`
                                                                                : '';
                                                                              return (
                                                                                <span key={i} className="dashcfg-chip">
                                                                                  <span className="dashcfg-chip-op">{opTxt}</span>
                                                                                  <span className="dashcfg-chip-body">
                                                                                    {open}{fn}({src}){close}
                                                                                  </span>
                                                                                  {fc > 0 && <span className="dashcfg-chip-tag" title={fTitle || 'Filtro aplicado'}>Filtro</span>}
                                                                                  <button
                                                                                    type="button"
                                                                                    className="dashcfg-chip-x"
                                                                                    title="Quitar este valor"
                                                                                    onClick={() => {
                                                                                      const next = calcSpec.filter((_, idx2) => idx2 !== i);
                                                                                      dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, next, { syncFormula: true });
                                                                                    }}
                                                                                  >
                                                                                    ×
                                                                                  </button>
                                                                                </span>
                                                                              );
                                                                            })}
                                                                          </div>
                                                                          <small className="dashcfg-hint">
                                                                            Si necesitas una operación especial, cambia a <strong>Avanzado</strong>.
                                                                          </small>
                                                                        </div>

                                                                        {false && (
                                                                          <div className="dashcfg-pivot-block">
                                                                            <div className="dashcfg-pivot-title">Filtro (filas)</div>
                                                                            {(() => {
                                                                              const baseTerm = (calcSpec || []).find((t) => (t?.sourceType || '') !== 'number') || null;
                                                                              const current = (baseTerm?.filters && baseTerm.filters[0]) ? baseTerm.filters[0] : { column: '', operator: 'in', values: [] };
                                                                              const gfTable = (quickAdd?.table || baseTerm?.table || dashcfgSelectedBaseTable || '').toString();
                                                                              const cols = schemaReadyForCalc && gfTable ? (schemaTables?.[gfTable] || []) : [];
                                                                              const prefer = (cols || []).find((c) => /(^|_)(status|estado|state)(_|$)/i.test(String(c || '')))
                                                                                || (cols || []).find((c) => /(status|estado|state)/i.test(String(c || '')))
                                                                                || '';
                                                                              const col = (current.column || prefer || '');
                                                                              const op = (current.operator || 'in');
                                                                              const vals = Array.isArray(current.values) ? current.values : [];

                                                                              const applyAll = (patch) => {
                                                                                const nextFilter = {
                                                                                  column: patch.column ?? col,
                                                                                  operator: patch.operator ?? op,
                                                                                  values: Array.isArray(patch.values) ? patch.values : (patch.values ?? vals),
                                                                                };
                                                                                const nextSpec = calcSpec.map((t) => {
                                                                                  if ((t?.sourceType || '') === 'number') return t;
                                                                                  return { ...(t || {}), filters: nextFilter.column ? [nextFilter] : [] };
                                                                                });
                                                                                dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, nextSpec, { syncFormula: true });
                                                                                if (nextFilter.column && gfTable) {
                                                                                  dashcfgLoadColumnValues({
                                                                                    sectionKey: dashcfgSelectedSectionKey,
                                                                                    table: gfTable,
                                                                                    column: nextFilter.column,
                                                                                    dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                                  });
                                                                                }
                                                                              };

                                                                              const valsKey = col && gfTable ? dashcfgValuesKey({
                                                                                clientId: (user?.role === 'admin' ? selectedConfigClientId : ''),
                                                                                sectionKey: dashcfgSelectedSectionKey,
                                                                                dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                                table: gfTable,
                                                                                column: col,
                                                                                period: cfgFormulaPeriod,
                                                                                category: cfgFormulaCategory,
                                                                              }) : '';
                                                                              const state = valsKey ? (cfgColumnValues?.[valsKey] || {}) : {};
                                                                              const list = Array.isArray(state.values) ? state.values : [];
                                                                              const loading = Boolean(state.loading);
                                                                              const err = state.error ? String(state.error) : '';

                                                                              return (
                                                                                <>
                                                                                  <div className="dashcfg-filter-row">
                                                                                    <select
                                                                                      className="dashcfg-term-input"
                                                                                      value={col}
                                                                                      onChange={(e) => applyAll({ column: e.target.value, values: [] })}
                                                                                      disabled={!schemaReadyForCalc || !gfTable}
                                                                                    >
                                                                                      <option value="">{gfTable ? 'Columna…' : 'Selecciona tabla…'}</option>
                                                                                      {cols.map((c) => (
                                                                                        <option key={c} value={c}>{c}</option>
                                                                                      ))}
                                                                                    </select>
                                                                                    <select
                                                                                      className="dashcfg-term-op"
                                                                                      value={op}
                                                                                      onChange={(e) => applyAll({ operator: e.target.value })}
                                                                                      disabled={!schemaReadyForCalc || !col}
                                                                                    >
                                                                                      <option value="in">IN</option>
                                                                                      <option value="not_in">NOT IN</option>
                                                                                      <option value="equals">=</option>
                                                                                      <option value="not_equals">!=</option>
                                                                                      <option value="contains">contiene</option>
                                                                                    </select>
                                                                                    <button
                                                                                      type="button"
                                                                                      className="btn-chip"
                                                                                      onClick={() => applyAll({ column: '', values: [] })}
                                                                                      disabled={!schemaReadyForCalc || (!col && vals.length === 0)}
                                                                                    >
                                                                                      Quitar filtro
                                                                                    </button>
                                                                                  </div>

                                                                                  {col && (
                                                                                    <>
                                                                                      <div className="dashcfg-filter-row">
                                                                                        <button
                                                                                          type="button"
                                                                                          className="btn-chip"
                                                                                          disabled={!schemaReadyForCalc || !col || !gfTable}
                                                                                          onClick={() => dashcfgLoadColumnValues({
                                                                                            sectionKey: dashcfgSelectedSectionKey,
                                                                                            table: gfTable,
                                                                                            column: col,
                                                                                            dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                                          })}
                                                                                        >
                                                                                          {loading ? 'Cargando…' : 'Cargar valores'}
                                                                                        </button>
                                                                                        {err && <span className="dashcfg-hint" style={{ color: '#fca5a5' }}>{err}</span>}
                                                                                      </div>

                                                                                      {op === 'contains' ? (
                                                                                        <input
                                                                                          className="dashcfg-term-input"
                                                                                          value={(vals[0] || '')}
                                                                                          onChange={(e) => applyAll({ values: [e.target.value] })}
                                                                                          placeholder="Texto a buscar…"
                                                                                        />
                                                                                      ) : (
                                                                                        <select
                                                                                          multiple
                                                                                          className="dashcfg-multiselect"
                                                                                          value={vals}
                                                                                          onChange={(e) => {
                                                                                            const v = Array.from(e.target.selectedOptions || []).map((o) => o.value);
                                                                                            applyAll({ values: v });
                                                                                          }}
                                                                                          disabled={loading}
                                                                                        >
                                                                                          {list.map((v) => (
                                                                                            <option key={v} value={v}>{v}</option>
                                                                                          ))}
                                                                                        </select>
                                                                                      )}
                                                                                    </>
                                                                                  )}
                                                                                </>
                                                                              );
                                                                            })()}
                                                                          </div>
                                                                        )}
                                                                      </div>
                                                                    )}

                                                                    {cfgCalcDrawerAdvanced && (
                                                                      <div className="dashcfg-calc-actions">
                                                                        <button
                                                                          type="button"
                                                                          className="btn-chip"
                                                                          disabled={!schemaReadyForCalc}
                                                                          onClick={() => {
                                                                            const next = [
                                                                              ...calcSpec,
                                                                              {
                                                                                op: (calcSpec.length === 0 ? null : '+'),
                                                                                func: 'SUM',
                                                                                sourceType: 'table',
                                                                                table: dashcfgSelectedBaseTable || '',
                                                                                column: '',
                                                                                fieldKey: '',
                                                                                number: '',
                                                                                openParen: 0,
                                                                                closeParen: 0,
                                                                                filters: [],
                                                                              },
                                                                            ];
                                                                            dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, next, { syncFormula: true });
                                                                          }}
                                                                        >
                                                                          + Término
                                                                        </button>

                                                                        {calcSpec.length > 0 && (
                                                                          <>
                                                                            <button
                                                                              type="button"
                                                                              className="btn-chip"
                                                                              disabled={!schemaReadyForCalc || selectedTermIdxs.length < 2}
                                                                              onClick={() => dashcfgGroupSelectedTerms(dashcfgSelectedSectionKey, fieldKey, calcSpec, 'group')}
                                                                            >
                                                                              Agrupar
                                                                            </button>
                                                                            <button
                                                                              type="button"
                                                                              className="btn-chip"
                                                                              disabled={!schemaReadyForCalc || selectedTermIdxs.length < 1}
                                                                              onClick={() => dashcfgGroupSelectedTerms(dashcfgSelectedSectionKey, fieldKey, calcSpec, 'ungroup')}
                                                                            >
                                                                              Desagrupar
                                                                            </button>
                                                                            <button
                                                                              type="button"
                                                                              className="btn-chip"
                                                                              disabled={selectedTermIdxs.length < 1}
                                                                              onClick={() => dashcfgClearSelection(dashcfgSelectedSectionKey, fieldKey)}
                                                                            >
                                                                              Selección ({selectedTermIdxs.length})
                                                                            </button>
                                                                          </>
                                                                        )}
                                                                      </div>
                                                                    )}

                                                                    {calcSpec.length === 0 ? (
                                                                      <div className="dashcfg-field-help">
                                                                        Agrega valores con el constructor (arriba). Luego podrás filtrar y probar.
                                                                      </div>
                                                                    ) : (
                                                                      cfgCalcDrawerAdvanced ? calcSpec.map((term, idx) => {
                                                                        const colListId = `dashcfg-cols-${calcSafeId}-${idx}`;
                                                                        const t = term || {};
                                                                        const sourceType = t.sourceType || 'table';
                                                                        const opVal = (idx === 0 ? '' : (t.op || '+'));
                                                                        const funcVal = (t.func || 'COUNT');
                                                                        const tableVal = (t.table || '');
                                                                        const colVal = (t.column || '');
                                                                        const fieldVal = (t.fieldKey || '');
                                                                        const numVal = (t.number ?? '');
                                                                        const openParenVal = parseInt(t.openParen ?? 0, 10) || 0;
                                                                        const closeParenVal = parseInt(t.closeParen ?? 0, 10) || 0;
                                                                        const termSelected = selectedTermSet.has(idx);
                                                                        const termFilters = Array.isArray(t.filters) ? t.filters : [];
                                                                        const f0 = (termFilters[0] && typeof termFilters[0] === 'object') ? termFilters[0] : null;
                                                                        const filterEnabled = Boolean(f0 && f0.column);
                                                                        const filterOp = (f0?.operator || 'in');
                                                                        const filterVals = Array.isArray(f0?.values) ? f0.values : [];

                                                                        const updateTerm = (patch) => {
                                                                          const next = calcSpec.map((x, i) => (i === idx ? { ...(x || {}), ...patch } : x));
                                                                          dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, next, { syncFormula: true });
                                                                        };
                                                                        const removeTerm = () => {
                                                                          dashcfgClearSelection(dashcfgSelectedSectionKey, fieldKey);
                                                                          const next = calcSpec.filter((_, i) => i !== idx);
                                                                          dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, next, { syncFormula: true });
                                                                        };

                                                                        return (
                                                                          <div key={idx} className={`dashcfg-term-row ${termSelected ? 'selected' : ''}`}>
                                                                            <label className="dashcfg-term-select" title="Seleccionar término para agrupar/desagrupar">
                                                                              <input
                                                                                type="checkbox"
                                                                                checked={termSelected}
                                                                                onChange={() => dashcfgToggleSelectedTerm(dashcfgSelectedSectionKey, fieldKey, idx)}
                                                                                disabled={!schemaReadyForCalc}
                                                                              />
                                                                            </label>
                                                                            {idx === 0 ? (
                                                                              <span className="dashcfg-term-op">=</span>
                                                                            ) : (
                                                                              <select
                                                                                className="dashcfg-term-op"
                                                                                value={opVal || '+'}
                                                                                onChange={(e) => updateTerm({ op: e.target.value })}
                                                                                disabled={!schemaReadyForCalc}
                                                                              >
                                                                                {DASHCFG_CALC_OPS.map((o) => (
                                                                                  <option key={o} value={o}>{o}</option>
                                                                                ))}
                                                                              </select>
                                                                            )}

                                                                            <button
                                                                              type="button"
                                                                              className={`btn-chip dashcfg-paren ${openParenVal > 0 ? 'paren-on' : ''}`}
                                                                              onClick={() => updateTerm({ openParen: openParenVal > 0 ? 0 : 1 })}
                                                                              disabled={!schemaReadyForCalc}
                                                                              title="Abrir paréntesis antes de este término (para agrupar)"
                                                                            >
                                                                              (
                                                                            </button>
                                                                            <button
                                                                              type="button"
                                                                              className={`btn-chip dashcfg-paren ${closeParenVal > 0 ? 'paren-on' : ''}`}
                                                                              onClick={() => updateTerm({ closeParen: closeParenVal > 0 ? 0 : 1 })}
                                                                              disabled={!schemaReadyForCalc}
                                                                              title="Cerrar paréntesis después de este término (para agrupar)"
                                                                            >
                                                                              )
                                                                            </button>

                                                                            <select
                                                                              className="dashcfg-term-source"
                                                                              value={sourceType}
                                                                              onChange={(e) => {
                                                                                const nextType = e.target.value;
                                                                                if (nextType === 'number') {
                                                                                  updateTerm({ sourceType: 'number', number: numVal, table: '', column: '', fieldKey: '' });
                                                                                } else if (nextType === 'field') {
                                                                                  updateTerm({ sourceType: 'field', fieldKey: mappedFieldKeys?.[0] || '', table: '', column: '' });
                                                                                } else {
                                                                                  updateTerm({ sourceType: 'table', table: dashcfgSelectedBaseTable || '', column: '' });
                                                                                }
                                                                              }}
                                                                              disabled={!schemaReadyForCalc}
                                                                            >
                                                                              <option value="table">Tabla/Columna</option>
                                                                              <option value="field">Campo mapeado</option>
                                                                              <option value="number">Número</option>
                                                                            </select>

                                                                            {sourceType !== 'number' && (
                                                                              <select
                                                                                className="dashcfg-term-func"
                                                                                value={funcVal}
                                                                                onChange={(e) => updateTerm({ func: e.target.value })}
                                                                                disabled={!schemaReadyForCalc}
                                                                              >
                                                                                {DASHCFG_CALC_FUNCS.map((f) => (
                                                                                  <option key={f} value={f}>
                                                                                    {f === 'COUNT_DISTINCT' ? 'COUNT_DISTINCT (únicos)' : f}
                                                                                  </option>
                                                                                ))}
                                                                              </select>
                                                                            )}

                                                                            {sourceType === 'field' && (
                                                                              <select
                                                                                className="dashcfg-term-input"
                                                                                value={fieldVal}
                                                                                onChange={(e) => updateTerm({ fieldKey: e.target.value })}
                                                                                disabled={!schemaReadyForCalc}
                                                                              >
                                                                                <option value="">Selecciona campo…</option>
                                                                                {(mappedFieldKeys || []).map((k) => (
                                                                                  <option key={k} value={k}>{dashcfgGetFieldLabel(dashcfgSelectedSectionKey, k)} ({k})</option>
                                                                                ))}
                                                                              </select>
                                                                            )}

                                                                            {sourceType === 'table' && (
                                                                              <>
                                                                                <input
                                                                                  className="dashcfg-term-input"
                                                                                  list={calcTableListId}
                                                                                  value={tableVal}
                                                                                  onChange={(e) => updateTerm({ table: e.target.value, column: '' })}
                                                                                  placeholder="Tabla…"
                                                                                  disabled={!schemaReadyForCalc}
                                                                                />

                                                                                <input
                                                                                  className="dashcfg-term-input"
                                                                                  list={colListId}
                                                                                  value={colVal}
                                                                                  onChange={(e) => updateTerm({ column: e.target.value })}
                                                                                  placeholder="Columna…"
                                                                                  disabled={!schemaReadyForCalc || !tableVal}
                                                                                />
                                                                                <datalist id={colListId}>
                                                                                  {(schemaTables?.[tableVal] || []).map((col) => (
                                                                                    <option key={col} value={col} />
                                                                                  ))}
                                                                                </datalist>
                                                                              </>
                                                                            )}

                                                                            {sourceType === 'number' && (
                                                                              <input
                                                                                className="dashcfg-term-input"
                                                                                value={numVal}
                                                                                onChange={(e) => updateTerm({ number: e.target.value })}
                                                                                placeholder="Número…"
                                                                                disabled={!schemaReadyForCalc}
                                                                              />
                                                                            )}

                                                                            {(sourceType === 'table' || sourceType === 'field') && (
                                                                              <details className="dashcfg-filter" open={filterEnabled}>
                                                                                <summary className="dashcfg-filter-summary">
                                                                                  <span className={`dashcfg-pill ${filterEnabled ? 'ok' : 'neutral'}`}>
                                                                                    {filterEnabled ? 'Filtro activo' : 'Filtro'}
                                                                                  </span>
                                                                                </summary>
                                                                                <div className="dashcfg-filter-body">
                                                                                  {(() => {
                                                                                    // Resolver tabla/columnas disponibles para el filtro
                                                                                    let fTable = '';
                                                                                    if (sourceType === 'table') fTable = tableVal || '';
                                                                                    if (sourceType === 'field') {
                                                                                      const m = dashcfgSelectedSectionCfg.fieldMappings?.[fieldVal] || {};
                                                                                      fTable = m?.table || '';
                                                                                    }
                                                                                    const fCols = schemaReadyForCalc && fTable ? (schemaTables?.[fTable] || []) : [];
                                                                                    const prefer = (fCols || []).find((c) => /(^|_)(status|estado|state)(_|$)/i.test(String(c || '')))
                                                                                      || (fCols || []).find((c) => /(status|estado|state)/i.test(String(c || '')))
                                                                                      || (fCols[0] || '');
                                                                                    const fCol = (f0?.column || prefer);
                                                                                    const valsKey = fTable && fCol ? dashcfgValuesKey({
                                                                                      clientId: (user?.role === 'admin' ? selectedConfigClientId : ''),
                                                                                      sectionKey: dashcfgSelectedSectionKey,
                                                                                      dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                                      table: fTable,
                                                                                      column: fCol,
                                                                                      period: cfgFormulaPeriod,
                                                                                      category: cfgFormulaCategory,
                                                                                    }) : '';
                                                                                    const valsState = valsKey ? (cfgColumnValues?.[valsKey] || {}) : {};
                                                                                    const valsList = Array.isArray(valsState.values) ? valsState.values : [];
                                                                                    const loadingVals = Boolean(valsState.loading);
                                                                                    const valsErr = valsState.error ? String(valsState.error) : '';

                                                                                    const setFilter = (nextCol, nextOp, nextVals) => {
                                                                                      const nf = { column: nextCol, operator: nextOp, values: Array.isArray(nextVals) ? nextVals : [] };
                                                                                      updateTerm({ filters: [nf] });
                                                                                      if (nextCol && fTable) {
                                                                                        dashcfgLoadColumnValues({
                                                                                          sectionKey: dashcfgSelectedSectionKey,
                                                                                          table: fTable,
                                                                                          column: nextCol,
                                                                                          dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                                        });
                                                                                      }
                                                                                    };

                                                                                    const disableFilter = () => updateTerm({ filters: [] });

                                                                                    if (!schemaReadyForCalc) {
                                                                                      return <div className="dashcfg-field-help">Carga el esquema para habilitar filtros.</div>;
                                                                                    }
                                                                                    if (!fTable) {
                                                                                      return <div className="dashcfg-field-help">Selecciona primero una tabla/columna para poder filtrar.</div>;
                                                                                    }

                                                                                    return (
                                                                                      <>
                                                                                        <div className="dashcfg-filter-row">
                                                                                          <select
                                                                                            className="dashcfg-term-input"
                                                                                            value={fCol || ''}
                                                                                            onChange={(e) => setFilter(e.target.value, filterOp, [])}
                                                                                          >
                                                                                            <option value="">Columna filtro…</option>
                                                                                            {fCols.map((c) => (
                                                                                              <option key={c} value={c}>{c}</option>
                                                                                            ))}
                                                                                          </select>

                                                                                          <select
                                                                                            className="dashcfg-term-op"
                                                                                            value={filterOp}
                                                                                            onChange={(e) => setFilter(fCol, e.target.value, filterVals)}
                                                                                            title="Operador del filtro"
                                                                                          >
                                                                                            <option value="in">IN</option>
                                                                                            <option value="not_in">NOT IN</option>
                                                                                            <option value="equals">=</option>
                                                                                            <option value="not_equals">!=</option>
                                                                                            <option value="contains">contiene</option>
                                                                                          </select>

                                                                                          <button type="button" className="btn-chip" onClick={disableFilter}>
                                                                                            Quitar filtro
                                                                                          </button>
                                                                                        </div>

                                                                                        {fCol && (
                                                                                          <>
                                                                                            <div className="dashcfg-filter-row">
                                                                                              <button
                                                                                                type="button"
                                                                                                className="btn-chip"
                                                                                                onClick={() => dashcfgLoadColumnValues({
                                                                                                  sectionKey: dashcfgSelectedSectionKey,
                                                                                                  table: fTable,
                                                                                                  column: fCol,
                                                                                                  dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                                                })}
                                                                                              >
                                                                                                {loadingVals ? 'Cargando valores…' : 'Cargar valores'}
                                                                                              </button>
                                                                                              {valsErr && <span className="dashcfg-hint" style={{ color: '#fca5a5' }}>{valsErr}</span>}
                                                                                            </div>

                                                                                            {filterOp === 'contains' ? (
                                                                                              <input
                                                                                                className="dashcfg-term-input"
                                                                                                value={(filterVals[0] || '')}
                                                                                                onChange={(e) => setFilter(fCol, 'contains', [e.target.value])}
                                                                                                placeholder="Texto a buscar…"
                                                                                              />
                                                                                            ) : (
                                                                                              <>
                                                                                                <select
                                                                                                  multiple
                                                                                                  className="dashcfg-multiselect"
                                                                                                  value={filterVals}
                                                                                                  onChange={(e) => {
                                                                                                    const v = Array.from(e.target.selectedOptions || []).map((o) => o.value);
                                                                                                    setFilter(fCol, filterOp, v);
                                                                                                  }}
                                                                                                  disabled={loadingVals}
                                                                                                  title="Selecciona valores (Ctrl/Cmd o Shift)"
                                                                                                >
                                                                                                  {valsList.map((v) => (
                                                                                                    <option key={v} value={v}>{v}</option>
                                                                                                  ))}
                                                                                                </select>
                                                                                                {!loadingVals && !valsErr && fCol && valsList.length === 0 && (
                                                                                                  <div className="dashcfg-field-help">
                                                                                                    No se encontraron valores. Prueba a cambiar el <strong>Preview (periodo)</strong> a “Últimos 30 días” o revisa que la columna tenga datos.
                                                                                                  </div>
                                                                                                )}
                                                                                              </>
                                                                                            )}
                                                                                          </>
                                                                                        )}
                                                                                      </>
                                                                                    );
                                                                                  })()}
                                                                                </div>
                                                                              </details>
                                                                            )}

                                                                            <button type="button" className="btn-chip" onClick={removeTerm} disabled={!schemaReadyForCalc}>
                                                                              Quitar
                                                                            </button>
                                                                          </div>
                                                                        );
                                                                      }) : null
                                                                    )}
                                                                  </div>
                                                                  <datalist id={calcTableListId}>
                                                                    {calcTables.map((tbl) => (
                                                                      <option key={tbl} value={tbl} />
                                                                    ))}
                                                                  </datalist>
                                                                </>
                                                              )}

                                                              {cfgCalcWizardStep === 1 && (
                                                                <>
                                                                  <div className="dashcfg-step">
                                                                    <div className="dashcfg-step-title">Filtros (filas)</div>
                                                                    <div className="dashcfg-step-help">
                                                                      Filtra por estado u otros valores. En modo <strong>Simple</strong> se aplica a todos los valores seleccionados.
                                                                    </div>
                                                                  </div>

                                                                  {!cfgCalcDrawerAdvanced ? (
                                                                    <div className="dashcfg-pivot-block">
                                                                      {(() => {
                                                                        const baseTerm = (calcSpec || []).find((t) => (t?.sourceType || '') !== 'number') || null;
                                                                        const gfTable = (quickAdd?.table || baseTerm?.table || dashcfgSelectedBaseTable || '').toString();
                                                                        const cols = schemaReadyForCalc && gfTable ? (schemaTables?.[gfTable] || []) : [];
                                                                        const preferStatus = (cols || []).find((c) => /(^|_)(status|estado|state)(_|$)/i.test(String(c || '')))
                                                                          || (cols || []).find((c) => /(status|estado|state)/i.test(String(c || '')))
                                                                          || '';
                                                                        const preferSub = (cols || []).find((c) => /(subestado|sub_status|substatus|sub_state)/i.test(String(c || '')))
                                                                          || '';

                                                                        const resolveTermTable = (t) => {
                                                                          const st = String(t?.sourceType || '').toLowerCase();
                                                                          if (st === 'table') return String(t?.table || '');
                                                                          if (st === 'field') {
                                                                            const fk = String(t?.fieldKey || '');
                                                                            const m = dashcfgSelectedSectionCfg.fieldMappings?.[fk] || {};
                                                                            return String(m?.table || '');
                                                                          }
                                                                          return '';
                                                                        };

                                                                        const currentFilters = Array.isArray(baseTerm?.filters) ? baseTerm.filters : [];
                                                                        const filters = (currentFilters.length ? currentFilters : [{ column: preferStatus || '', operator: 'in', values: [] }])
                                                                          .slice(0, 3)
                                                                          .map((f, idx) => ({
                                                                            column: String((f?.column || (idx === 0 ? preferStatus : '')) || ''),
                                                                            operator: String((f?.operator || 'in') || 'in'),
                                                                            values: Array.isArray(f?.values) ? f.values : [],
                                                                          }));

                                                                        const applyAllFilters = (nextFilters) => {
                                                                          const cleaned = (Array.isArray(nextFilters) ? nextFilters : [])
                                                                            .filter((f) => f && typeof f === 'object')
                                                                            .slice(0, 5)
                                                                            .map((f) => ({
                                                                              column: String(f.column || '').trim(),
                                                                              operator: String(f.operator || 'in').trim().toLowerCase(),
                                                                              values: Array.isArray(f.values) ? f.values : [],
                                                                            }))
                                                                            .filter((f) => Boolean(f.column));

                                                                          const nextSpec = calcSpec.map((t) => {
                                                                            if (!t || (t?.sourceType || '') === 'number') return t;
                                                                            const tTable = resolveTermTable(t);
                                                                            if (gfTable && tTable && tTable !== gfTable) return t;
                                                                            return { ...(t || {}), filters: cleaned };
                                                                          });

                                                                          dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, nextSpec, { syncFormula: true });
                                                                        };

                                                                        const setFilterAt = (idx, patch) => {
                                                                          const cur = filters[idx] || { column: '', operator: 'in', values: [] };
                                                                          const next = [...filters];
                                                                          const nextCol = Object.prototype.hasOwnProperty.call(patch || {}, 'column') ? String(patch.column || '') : cur.column;
                                                                          const nextOp = Object.prototype.hasOwnProperty.call(patch || {}, 'operator') ? String(patch.operator || 'in') : cur.operator;
                                                                          const nextVals = Object.prototype.hasOwnProperty.call(patch || {}, 'values') ? (Array.isArray(patch.values) ? patch.values : []) : cur.values;
                                                                          next[idx] = { column: nextCol, operator: nextOp, values: nextVals };
                                                                          applyAllFilters(next);
                                                                          if (gfTable && nextCol && Object.prototype.hasOwnProperty.call(patch || {}, 'column')) {
                                                                            dashcfgLoadColumnValues({
                                                                              sectionKey: dashcfgSelectedSectionKey,
                                                                              table: gfTable,
                                                                              column: nextCol,
                                                                              dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                            });
                                                                          }
                                                                        };

                                                                        const removeFilterAt = (idx) => {
                                                                          const next = filters.filter((_, i) => i !== idx);
                                                                          applyAllFilters(next);
                                                                        };

                                                                        const addCondition = () => {
                                                                          if (filters.length >= 3) return;
                                                                          const used = new Set(filters.map((f) => String(f.column || '')).filter(Boolean));
                                                                          const pick = preferSub && !used.has(preferSub)
                                                                            ? preferSub
                                                                            : (cols || []).find((c) => c && !used.has(String(c))) || '';
                                                                          const next = [...filters, { column: String(pick || ''), operator: 'in', values: [] }];
                                                                          applyAllFilters(next);
                                                                        };

                                                                        return (
                                                                          <>
                                                                            {filters.map((f, idx) => {
                                                                              const col = String(f.column || '').trim();
                                                                              const op = String(f.operator || 'in').toLowerCase();
                                                                              const vals = Array.isArray(f.values) ? f.values : [];

                                                                              const valsKey = col && gfTable ? dashcfgValuesKey({
                                                                                clientId: (user?.role === 'admin' ? selectedConfigClientId : ''),
                                                                                sectionKey: dashcfgSelectedSectionKey,
                                                                                dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                                table: gfTable,
                                                                                column: col,
                                                                                period: cfgFormulaPeriod,
                                                                                category: cfgFormulaCategory,
                                                                              }) : '';
                                                                              const state = valsKey ? (cfgColumnValues?.[valsKey] || {}) : {};
                                                                              const list = Array.isArray(state.values) ? state.values : [];
                                                                              const loading = Boolean(state.loading);
                                                                              const err = state.error ? String(state.error) : '';

                                                                              return (
                                                                                <div key={`${idx}-${col || 'col'}`} className="dashcfg-filter">
                                                                                  <div className="dashcfg-filter-row">
                                                                                    <span className="dashcfg-pill neutral">{`Condición ${idx + 1}`}</span>
                                                                                    <select
                                                                                      className="dashcfg-term-input"
                                                                                      value={col}
                                                                                      onChange={(e) => setFilterAt(idx, { column: e.target.value, values: [] })}
                                                                                      disabled={!schemaReadyForCalc || !gfTable}
                                                                                    >
                                                                                      <option value="">{gfTable ? 'Columna…' : 'Selecciona tabla…'}</option>
                                                                                      {cols.map((c) => (
                                                                                        <option key={c} value={c}>{c}</option>
                                                                                      ))}
                                                                                    </select>
                                                                                    <select
                                                                                      className="dashcfg-term-op"
                                                                                      value={op}
                                                                                      onChange={(e) => setFilterAt(idx, { operator: e.target.value })}
                                                                                      disabled={!schemaReadyForCalc || !col}
                                                                                    >
                                                                                      <option value="in">IN</option>
                                                                                      <option value="not_in">NOT IN</option>
                                                                                      <option value="equals">=</option>
                                                                                      <option value="not_equals">!=</option>
                                                                                      <option value="contains">contiene</option>
                                                                                    </select>
                                                                                    <button
                                                                                      type="button"
                                                                                      className="btn-chip"
                                                                                      onClick={() => removeFilterAt(idx)}
                                                                                      disabled={!schemaReadyForCalc || filters.length <= 1}
                                                                                    >
                                                                                      Quitar
                                                                                    </button>
                                                                                  </div>

                                                                                  {col ? (
                                                                                    <>
                                                                                      <div className="dashcfg-filter-row">
                                                                                        <button
                                                                                          type="button"
                                                                                          className="btn-chip"
                                                                                          disabled={!schemaReadyForCalc || !col || !gfTable}
                                                                                          onClick={() => dashcfgLoadColumnValues({
                                                                                            sectionKey: dashcfgSelectedSectionKey,
                                                                                            table: gfTable,
                                                                                            column: col,
                                                                                            dataSource: (dashcfgSelectedSectionCfg.dataSource || 'main'),
                                                                                          })}
                                                                                        >
                                                                                          {loading ? 'Cargando…' : 'Cargar valores'}
                                                                                        </button>
                                                                                        {err && <span className="dashcfg-hint" style={{ color: '#fca5a5' }}>{err}</span>}
                                                                                      </div>

                                                                                      {op === 'contains' ? (
                                                                                        <input
                                                                                          className="dashcfg-term-input"
                                                                                          value={(vals[0] || '')}
                                                                                          onChange={(e) => setFilterAt(idx, { values: [e.target.value] })}
                                                                                          placeholder="Texto a buscar…"
                                                                                        />
                                                                                      ) : (
                                                                                        <>
                                                                                          <div className="dashcfg-col-list" aria-label="Valores disponibles">
                                                                                            {list.length === 0 ? (
                                                                                              <div className="dashcfg-field-help">
                                                                                                Pulsa <strong>Cargar valores</strong> para ver opciones de esta columna.
                                                                                              </div>
                                                                                            ) : (
                                                                                              list.map((v) => {
                                                                                                const checked = vals.includes(v);
                                                                                                return (
                                                                                                  <label key={v} className={`dashcfg-col-item ${checked ? 'on' : ''}`}>
                                                                                                    <input
                                                                                                      type="checkbox"
                                                                                                      checked={checked}
                                                                                                      onChange={(e) => {
                                                                                                        const set = new Set(vals);
                                                                                                        if (e.target.checked) set.add(v);
                                                                                                        else set.delete(v);
                                                                                                        setFilterAt(idx, { values: Array.from(set) });
                                                                                                      }}
                                                                                                    />
                                                                                                    <span>{v}</span>
                                                                                                  </label>
                                                                                                );
                                                                                              })
                                                                                            )}
                                                                                          </div>
                                                                                          <div className="dashcfg-col-actions">
                                                                                            <button
                                                                                              type="button"
                                                                                              className="btn-chip"
                                                                                              disabled={list.length === 0}
                                                                                              onClick={() => setFilterAt(idx, { values: list.slice(0, 200) })}
                                                                                            >
                                                                                              Seleccionar todo
                                                                                            </button>
                                                                                            <button
                                                                                              type="button"
                                                                                              className="btn-chip"
                                                                                              disabled={vals.length === 0}
                                                                                              onClick={() => setFilterAt(idx, { values: [] })}
                                                                                            >
                                                                                              Limpiar
                                                                                            </button>
                                                                                          </div>
                                                                                        </>
                                                                                      )}
                                                                                    </>
                                                                                  ) : (
                                                                                    <div className="dashcfg-field-help">Elige una columna para esta condición.</div>
                                                                                  )}
                                                                                </div>
                                                                              );
                                                                            })}

                                                                            <div className="dashcfg-col-actions" style={{ marginTop: 10 }}>
                                                                              <button
                                                                                type="button"
                                                                                className="btn-chip"
                                                                                onClick={addCondition}
                                                                                disabled={!schemaReadyForCalc || filters.length >= 3}
                                                                                title={filters.length >= 3 ? 'Máximo 3 condiciones en modo Simple' : 'Añadir otra condición (AND)'}
                                                                              >
                                                                                + Condición
                                                                              </button>
                                                                              <button
                                                                                type="button"
                                                                                className="btn-chip"
                                                                                onClick={() => applyAllFilters([])}
                                                                                disabled={!schemaReadyForCalc}
                                                                              >
                                                                                Quitar filtros
                                                                              </button>
                                                                            </div>
                                                                          </>
                                                                        );
                                                                      })()}
                                                                    </div>
                                                                  ) : (
                                                                    <div className="dashcfg-empty">
                                                                      Estás en modo <strong>Avanzado</strong>. En este modo, los filtros se ajustan por término en “Valores”.
                                                                      Si quieres un filtro global tipo Excel, cambia a <strong>Simple</strong>.
                                                                    </div>
                                                                  )}
                                                                </>
                                                              )}

                                                              {cfgCalcWizardStep === 2 && (
                                                                <>
                                                                  <div className="dashcfg-step">
                                                                    <div className="dashcfg-step-title">Probar</div>
                                                                    <div className="dashcfg-step-help">
                                                                      Revisa la expresión y pulsa “Probar” para ver el resultado con el periodo/categoría seleccionados.
                                                                    </div>
                                                                  </div>

                                                                  <input
                                                                    type="text"
                                                                    className="dashcfg-calc-input"
                                                                    value={calcEffective}
                                                                    disabled={calcSpec.length > 0}
                                                                    onChange={(e) => updateSectionConfig(dashcfgSelectedSectionKey, (sec) => {
                                                                      const nextCalcs = { ...(sec.fieldCalculations || {}) };
                                                                      nextCalcs[fieldKey] = e.target.value;
                                                                      const nextSpecs = { ...(sec.fieldCalculationSpecs || {}) };
                                                                      nextSpecs[fieldKey] = []; // si editas manualmente, desactiva el constructor
                                                                      return { ...sec, fieldCalculations: nextCalcs, fieldCalculationSpecs: nextSpecs };
                                                                    })}
                                                                    placeholder="Ej: SUM({{talk_time}}) + SUM(otra_tabla.no_atendidas)"
                                                                  />
                                                                  <div className="dashcfg-field-help">
                                                                    Puedes usar <strong>{'{{campo}}'}</strong> (usa el mapeo) o <strong>tabla.columna</strong>.
                                                                  </div>
                                                                  <div className="dashcfg-formula-preview">
                                                                    <div className="dashcfg-formula-label">Expresión</div>
                                                                    <code className="dashcfg-formula-code">{String(calcEffective || '').trim() ? calcEffective : '--'}</code>
                                                                  </div>

                                                                  <div className="dashcfg-calc-actions">
                                                                    {/* Acciones del paso Probar: mantenemos todo simple aquí */}

                                                                    {calcSpec.length > 0 && (
                                                                      <button
                                                                        type="button"
                                                                        className="btn-chip"
                                                                        onClick={() => dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, [], { syncFormula: true })}
                                                                      >
                                                                        Limpiar
                                                                      </button>
                                                                    )}

                                                                    {calcSpec.length > 0 && (
                                                                      <button
                                                                        type="button"
                                                                        className="btn-chip"
                                                                        onClick={() => dashcfgSetCalcSpec(dashcfgSelectedSectionKey, fieldKey, [], { syncFormula: false })}
                                                                        title="Desactiva el constructor y permite editar la fórmula a mano"
                                                                      >
                                                                        Modo manual
                                                                      </button>
                                                                    )}

                                                                    <button
                                                                      type="button"
                                                                      className="btn-chip"
                                                                      disabled={!String(calcEffective || '').trim()}
                                                                      onClick={() => dashcfgPreviewFormula(
                                                                        dashcfgSelectedSectionKey,
                                                                        fieldKey,
                                                                        calcEffective,
                                                                        (calcSpec.length > 0 ? calcSpec : null)
                                                                      )}
                                                                    >
                                                                      Probar
                                                                    </button>
                                                                    {(() => {
                                                                      const k = dashcfgPreviewKey(dashcfgSelectedSectionKey, fieldKey);
                                                                      const st = cfgFormulaPreview?.[k] || {};
                                                                      if (st.loading) return <span className="dashcfg-hint">Calculando…</span>;
                                                                      if (st.error) return <span className="dashcfg-hint" style={{ color: '#fca5a5' }}>{st.error}</span>;
                                                                      if (st.value == null) return null;
                                                                      const debug = st.debug || null;
                                                                      const items = Array.isArray(debug?.terms)
                                                                        ? debug.terms
                                                                        : (Array.isArray(debug?.calls) ? debug.calls : []);
                                                                      const anyNoDate = items.some((c) => c?.applied_filters && c.applied_filters.date_filter === false);
                                                                      const anyNoType = (dashcfgSelectedSectionKey === 'analytics')
                                                                        ? items.some((c) => c?.applied_filters && c.applied_filters.call_type_filter === false)
                                                                        : false;
                                                                      return (
                                                                        <span className="dashcfg-hint">
                                                                          Resultado: <strong>{String(st.value)}</strong>
                                                                          {(anyNoDate || anyNoType) && (
                                                                            <span>
                                                                              {' '}· Aviso:{' '}
                                                                              {anyNoDate ? 'sin filtro de fecha en alguna tabla' : ''}
                                                                              {anyNoDate && anyNoType ? ' / ' : ''}
                                                                              {anyNoType ? 'sin filtro IN/OUT en alguna tabla' : ''}
                                                                            </span>
                                                                          )}
                                                                        </span>
                                                                      );
                                                                    })()}
                                                                  </div>

                                                                  {(() => {
                                                                    const k = dashcfgPreviewKey(dashcfgSelectedSectionKey, fieldKey);
                                                                    const st = cfgFormulaPreview?.[k] || {};
                                                                    if (st.loading || st.error || !st.debug) return null;
                                                                    const debug = st.debug || {};
                                                                    const exprPreview = typeof debug.expression === 'string' ? debug.expression : '';
                                                                    const sqlPreview = typeof debug.sql_preview === 'string' ? debug.sql_preview : '';
                                                                    const sqlPreviewFull = typeof debug.sql_preview_full === 'string' ? debug.sql_preview_full : '';
                                                                    const terms = Array.isArray(debug.terms) ? debug.terms : [];
                                                                    return (
                                                                      <div className="dashcfg-debug">
                                                                        {exprPreview && (
                                                                          <details className="dashcfg-details">
                                                                            <summary>Ver expresión evaluada</summary>
                                                                            <div className="dashcfg-details-body">
                                                                              <pre className="dashcfg-sql-pre small">
                                                                                <code className="dashcfg-sql-code">{exprPreview}</code>
                                                                              </pre>
                                                                              <div className="dashcfg-field-help">
                                                                                Esta es la operación final aplicada sobre los bloques (<strong>v0</strong>, <strong>v1</strong>, …).
                                                                              </div>
                                                                            </div>
                                                                          </details>
                                                                        )}

                                                                        <details className="dashcfg-details">
                                                                          <summary>{sqlPreviewFull ? 'Ver SQL generado (compacto)' : 'Ver SQL generado (preview)'}</summary>
                                                                          <div className="dashcfg-details-body">
                                                                            <div className="dashcfg-details-actions">
                                                                              <button
                                                                                type="button"
                                                                                className="btn-chip"
                                                                                disabled={!sqlPreview}
                                                                                onClick={async () => {
                                                                                  try {
                                                                                    await navigator.clipboard.writeText(sqlPreview || '');
                                                                                    showSuccess('SQL copiado al portapapeles');
                                                                                  } catch (e) {
                                                                                    setConfigError('No se pudo copiar el SQL (permiso del navegador).');
                                                                                  }
                                                                                }}
                                                                              >
                                                                                Copiar SQL
                                                                              </button>
                                                                            </div>
                                                                            <pre className="dashcfg-sql-pre">
                                                                              <code className="dashcfg-sql-code">{sqlPreview || 'Pulsa “Probar” para generar el SQL.'}</code>
                                                                            </pre>
                                                                            <div className="dashcfg-field-help">
                                                                              Este SQL es una representación legible (estilo Looker) de lo que se está calculando: cada bloque es un agregado + filtros, y luego se aplica la operación final.
                                                                            </div>
                                                                          </div>
                                                                        </details>

                                                                        {sqlPreviewFull && (
                                                                          <details className="dashcfg-details">
                                                                            <summary>Ver SQL generado (completo)</summary>
                                                                            <div className="dashcfg-details-body">
                                                                              <div className="dashcfg-details-actions">
                                                                                <button
                                                                                  type="button"
                                                                                  className="btn-chip"
                                                                                  disabled={!sqlPreviewFull}
                                                                                  onClick={async () => {
                                                                                    try {
                                                                                      await navigator.clipboard.writeText(sqlPreviewFull || '');
                                                                                      showSuccess('SQL completo copiado al portapapeles');
                                                                                    } catch (e) {
                                                                                      setConfigError('No se pudo copiar el SQL (permiso del navegador).');
                                                                                    }
                                                                                  }}
                                                                                >
                                                                                  Copiar SQL completo
                                                                                </button>
                                                                              </div>
                                                                              <pre className="dashcfg-sql-pre">
                                                                                <code className="dashcfg-sql-code">{sqlPreviewFull}</code>
                                                                              </pre>
                                                                            </div>
                                                                          </details>
                                                                        )}

                                                                        {terms.length > 0 && (
                                                                          <details className="dashcfg-details">
                                                                            <summary>Ver desglose por bloques</summary>
                                                                            <div className="dashcfg-details-body">
                                                                              <div className="dashcfg-debug-terms">
                                                                                {terms.map((t, i) => {
                                                                                  const stype = (typeof t?.sourceType === 'string' ? t.sourceType : '').toLowerCase();
                                                                                  const label = (stype === 'number')
                                                                                    ? `Número`
                                                                                    : `${t?.func || 'AGG'}(${t?.table || 'tabla'}.${t?.column || 'col'})`;
                                                                                  const val = (t && t.value != null) ? t.value : null;
                                                                                  const applied = t?.applied_filters || {};
                                                                                  const termSql = typeof t?.sql_preview === 'string' ? t.sql_preview : '';
                                                                                  const tf = Array.isArray(applied?.term_filters) ? applied.term_filters : [];
                                                                                  const inf = Array.isArray(applied?.in_filters) ? applied.in_filters : [];
                                                                                  const varName = (typeof t?.var === 'string' && t.var) ? t.var : `v${i}`;
                                                                                  const rowCount = (t && t.row_count != null) ? Number(t.row_count) : null;
                                                                                  const opMap = { in: 'IN', not_in: 'NOT IN', equals: '=', not_equals: '!=', contains: 'contiene' };
                                                                                  const tfTxt = tf.slice(0, 2).map((f) => {
                                                                                    const col = String(f?.column || '');
                                                                                    const op = opMap[String(f?.operator || '').toLowerCase()] || String(f?.operator || '');
                                                                                    const vs = Array.isArray(f?.values) ? f.values : [];
                                                                                    const shown = vs.slice(0, 4).join(', ');
                                                                                    const tail = vs.length > 4 ? ` +${vs.length - 4}…` : '';
                                                                                    const rhs = (String(f?.operator || '').toLowerCase() === 'contains')
                                                                                      ? (vs[0] || '')
                                                                                      : (String(f?.operator || '').toLowerCase() === 'equals' || String(f?.operator || '').toLowerCase() === 'not_equals')
                                                                                        ? (vs[0] || '')
                                                                                        : `(${shown}${tail})`;
                                                                                    return col ? `${col} ${op} ${rhs}` : '';
                                                                                  }).filter(Boolean).join(' · ');
                                                                                  return (
                                                                                    <div key={`${label}-${i}`} className="dashcfg-debug-term">
                                                                                      <div className="dashcfg-debug-term-head">
                                                                                        <div className="dashcfg-debug-term-title">
                                                                                          <span className="dashcfg-pill neutral">{varName}</span>{' '}
                                                                                          <strong>{label}</strong>
                                                                                        </div>
                                                                                        <div className="dashcfg-debug-term-value">
                                                                                          {val == null ? '—' : <span>Valor: <strong>{String(val)}</strong></span>}
                                                                                        </div>
                                                                                      </div>
                                                                                      <div className="dashcfg-debug-term-meta">
                                                                                        <span className="dashcfg-hint">
                                                                                          Filtros:{' '}
                                                                                          {applied?.date_filter ? 'fecha' : 'sin fecha'}
                                                                                          {' · '}
                                                                                          {(dashcfgSelectedSectionKey === 'analytics')
                                                                                            ? (applied?.call_type_filter ? 'IN/OUT' : 'sin IN/OUT')
                                                                                            : '—'}
                                                                                          {tf.length ? ` · ${tf.length} filtro(s) de columna` : ''}
                                                                                          {tfTxt ? ` · ${tfTxt}` : ''}
                                                                                          {inf.length ? ` · ${inf.reduce((acc, x) => acc + (x?.count || 0), 0)} IN(s)` : ''}
                                                                                          {(rowCount != null && !Number.isNaN(rowCount)) ? ` · filas: ${rowCount}` : ''}
                                                                                        </span>
                                                                                      </div>
                                                                                      {termSql && (
                                                                                        <pre className="dashcfg-sql-pre small">
                                                                                          <code className="dashcfg-sql-code">{termSql}</code>
                                                                                        </pre>
                                                                                      )}
                                                                                    </div>
                                                                                  );
                                                                                })}
                                                                              </div>
                                                                            </div>
                                                                          </details>
                                                                        )}
                                                                      </div>
                                                                    );
                                                                  })()}
                                                                </>
                                                              )}
                                                            </div>
                                                          </div>
                                                        </div>
                                                        , document.body)}
                                                    </div>
                                                  )}
                                                </div>
                                                {dashcfgSelectedMeta?.dataFields?.includes(fieldKey) && cfgShowAdvanced ? (
                                                  <div className="dashcfg-map">
                                                    <select
                                                      value={dashcfgSelectedSectionCfg.fieldMappings?.[fieldKey]?.column || ''}
                                                      onChange={(e) => updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
                                                        ...sec,
                                                        fieldMappings: {
                                                          ...(sec.fieldMappings || {}),
                                                          [fieldKey]: {
                                                            ...(sec.fieldMappings?.[fieldKey] || {}),
                                                            table: dashcfgSelectedBaseTable || sec.fieldMappings?.[fieldKey]?.table || null,
                                                            column: e.target.value || null,
                                                          }
                                                        }
                                                      }))}
                                                      disabled={
                                                        !dashcfgSelectedBaseTable
                                                        || schemaLoading
                                                        || (schemaDataSource !== (dashcfgSelectedSectionCfg.dataSource || 'main'))
                                                      }
                                                    >
                                                      <option value="">{dashcfgSelectedBaseTable ? 'Columna…' : 'Selecciona tabla…'}</option>
                                                      {(
                                                        (schemaDataSource === (dashcfgSelectedSectionCfg.dataSource || 'main')
                                                          ? (schemaTables?.[dashcfgSelectedBaseTable] || [])
                                                          : [])
                                                      ).map((col) => (
                                                        <option key={col} value={col}>{col}</option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                ) : (
                                                  <div className="dashcfg-map muted">
                                                    <span className="dashcfg-pill neutral">
                                                      {dashcfgSelectedMeta?.dataFields?.includes(fieldKey) ? 'Dato fuente' : 'Calculado'}
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                      </div>
                                    )}
                                  </div>

                                  {/* Configuración de Subestados Jerárquicos - Solo para analytics_ai - MOVIDA ARRIBA PARA MEJOR VISIBILIDAD */}
                                  {dashcfgSelectedSectionKey === 'analytics_ai' && (
                                    <div className="dashcfg-group" style={{
                                      marginTop: '32px',
                                      border: '2px solid rgba(59, 130, 246, 0.3)',
                                      borderRadius: '12px',
                                      padding: '20px',
                                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0.02) 100%)'
                                    }}>
                                      <div className="dashcfg-group-title" style={{
                                        fontSize: '18px',
                                        fontWeight: '700',
                                        color: 'rgba(59, 130, 246, 1)',
                                        marginBottom: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                      }}>
                                        📊 Configuración de Desglose de Subestados
                                        <span style={{
                                          fontSize: '12px',
                                          fontWeight: '500',
                                          color: 'rgba(59, 130, 246, 0.7)',
                                          background: 'rgba(59, 130, 246, 0.1)',
                                          padding: '4px 8px',
                                          borderRadius: '4px'
                                        }}>
                                          Solo para Analíticas Agente IA
                                        </span>
                                      </div>
                                      <div className="dashcfg-field-help" style={{
                                        marginBottom: '16px',
                                        fontSize: '14px',
                                        lineHeight: '1.6',
                                        color: 'rgba(12, 44, 108, 0.8)'
                                      }}>
                                        <strong>¿Qué es esto?</strong> Configura la jerarquía de estados y subestados de llamadas que se mostrará en el panel "Desglose de Subestados" del dashboard del cliente.
                                        Define niveles padre-hijo (ej: Llamadas Completadas → Titular → Titular Cuelga Llamada) para organizar los datos de forma estructurada.
                                      </div>
                                      <div style={{
                                        padding: '16px',
                                        background: 'rgba(12, 44, 108, 0.03)',
                                        border: '1px solid rgba(12, 44, 108, 0.1)',
                                        borderRadius: '8px',
                                        marginBottom: '16px'
                                      }}>
                                        <div style={{ marginBottom: '12px' }}>
                                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                                            Configuración JSON de Jerarquía
                                          </label>
                                          <textarea
                                            value={cfgSubestadosJsonText}
                                            onChange={(e) => {
                                              const newText = e.target.value;
                                              setCfgSubestadosJsonText(newText);

                                              // Intentar parsear el JSON
                                              try {
                                                const parsed = JSON.parse(newText);
                                                if (Array.isArray(parsed)) {
                                                  // JSON válido: actualizar el estado global
                                                  updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
                                                    ...sec,
                                                    subestadosConfig: {
                                                      ...(sec.subestadosConfig || {}),
                                                      hierarchy: parsed
                                                    }
                                                  }));
                                                  setCfgSubestadosJsonError('');
                                                } else {
                                                  setCfgSubestadosJsonError('El JSON debe ser un array');
                                                }
                                              } catch (err) {
                                                // JSON inválido temporalmente: solo mostrar error, no actualizar estado
                                                setCfgSubestadosJsonError(`Error de sintaxis: ${err.message}`);
                                              }
                                            }}
                                            onBlur={(e) => {
                                              // Al perder el foco, intentar formatear el JSON si es válido
                                              try {
                                                const parsed = JSON.parse(e.target.value);
                                                if (Array.isArray(parsed)) {
                                                  const formatted = JSON.stringify(parsed, null, 2);
                                                  setCfgSubestadosJsonText(formatted);
                                                  setCfgSubestadosJsonError('');
                                                }
                                              } catch (err) {
                                                // Mantener el texto tal cual si hay error
                                              }
                                            }}
                                            style={{
                                              width: '100%',
                                              minHeight: '300px',
                                              padding: '12px',
                                              borderRadius: '6px',
                                              border: cfgSubestadosJsonError ? '2px solid #ef4444' : '1px solid rgba(12, 44, 108, 0.2)',
                                              background: '#ffffff',
                                              color: '#333',
                                              fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                                              fontSize: '13px',
                                              lineHeight: '1.5'
                                            }}
                                            placeholder={`[
  {
    "level": 1,
    "key": "contactabilidad_si",
    "label": "Contactabilidad: SÍ",
    "column": "contactabilidad",
    "value": "SI",
    "operator": "equals"
  },
  {
    "level": 2,
    "key": "subestado_1",
    "label": "Subestado 1",
    "parentKey": "contactabilidad_si",
    "column": "subestado",
    "value": "Valor del subestado",
    "operator": "equals"
  }
]`}
                                          />
                                          {cfgSubestadosJsonError && (
                                            <div style={{
                                              marginTop: '8px',
                                              padding: '8px 12px',
                                              background: 'rgba(239, 68, 68, 0.1)',
                                              border: '1px solid rgba(239, 68, 68, 0.3)',
                                              borderRadius: '6px',
                                              color: '#ef4444',
                                              fontSize: '12px'
                                            }}>
                                              ⚠️ {cfgSubestadosJsonError}
                                            </div>
                                          )}
                                          {!cfgSubestadosJsonError && cfgSubestadosJsonText && (
                                            <div style={{
                                              marginTop: '8px',
                                              padding: '8px 12px',
                                              background: 'rgba(34, 197, 94, 0.1)',
                                              border: '1px solid rgba(34, 197, 94, 0.3)',
                                              borderRadius: '6px',
                                              color: '#22c55e',
                                              fontSize: '12px'
                                            }}>
                                              ✓ JSON válido - Puedes editar libremente
                                            </div>
                                          )}
                                        </div>
                                        <div style={{
                                          padding: '12px',
                                          background: 'rgba(59, 130, 246, 0.05)',
                                          borderRadius: '6px',
                                          fontSize: '13px',
                                          color: 'rgba(12, 44, 108, 0.8)'
                                        }}>
                                          <strong>Estructura de cada nivel:</strong>
                                          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                            <li><strong>level:</strong> Nivel en la jerarquía (1, 2, 3...)</li>
                                            <li><strong>key:</strong> Identificador único del nivel</li>
                                            <li><strong>label:</strong> Etiqueta a mostrar</li>
                                            <li><strong>parentKey:</strong> Key del nivel padre (opcional para nivel 1)</li>
                                            <li><strong>column:</strong> Nombre de la columna en la tabla de datos</li>
                                            <li><strong>value:</strong> Valor a buscar en la columna</li>
                                            <li><strong>operator:</strong> "equals", "contains" o "in"</li>
                                            <li><strong>calculation:</strong> Para usar fórmulas, usar {"{"}"sourceType": "field", "fieldKey": "nombre_campo"{"}"}</li>
                                          </ul>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {(() => {
                                    const metaDataFields = dashcfgSelectedMeta.dataFields || [];
                                    if (metaDataFields.length === 0) return null;

                                    const dataFieldsFiltered = metaDataFields
                                      .filter((fieldKey) => !dashcfgIsClientField(dashcfgSelectedSectionKey, fieldKey))
                                      .filter((fieldKey) => {
                                        const q = (cfgSearch || '').trim().toLowerCase();
                                        if (!q) return true;
                                        const label = dashcfgGetFieldLabel(dashcfgSelectedSectionKey, fieldKey);
                                        const help = dashcfgGetFieldHelp(dashcfgSelectedSectionKey, fieldKey);
                                        const m = dashcfgSelectedSectionCfg.fieldMappings?.[fieldKey];
                                        const hay = `${fieldKey} ${label} ${help} ${m?.table || ''} ${m?.column || ''}`.toLowerCase();
                                        return hay.includes(q);
                                      });

                                    return (
                                      <div className="dashcfg-group" style={{
                                        marginTop: '32px',
                                        border: '2px solid rgba(59, 130, 246, 0.3)',
                                        borderRadius: '8px',
                                        padding: '20px',
                                        background: 'rgba(59, 130, 246, 0.05)'
                                      }}>
                                        <div className="dashcfg-group-title" style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: 'rgba(59, 130, 246, 1)' }}>
                                          📋 Datos base (mapeo)
                                        </div>
                                        <div className="dashcfg-field-help" style={{ marginBottom: '16px', fontSize: '13px', color: 'rgba(12, 44, 108, 0.7)', lineHeight: '1.5' }}>
                                          Estos campos son columnas de la tabla de datos que se usan para cálculos y filtros. Mapea cada campo a su columna correspondiente en la tabla seleccionada en el paso "3. Datos".
                                          <br />
                                          <strong>Total de campos:</strong> {metaDataFields.length} | <strong>Campos a mostrar:</strong> {dataFieldsFiltered.length}
                                        </div>
                                        {dataFieldsFiltered.length === 0 ? (
                                          <div className="dashcfg-empty" style={{
                                            padding: '20px',
                                            textAlign: 'center',
                                            color: 'rgba(12, 44, 108, 0.5)',
                                            background: 'rgba(59, 130, 246, 0.05)',
                                            borderRadius: '6px',
                                            border: '1px dashed rgba(59, 130, 246, 0.3)'
                                          }}>
                                            {cfgSearch ? (
                                              'No se encontraron campos que coincidan con la búsqueda.'
                                            ) : (
                                              <>
                                                <strong>No hay campos para mostrar.</strong>
                                                <br />
                                                Total de campos de datos: {metaDataFields.length}
                                                <br />
                                                {metaDataFields.length > 0 && (
                                                  <>
                                                    Campos: {metaDataFields.join(', ')}
                                                    <br />
                                                    <small style={{ fontSize: '11px', color: 'rgba(12, 44, 108, 0.4)' }}>
                                                      Estos campos también están en "Lo que ve el cliente" o fueron filtrados.
                                                    </small>
                                                  </>
                                                )}
                                              </>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="dashcfg-field-list">
                                            {dataFieldsFiltered.map((fieldKey) => {
                                              const label = dashcfgGetFieldLabel(dashcfgSelectedSectionKey, fieldKey);
                                              const help = dashcfgGetFieldHelp(dashcfgSelectedSectionKey, fieldKey);
                                              const required = dashcfgIsRequiredMapping(dashcfgSelectedSectionKey, fieldKey);
                                              const m = dashcfgSelectedSectionCfg.fieldMappings?.[fieldKey];
                                              const ok = dashcfgMappingComplete(m);
                                              return (
                                                <div key={fieldKey} className={`dashcfg-field-row ${required && !ok ? 'missing' : ''}`}>
                                                  <div className="dashcfg-field-main">
                                                    <div className="dashcfg-field-name">
                                                      {label}{' '}
                                                      {required && (
                                                        <span className="dashcfg-pill warn" title="Requerido para que las métricas funcionen">
                                                          Requerido
                                                        </span>
                                                      )}
                                                      {ok && <span className="dashcfg-pill ok">OK</span>}
                                                    </div>
                                                    {help && <div className="dashcfg-field-help">{help}</div>}
                                                  </div>
                                                  {cfgShowAdvanced ? (
                                                    <div className="dashcfg-map">
                                                      <select
                                                        value={m?.column || ''}
                                                        onChange={(e) => updateSectionConfig(dashcfgSelectedSectionKey, (sec) => ({
                                                          ...sec,
                                                          fieldMappings: {
                                                            ...(sec.fieldMappings || {}),
                                                            [fieldKey]: {
                                                              ...(sec.fieldMappings?.[fieldKey] || {}),
                                                              table: dashcfgSelectedBaseTable || sec.fieldMappings?.[fieldKey]?.table || null,
                                                              column: e.target.value || null,
                                                            }
                                                          }
                                                        }))}
                                                        disabled={
                                                          !dashcfgSelectedBaseTable
                                                          || schemaLoading
                                                          || (schemaDataSource !== (dashcfgSelectedSectionCfg.dataSource || 'main'))
                                                        }
                                                      >
                                                        <option value="">{dashcfgSelectedBaseTable ? 'Columna…' : 'Selecciona tabla…'}</option>
                                                        {(
                                                          (schemaDataSource === (dashcfgSelectedSectionCfg.dataSource || 'main')
                                                            ? (schemaTables?.[dashcfgSelectedBaseTable] || [])
                                                            : [])
                                                        ).map((col) => (
                                                          <option key={col} value={col}>{col}</option>
                                                        ))}
                                                      </select>
                                                    </div>
                                                  ) : (
                                                    <div className="dashcfg-map muted">
                                                      <span className="dashcfg-pill neutral">Modo mapeo desactivado</span>
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </section>
                              </div>
                            )
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Tab de campañas - se gestiona desde CampaignManagement.js, no desde AdminConsole */}
              </div>
            </div>

            {/* Cerrar Sesión */}
            <div className="admin-logout">
              <div className="logout-content">
                <h3>🚪 Sesión</h3>
                <p style={{ fontSize: '14px', color: 'var(--muted)', margin: '8px 0' }}>
                  Sesión iniciada como: <strong>{user?.username}</strong>
                </p>
                <button
                  className="logout-button"
                  onClick={handleLogout}
                  style={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '16px 32px',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    width: '100%',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(239, 68, 68, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(239, 68, 68, 0.3)';
                  }}
                >
                  <LogOutIcon />
                  Cerrar Sesión
                </button>
              </div>
            </div>

          </div>
        )}
      </div>

      {/* Modal para editar usuario */}
      {showUserModal && editingUser && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(20px)',
            border: '1px solid var(--glass-border)',
            borderRadius: '20px',
            padding: '32px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: 'var(--text)' }}>✏️ Editar Usuario</h2>

            <form onSubmit={handleUpdateUser}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Usuario *</label>
                  <input
                    type="text"
                    value={editingUser.username}
                    onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Email</label>
                  <input
                    type="email"
                    value={editingUser.email || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Nombre Completo</label>
                  <input
                    type="text"
                    value={editingUser.full_name || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Nueva Contraseña (dejar vacío para no cambiar)</label>
                  <input
                    type="password"
                    value={editingUser.new_password || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, new_password: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Rol</label>
                  <select
                    value={editingUser.role}
                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                  >
                    <option value="user">Usuario</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Campaña</label>
                  <select
                    value={editingUser.client_id || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, client_id: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                  >
                    <option value="">Sin cliente</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '500' }}>
                    <input
                      type="checkbox"
                      checked={editingUser.is_active}
                      onChange={(e) => setEditingUser({ ...editingUser, is_active: e.target.checked })}
                      style={{ transform: 'scale(1.2)' }}
                    />
                    Usuario Activo
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowUserModal(false);
                    setEditingUser(null);
                  }}
                  style={{
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    border: '1px solid var(--card-border)',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  style={{
                    background: 'var(--accent)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para editar cliente */}
      {showClientModal && editingClient && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(20px)',
            border: '1px solid var(--glass-border)',
            borderRadius: '20px',
            padding: '32px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: 'var(--text)' }}>✏️ Editar Cliente</h2>

            <form onSubmit={handleUpdateClient}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Nombre del Cliente *</label>
                  <input
                    type="text"
                    value={editingClient.name}
                    onChange={(e) => setEditingClient({ ...editingClient, name: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Slug (URL) *</label>
                  <input
                    type="text"
                    value={editingClient.slug}
                    onChange={(e) => setEditingClient({ ...editingClient, slug: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Logo</label>
                  <ImageUpload
                    value={editingClient.logo_url || ''}
                    onChange={(logo_url) => setEditingClient({ ...editingClient, logo_url })}
                    placeholder="Subir logo del cliente"
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '500' }}>
                    <input
                      type="checkbox"
                      checked={editingClient.is_active}
                      onChange={(e) => setEditingClient({ ...editingClient, is_active: e.target.checked })}
                      style={{ transform: 'scale(1.2)' }}
                    />
                    Cliente Activo
                  </label>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Dashboard de Looker Studio</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="url"
                      value={editingClient.looker_dashboard_url || ''}
                      onChange={(e) => setEditingClient({ ...editingClient, looker_dashboard_url: e.target.value })}
                      placeholder="https://lookerstudio.google.com/embed/reporting/..."
                      style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                    />
                    <div style={{
                      width: '20px',
                      height: '20px',
                      background: editingClient.looker_dashboard_url ? '#22c55e' : '#6b7280',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      color: 'white'
                    }}>
                      📊
                    </div>
                  </div>
                  <small style={{ color: 'var(--muted)', fontSize: '12px' }}>
                    URL del dashboard de Looker Studio para este cliente (opcional)
                  </small>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontSize: '14px', fontWeight: '500' }}>Agentes Virtuales</label>
                    <button
                      type="button"
                      onClick={addEditingAgent}
                      style={{
                        background: 'var(--gradient-primary)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <PlusIcon />
                      Agregar Agente
                    </button>
                  </div>

                  {editingClientAgents.length === 0 ? (
                    <div style={{
                      padding: '20px',
                      border: '2px dashed var(--card-border)',
                      borderRadius: '8px',
                      textAlign: 'center',
                      color: 'var(--muted)',
                      fontSize: '14px'
                    }}>
                      No hay agentes configurados. Haz clic en "Agregar Agente" para comenzar.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {editingClientAgents.map((agent, index) => (
                        <div key={agent.id || index} style={{
                          border: '1px solid var(--card-border)',
                          borderRadius: '8px',
                          padding: '12px',
                          background: 'var(--card-bg)'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text)' }}>
                              {agent.name || `Agente ${index + 1}`}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeEditingAgent(index)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                cursor: 'pointer',
                                fontSize: '10px'
                              }}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <input
                              type="text"
                              value={agent.name || ''}
                              onChange={(e) => updateEditingAgent(index, 'name', e.target.value)}
                              placeholder="Nombre del agente (ej: Agente Ventas)"
                              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '14px' }}
                            />
                            <input
                              type="text"
                              value={agent.agent_id || ''}
                              onChange={(e) => updateEditingAgent(index, 'agent_id', e.target.value)}
                              placeholder="agent_xxxxxxxxxxxxxxxxxx"
                              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '14px' }}
                            />
                            <input
                              type="text"
                              value={agent.description || ''}
                              onChange={(e) => updateEditingAgent(index, 'description', e.target.value)}
                              placeholder="Descripción opcional"
                              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '14px' }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <small style={{ color: 'var(--muted)', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                    Agrega tantos agentes como necesites para este cliente
                  </small>
                </div>
                {/* URLs por pestaña para cliente existente */}
                <div>
                  <label style={{ display: 'block', margin: '8px 0', fontSize: '14px', fontWeight: '600' }}>URLs específicas por pestaña</label>
                  {['general-metrics', 'temporal-analysis', 'ddi-campaigns', 'conversion-kpis'].map((key) => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ width: '180px', fontSize: '12px', color: 'var(--muted)' }}>
                        {key === 'general-metrics' && 'Mètriques Generals'}
                        {key === 'temporal-analysis' && 'Anàlisi Temporal'}
                        {key === 'ddi-campaigns' && 'Campanyes DDI'}
                        {key === 'conversion-kpis' && 'Conversió i KPIs'}
                      </span>
                      <input
                        type="url"
                        value={editingPerTabUrls[key] || ''}
                        onChange={(e) => setEditingPerTabUrls(prev => ({ ...prev, [key]: e.target.value }))}
                        placeholder="https://lookerstudio.google.com/embed/reporting/..."
                        style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                      />
                    </div>
                  ))}
                  <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Dejar vacío para usar la URL única del cliente.</small>
                </div>

                {/* Configuración de datos multi-tenant */}
                <div style={{ marginTop: '16px', padding: '16px', background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '8px' }}>
                  <h4 style={{ marginBottom: '12px', fontSize: '15px', fontWeight: '600' }}>⚙️ Configuración de Datos</h4>
                  <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '12px' }}>Configura la fuente de datos y parámetros específicos para este cliente</p>

                  <div style={{ display: 'grid', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Nombre de la tabla de datos</label>
                      <input
                        type="text"
                        value={editingClient.data_table_name || ''}
                        onChange={(e) => setEditingClient({ ...editingClient, data_table_name: e.target.value })}
                        placeholder="datos_caser, datos_soremo, etc."
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                      />
                      <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Nombre de la tabla en la base de datos (ej: datos_caser)</small>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Colas de entrada (Inbound Queues)</label>
                      <input
                        type="text"
                        value={editingClient.inbound_queues || ''}
                        onChange={(e) => setEditingClient({ ...editingClient, inbound_queues: e.target.value })}
                        placeholder="queue39,queue40,queue77..."
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                      />
                      <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Separadas por comas (ej: queue39,queue40,queue77)</small>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Campañas de salida (Outbound Campaigns)</label>
                      <input
                        type="text"
                        value={editingClient.outbound_campaigns || ''}
                        onChange={(e) => setEditingClient({ ...editingClient, outbound_campaigns: e.target.value })}
                        placeholder="campaign31,campaign2..."
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                      />
                      <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Separadas por comas (ej: campaign31,campaign2)</small>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>GoContact Tenant ID (opcional)</label>
                      <input
                        type="text"
                        value={editingGoContactTenantId || ''}
                        onChange={(e) => setEditingGoContactTenantId(e.target.value)}
                        placeholder="UUID (ej: 2cb42dff-...)"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                      />
                      <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Si cada cliente tiene un tenant distinto en GoContact, colócalo aquí.</small>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Columna de campañas</label>
                      <input
                        type="text"
                        value={editingClient.campaign_column || ''}
                        onChange={(e) => setEditingClient({ ...editingClient, campaign_column: e.target.value })}
                        placeholder="campana, campaign_name, etc."
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                      />
                      <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Nombre de la columna para campañas (por defecto: campana)</small>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Columna de contrataciones</label>
                      <input
                        type="text"
                        value={editingClient.hiring_column || ''}
                        onChange={(e) => setEditingClient({ ...editingClient, hiring_column: e.target.value })}
                        placeholder="segmento, 39_5_subtipificacion_resolucion_principal, etc."
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                      />
                      <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Nombre de la columna para contrataciones (por defecto: segmento)</small>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Valores de contratación</label>
                      <input
                        type="text"
                        value={editingClient.hiring_values || ''}
                        onChange={(e) => setEditingClient({ ...editingClient, hiring_values: e.target.value })}
                        placeholder="CONTRATACION,CONTRATACION PTE CASER"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                      />
                      <small style={{ color: 'var(--muted)', fontSize: '12px' }}>Valores que indican contratación, separados por comas</small>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>ID Report Builder Inbound</label>
                        <input
                          type="text"
                          value={editingClient.id_report_builder_inbound || ''}
                          onChange={(e) => setEditingClient({ ...editingClient, id_report_builder_inbound: e.target.value })}
                          placeholder="Ej: 5"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>ID Report Builder Outbound</label>
                        <input
                          type="text"
                          value={editingClient.id_report_builder_outbound || ''}
                          onChange={(e) => setEditingClient({ ...editingClient, id_report_builder_outbound: e.target.value })}
                          placeholder="Ej: 6, 86"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Lógica de Abandonadas</label>
                        <select
                          value={editingClient.logica_abandonadas || 'default'}
                          onChange={(e) => setEditingClient({ ...editingClient, logica_abandonadas: e.target.value })}
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }}
                        >
                          <option value="default">Default (SEGMENTO/Hangup)</option>
                          <option value="call_outcome">Call Outcome Name</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {false && (
                  <div style={{ marginTop: '16px' }}>
                    <label style={{ display: 'block', margin: '8px 0', fontSize: '14px', fontWeight: '600' }}>Secciones Habilitadas</label>
                    <p style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '12px' }}>Selecciona qué secciones estarán visibles para este cliente</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                      {editingClient.enabled_sections && Object.keys({ agent_analytics: true, ...editingClient.enabled_sections }).map((section) => (
                        <label key={section} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          cursor: 'pointer',
                          padding: '12px',
                          background: editingClient.enabled_sections[section] ? 'var(--glass-bg)' : 'transparent',
                          borderRadius: '8px',
                          border: editingClient.enabled_sections[section] ? '2px solid var(--accent)' : '1px solid var(--card-border)',
                          transition: 'all 0.2s ease'
                        }}>
                          <input
                            type="checkbox"
                            checked={Boolean(editingClient.enabled_sections[section])}
                            onChange={(e) => setEditingClient({
                              ...editingClient,
                              enabled_sections: { ...editingClient.enabled_sections, [section]: e.target.checked }
                            })}
                            style={{ accentColor: 'var(--accent)', width: '20px', height: '20px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', fontWeight: '500' }}>
                            {section === 'analytics' && '📊 Analíticas'}
                            {section === 'agent_analytics' && '🤖 Analíticas Agente IA'}
                            {section === 'campaigns' && '🎯 Campañas'}
                            {section === 'conversations' && '💬 Conversaciones'}
                            {section === 'reports' && '📑 Informes'}
                            {section === 'osint' && '🛡️ OSINT'}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowClientModal(false);
                    setEditingClient(null);
                  }}
                  style={{
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    border: '1px solid var(--card-border)',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  style={{
                    background: 'var(--accent)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* (El panel de Configurar vistas se renderiza dentro de admin-main) */}

      {/* Modal para editar campaña - eliminado, se gestiona desde CampaignManagement.js */}
    </>
  );
};

export default AdminConsole;